import { apiFetch } from "api/auth";

const API_BASE =
  process.env.REACT_APP_API_URL || "http://localhost:3000/api/v1";

export interface NextLeadNumberResponse {
  leadNo: string;
}

export async function getNextLeadNumber(): Promise<NextLeadNumberResponse> {
  const res = await apiFetch(`${API_BASE}/new-app/discussion/next-lead-number`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });

  const json = (await res.json()) as ApiSuccessResponse<NextLeadNumberResponse> | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to get next lead number";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<NextLeadNumberResponse>).data;
}

export interface CreateDiscussionPayload {
  markName: string;
  contactName: string;
  phone: string;
  email?: string;
  callTime: string;
}

export interface CreateDiscussionResponse {
  leadId: string;
  leadNo: string;
}

/** Payload for updating preliminary discussion (when lead exists). leadNo is readonly. */
export interface UpdateDiscussionPayload {
  markName: string;
  contactName: string;
  phone: string;
  email?: string;
  callTime: string;
}

export interface LeadDetails {
  leadNo: string;
  customerId?: string | null;
  contactName: string;
  phone: string | null;
  email: string | null;
  markName: string | null;
  tradeName?: string | null;
  dateOfUse?: string | null;
  descriptionOfGoods?: string | null;
  logoUrl?: string | null;
  nonDistinctiveness?: boolean | null;
  /** Time & date of call - ISO 8601 string */
  callTime?: string | null;
}

export interface SubmitSearchDetailsPayload {
  tradeName: string;
  dateOfUse: string;
  goodsDescription: string;
}

export interface SubmitSearchDetailsResponse {
  leadId: string;
}

interface ApiSuccessResponse<T> {
  success: true;
  message: string;
  data: T;
}

interface ApiErrorResponse {
  success?: false;
  message?: string;
  error?: string;
  statusCode?: number;
}

/**
 * Shape every backend PDF generator actually responds with.
 *
 * Several generators below used to declare their payload as
 * `{ key, url, filename }`. That type-checked (the cast is unvalidated) but at
 * runtime `key`/`url` were `undefined`, so callers stored an empty PDF key and
 * the UI reported "generated" while leaving Download/Preview disabled until the
 * page was saved and reopened. Parse the real shape and map it instead.
 */
interface GeneratedPdfPayload {
  s3Key: string;
  downloadUrl: string;
  filename: string;
}

/** Maps the backend PDF payload onto the `{ key, url, filename }` shape callers expect. */
function toGeneratedPdf(
  payload: GeneratedPdfPayload
): { key: string; url: string; filename: string } {
  return {
    key: payload?.s3Key,
    url: payload?.downloadUrl,
    filename: payload?.filename,
  };
}

export async function createDiscussion(
  payload: CreateDiscussionPayload
): Promise<CreateDiscussionResponse> {
  const res = await apiFetch(`${API_BASE}/new-app/discussion`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  const json = (await res.json()) as ApiSuccessResponse<unknown> | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to create discussion";
    throw new Error(message);
  }

  const data = (json as ApiSuccessResponse<CreateDiscussionResponse>).data;
  return data;
}

export async function updateDiscussion(
  leadId: string,
  payload: UpdateDiscussionPayload
): Promise<CreateDiscussionResponse> {
  const res = await apiFetch(`${API_BASE}/new-app/discussion/${leadId}/preliminary`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  const json = (await res.json()) as ApiSuccessResponse<unknown> | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to update discussion";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<CreateDiscussionResponse>).data;
}

export async function getLeadDetails(leadId: string): Promise<{ lead: LeadDetails }> {
  const res = await apiFetch(`${API_BASE}/new-app/${leadId}/all-discussion-details`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });

  const json = (await res.json()) as ApiSuccessResponse<{ lead: LeadDetails }> | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to load lead details";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<{ lead: LeadDetails }>).data;
}

export interface SendChecklistResponse {
  contactName?: string;
  waLink?: string;
}

export interface NewMarkFromLegalAdvicePayload {
  markName: string;
  contactName: string;
  phone: string;
  email?: string;
  callTime: string;
}

export async function completeLegalAdvice(leadId: string): Promise<{ leadId: string }> {
  const res = await apiFetch(`${API_BASE}/new-app/discussion/${leadId}/legal-advice/complete`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({}),
  });

  const json = (await res.json()) as ApiSuccessResponse<{ leadId: string }> | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to complete legal advice";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<{ leadId: string }>).data;
}

export async function submitNewMarkFromLegalAdvice(
  leadId: string,
  payload: NewMarkFromLegalAdvicePayload
): Promise<{ leadId: string; leadNo: string }> {
  const res = await apiFetch(`${API_BASE}/new-app/discussion/${leadId}/legal-advice/new-mark`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  const json = (await res.json()) as
    | ApiSuccessResponse<{ leadId: string; leadNo: string }>
    | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to submit new mark";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<{ leadId: string; leadNo: string }>).data;
}

export async function sendChecklist(leadId: string): Promise<SendChecklistResponse> {
  const res = await apiFetch(
    `${API_BASE}/new-app/discussion/${leadId}/send-checklist`,
    { method: "POST", headers: { "Content-Type": "application/json" } }
  );

  const json = (await res.json()) as ApiSuccessResponse<SendChecklistResponse> | ApiErrorResponse;

  if (!res.ok) {
    const err =
      (json as ApiErrorResponse);
    const message =
      err?.message || err?.error || "Failed to send checklist";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<SendChecklistResponse>).data;
}

export async function submitSearchDetails(
  leadId: string,
  payload: SubmitSearchDetailsPayload,
  logoFile?: File | null
): Promise<SubmitSearchDetailsResponse> {
  const formData = new FormData();
  formData.append("tradeName", payload.tradeName);
  formData.append("dateOfUse", payload.dateOfUse);
  formData.append("goodsDescription", payload.goodsDescription);
  if (logoFile) {
    formData.append("file", logoFile);
  }

  const res = await apiFetch(
    `${API_BASE}/new-app/discussion/${leadId}/search-details`,
    { method: "POST", body: formData }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<SubmitSearchDetailsResponse>
    | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to submit search details";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<SubmitSearchDetailsResponse>).data;
}

/** Build wa.me link for WhatsApp click-to-chat (frontend fallback when no API). */
export function buildWhatsAppLink(phone: string, message: string): string {
  const normalized = (phone || "").replace(/[^\d]/g, "");
  if (!normalized) return "";
  const encoded = encodeURIComponent(message);
  return `https://wa.me/${normalized}?text=${encoded}`;
}

// --- Filing Step 1: Check list of Documents to Client ---

export interface SendDocChecklistResponse {
  contactName?: string;
  waLink?: string;
}

export async function sendDocChecklist(
  leadId: string
): Promise<SendDocChecklistResponse> {
  const res = await apiFetch(
    `${API_BASE}/new-app/filing/${leadId}/send-doc-checklist`,
    { method: "POST", headers: { "Content-Type": "application/json" } }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<SendDocChecklistResponse>
    | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to send document checklist";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<SendDocChecklistResponse>).data;
}

// --- Filing Step 2: Documents from Client ---

export interface DocumentsFromClientResponse {
  isSoleProprietor: boolean;
  isDocumentsOfMarkRequired: boolean;
  documentsOfApplicantReceived: boolean;
  documentsOfSignatoryReceived: boolean;
  documentsOfMarkReceived: boolean | null;
}

export interface UpdateDocumentsFromClientPayload {
  documentsOfApplicantReceived?: boolean;
  documentsOfSignatoryReceived?: boolean;
  documentsOfMarkReceived?: boolean;
}

export async function getDocumentsFromClient(
  leadId: string
): Promise<DocumentsFromClientResponse> {
  const res = await apiFetch(
    `${API_BASE}/new-app/filing/${leadId}/documents-from-client`,
    { method: "GET" }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<DocumentsFromClientResponse>
    | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to load documents from client";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<DocumentsFromClientResponse>).data;
}

export async function updateDocumentsFromClient(
  leadId: string,
  payload: UpdateDocumentsFromClientPayload
): Promise<void> {
  const res = await apiFetch(
    `${API_BASE}/new-app/filing/${leadId}/documents-from-client`,
    { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) }
  );

  const json = (await res.json()) as ApiSuccessResponse<unknown> | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to update documents from client";
    throw new Error(message);
  }
}

// --- Filing Step 3: Filing Details to Client ---

export interface FilingDetailsFetchedData {
  mark: string | null;
  selectedClasses: number[];
  goodsServicesPerClass: { classNumber: number; goodsServices: string[] }[];
  selectedTypesOfMark: string[];
  marks: { classNumber: number; typeOfMark: string; date?: string }[];
  noOfApplications: number | null;
  outstandingAmount: number | null;
}

export interface FilingDetailsResponse {
  filingDetails: {
    applicant: string | null;
    applicantAddressLine: string | null;
    applicantAddressDistrict: string | null;
    applicantAddressState: string | null;
    applicantAddressCountry: string | null;
    applicantType: string | null;
    applicantTypeOther: string | null;
    partnersOrTrustees: string | null;
    signatoryName: string | null;
    signatoryDateOfBirth: string | null;
    signatoryAge: number | null;
    signatoryGender: string | null;
    signatoryGuardian: string | null;
    signatoryGuardianName: string | null;
    signatoryDesignation: string | null;
    imageOfMarkKey: string | null;
    imageOfMarkUrl: string | null;
    userDatesPerMark:
      | {
          classNumber: number;
          typeOfMark: string;
          userDateType?: string;
          date?: string;
          supportingDocuments?: {
            docNo: number;
            docName: string;
            docDetail: string;
            docDate: string;
          }[];
        }[]
      | null;
    stampPapersPerApplication:
      | {
          classNumber: number;
          typeOfMark: string;
          stampPapersUa: number;
          stampPapersPoa: number;
          amount: number | null;
        }[]
      | null;
    stampPapersUaTotal: number | null;
    stampPapersPoaTotal: number | null;
    stampVendorName: string | null;
    stampVendorPhone: string | null;
    amountPayableToStampVendor: number;
    paidToVendor: boolean | null;
    stampVendorPaymentStatusDisplay: string;
    amountPayableToNotary: number;
    paidToNotary: boolean | null;
    notaryPaymentStatusDisplay: string;
    notarisationPerApplication:
      | {
          classNumber: number;
          typeOfMark: string;
          stampPapersUa: number;
          amountPayableToNotary: number;
          paidToNotary: boolean | null;
          paymentStatusDisplay: string;
        }[]
      | null;
  };
  signatoryRelationWithGuardian: string | null;
  fetched: FilingDetailsFetchedData;
  stampVendors: { name: string; phone: string; isDefault?: boolean }[];
  isStampAffidavitStepActive: boolean;
  isNotarisationStepActive: boolean;
}

export interface SupportingDocumentInput {
  docNo: number;
  docName: string;
  docDetail: string;
  docDate: string;
}

export interface UserDatePerMarkInput {
  classNumber: number;
  typeOfMark: string;
  userDateType?: string;
  date?: string;
  supportingDocuments?: SupportingDocumentInput[];
}

export interface UpdateFilingDetailsPayload {
  applicant?: string;
  applicantAddressLine?: string;
  applicantAddressDistrict?: string;
  applicantAddressState?: string;
  applicantAddressCountry?: string;
  applicantType?: string;
  applicantTypeOther?: string;
  partnersOrTrustees?: string;
  signatoryName?: string;
  signatoryDateOfBirth?: string;
  signatoryAge?: number;
  signatoryGender?: string;
  signatoryGuardian?: string;
  signatoryGuardianName?: string;
  signatoryDesignation?: string;
  imageOfMarkKey?: string;
  userDatesPerMark?: UserDatePerMarkInput[];
  stampPapersForUa?: number;
  stampPapersForPoA?: number;
  stampVendorName?: string;
  stampVendorPhone?: string;
  amountPayableToStampVendor?: number;
  paidToVendor?: boolean;
}

export async function getFilingDetails(
  leadId: string
): Promise<FilingDetailsResponse> {
  const res = await apiFetch(
    `${API_BASE}/new-app/filing/${leadId}/filing-details`,
    { method: "GET" }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<FilingDetailsResponse>
    | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to load filing details";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<FilingDetailsResponse>).data;
}

export interface DefaultSupportingDocument {
  docName: string;
  docDetail: string;
  docDate: string;
}

export async function getDefaultSupportingDocuments(): Promise<
  DefaultSupportingDocument[]
> {
  const res = await apiFetch(
    `${API_BASE}/new-app/filing/default-supporting-documents`,
    { method: "GET" }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<DefaultSupportingDocument[]>
    | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to load default supporting documents";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<DefaultSupportingDocument[]>).data;
}

export async function updateFilingDetails(
  leadId: string,
  payload: UpdateFilingDetailsPayload
): Promise<FilingDetailsResponse> {
  const res = await apiFetch(
    `${API_BASE}/new-app/filing/${leadId}/filing-details`,
    { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<FilingDetailsResponse>
    | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to update filing details";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<FilingDetailsResponse>).data;
}

export async function getFilingDetailsWhatsAppPreview(
  leadId: string
): Promise<{ message: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/filing/${leadId}/filing-details/whatsapp-preview`,
    { method: "GET" }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<{ message: string }>
    | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to get WhatsApp preview";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<{ message: string }>).data;
}

export type WhatsappSendResultPayload = {
  waLink: string;
  sentViaApi: boolean;
  messageId?: string;
};

export async function sendFilingDetailsWhatsApp(
  leadId: string
): Promise<WhatsappSendResultPayload> {
  const res = await apiFetch(
    `${API_BASE}/new-app/filing/${leadId}/filing-details/send-whatsapp`,
    { method: "POST", headers: { "Content-Type": "application/json" } }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<WhatsappSendResultPayload>
    | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to send filing details to client";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<WhatsappSendResultPayload>).data;
}

export async function sendMarkImageWhatsApp(
  leadId: string
): Promise<WhatsappSendResultPayload> {
  const res = await apiFetch(
    `${API_BASE}/new-app/filing/${leadId}/filing-details/send-mark-image-whatsapp`,
    { method: "POST", headers: { "Content-Type": "application/json" } }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<WhatsappSendResultPayload>
    | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to send mark image via WhatsApp";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<WhatsappSendResultPayload>).data;
}

export async function uploadMarkImage(
  leadId: string,
  file: File
): Promise<{ imageOfMarkKey: string; imageOfMarkUrl: string }> {
  const formData = new FormData();
  formData.append("file", file);

  const res = await apiFetch(
    `${API_BASE}/new-app/filing/${leadId}/filing-details/upload-mark-image`,
    { method: "POST", body: formData }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<{ imageOfMarkKey: string; imageOfMarkUrl: string }>
    | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to upload image of mark";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<{ imageOfMarkKey: string; imageOfMarkUrl: string }>).data;
}

export async function completeFilingDetailsConfirmation(
  leadId: string
): Promise<{ leadId: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/filing/${leadId}/filing-details-confirmation/complete`,
    { method: "POST", headers: { "Content-Type": "application/json" } }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<{ leadId: string }>
    | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to complete confirmation of filing details";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<{ leadId: string }>).data;
}

// --- Step 9: Signature from Client ---

export async function completeSignatureFromClient(
  leadId: string
): Promise<{ leadId: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/filing/${leadId}/signature-from-client/complete`,
    { method: "POST", headers: { "Content-Type": "application/json" } }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<{ leadId: string }>
    | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to complete Signature from Client step";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<{ leadId: string }>).data;
}

// --- Step 11: Scanning of Documents ---

export async function completeScanningOfDocuments(
  leadId: string
): Promise<{ leadId: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/filing/${leadId}/scanning-of-documents/complete`,
    { method: "POST", headers: { "Content-Type": "application/json" } }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<{ leadId: string }>
    | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to complete Scanning of Documents step";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<{ leadId: string }>).data;
}

// --- Step 12: Uploading Application Details ---

export async function completeUploadingApplicationDetails(
  leadId: string
): Promise<{ leadId: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/filing/${leadId}/uploading-application-details/complete`,
    { method: "POST", headers: { "Content-Type": "application/json" } }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<{ leadId: string }>
    | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to complete Uploading Application Details step";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<{ leadId: string }>).data;
}

// --- Step 13: Filing of Application ---

export async function completeFilingOfApplication(
  leadId: string
): Promise<{ leadId: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/filing/${leadId}/filing-of-application/complete`,
    { method: "POST", headers: { "Content-Type": "application/json" } }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<{ leadId: string }>
    | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to complete Filing of Application step";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<{ leadId: string }>).data;
}

// --- Step 14: Acknowledgement to Client ---

export interface AcknowledgementFormData {
  noOfApplications: number;
  marks: { classNumber: number; typeOfMark: string; mark?: string }[];
  selectedClasses: number[];
  selectedTypesOfMark: string[];
  markName: string | null;
  filingDetails: {
    applicantType: string | null;
    signatoryDesignation: string | null;
    applicant: string | null;
    applicantAddressLine: string | null;
    applicantAddressDistrict: string | null;
    applicantAddressState: string | null;
    applicantAddressCountry: string | null;
    signatoryGender: string | null;
  };
  addresseeDefaults?: {
    addresseeLine1: string | null;
    addresseeLine2: string | null;
    addresseeLine3: string | null;
    formOfAddress: string | null;
  };
}

export interface AcknowledgementApplicationItem {
  tmNo: string;
  mark: string;
  classNumber: number;
  typeOfMark: string;
  applicationDate?: string;
  status?: string;
}

export interface AcknowledgementSummary {
  id: string;
  acknowledgementNo: string;
  dateOfFiling: string;
  addresseeLine1: string | null;
  addresseeLine2: string | null;
  addresseeLine3: string | null;
  districtOfApplicant: string | null;
  formOfAddress: string | null;
  noOfApplications: number;
  applications: AcknowledgementApplicationItem[];
  applicant: string | null;
  applicationStatusPdfs: {
    ackApplicationId: string;
    tmNo: string;
    pdfKey: string | null;
    pdfUrl: string | null;
  }[];
  acknowledgementLetterPdfKey: string | null;
  acknowledgementLetterWithStatusPdfKey: string | null;
  trademarksReceiptPdfKey: string | null;
  acknowledgementLetterPdfUrl?: string | null;
  acknowledgementLetterWithStatusPdfUrl?: string | null;
  trademarksReceiptPdfUrl?: string | null;
  dateOfFilingOfExpeditedApplication?: string | null;
  expeditedReceiptPdfKey?: string | null;
  expeditedReceiptPdfUrl?: string | null;
}

export interface SubmitAcknowledgementPayload {
  dateOfFiling: string;
  applications: { tmNo: string; mark?: string; classNumber?: number; typeOfMark?: string }[];
  addresseeLine1?: string;
  addresseeLine2?: string;
  addresseeLine3?: string;
  districtOfApplicant?: string;
  formOfAddress?: string;
}

export interface UpdateAcknowledgementPayload {
  dateOfFiling?: string;
  applications?: { tmNo: string; mark?: string; classNumber?: number; typeOfMark?: string }[];
  addresseeLine1?: string;
  addresseeLine2?: string;
  addresseeLine3?: string;
  districtOfApplicant?: string;
  formOfAddress?: string;
}

export async function getAcknowledgementForm(
  leadId: string
): Promise<AcknowledgementFormData> {
  const res = await apiFetch(
    `${API_BASE}/new-app/filing/${leadId}/acknowledgement-to-client/form`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<AcknowledgementFormData>
    | ApiErrorResponse;
  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to load acknowledgement form";
    throw new Error(message);
  }
  return (json as ApiSuccessResponse<AcknowledgementFormData>).data;
}

export async function getAcknowledgementSummary(
  leadId: string
): Promise<AcknowledgementSummary | null> {
  const res = await apiFetch(
    `${API_BASE}/new-app/filing/${leadId}/acknowledgement-to-client/summary`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<AcknowledgementSummary | null | object>
    | ApiErrorResponse;
  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to load acknowledgement summary";
    throw new Error(message);
  }
  const raw = (json as ApiSuccessResponse<AcknowledgementSummary | null | object>).data;
  return raw && typeof raw === "object" && "id" in raw
    ? (raw as AcknowledgementSummary)
    : null;
}

/**
 * Submit a new acknowledgement, then fetch the summary.
 *
 * Backend contract: POST submit returns `true` on success — the response payload is NOT the
 * summary. The controller doc explicitly says "Returns true on success; client fetches summary
 * via GET summary." So we issue the POST, then GET the summary, and return that to callers.
 */
export async function submitAcknowledgement(
  leadId: string,
  payload: SubmitAcknowledgementPayload
): Promise<AcknowledgementSummary> {
  const res = await apiFetch(
    `${API_BASE}/new-app/filing/${leadId}/acknowledgement-to-client/submit`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  const json = (await res.json()) as ApiSuccessResponse<unknown> | ApiErrorResponse;
  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to submit acknowledgement";
    throw new Error(message);
  }
  const summary = await getAcknowledgementSummary(leadId);
  if (!summary) {
    throw new Error("Saved, but failed to load the acknowledgement summary.");
  }
  return summary;
}

/** Update an existing acknowledgement, then fetch the refreshed summary. Same boolean-vs-summary
 *  contract as submitAcknowledgement above. */
export async function updateAcknowledgement(
  leadId: string,
  acknowledgementId: string,
  payload: UpdateAcknowledgementPayload
): Promise<AcknowledgementSummary> {
  const res = await apiFetch(
    `${API_BASE}/new-app/filing/${leadId}/acknowledgement-to-client/${acknowledgementId}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  const json = (await res.json()) as ApiSuccessResponse<unknown> | ApiErrorResponse;
  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to update acknowledgement";
    throw new Error(message);
  }
  const summary = await getAcknowledgementSummary(leadId);
  if (!summary) {
    throw new Error("Saved, but failed to load the acknowledgement summary.");
  }
  return summary;
}

export async function generateAcknowledgementLetter(
  leadId: string
): Promise<{ s3Key: string; downloadUrl: string; filename: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/filing/${leadId}/acknowledgement-to-client/generate-letter`,
    { method: "POST", headers: { "Content-Type": "application/json" } }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ s3Key: string; downloadUrl: string; filename: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to generate acknowledgement letter";
    throw new Error(message);
  }
  return (json as ApiSuccessResponse<{ s3Key: string; downloadUrl: string; filename: string }>).data;
}

/**
 * Email preview for a Step 14 acknowledgement document send (Merged Letter / Trademarks
 * Receipt). Shape matches the shared EmailPreviewBlock.
 */
export interface AcknowledgementEmailPreview {
  to: string | null;
  subject: string;
  body: string;
  filename: string | null;
  downloadUrl: string | null;
}

/** Step 14: WhatsApp + Email preview content for forwarding an acknowledgement doc to client. */
export interface AcknowledgementSendPreview {
  document: {
    key: string | null;
    url: string | null;
    filename: string;
  };
  whatsappMessage: string;
  emailPreview: AcknowledgementEmailPreview;
}

/** Back-compat aliases (Trademarks Receipt was the first consumer of this shape). */
export type TrademarksReceiptEmailPreview = AcknowledgementEmailPreview;
export type TrademarksReceiptSendPreview = AcknowledgementSendPreview;

/** Step 14: Fetch the exact WhatsApp + Email content for the Merged Letter send (no send). */
export async function previewSendAcknowledgementMergedLetterToClient(
  leadId: string
): Promise<AcknowledgementSendPreview> {
  const res = await apiFetch(
    `${API_BASE}/new-app/filing/${leadId}/acknowledgement-to-client/send-merged-letter-to-client/preview`,
    { method: "POST", headers: { "Content-Type": "application/json" } }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<AcknowledgementSendPreview>
    | ApiErrorResponse;
  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to load merged letter send preview";
    throw new Error(message);
  }
  return (json as ApiSuccessResponse<AcknowledgementSendPreview>).data;
}

export async function sendAcknowledgementMergedLetterToClient(
  leadId: string
): Promise<{ waLink?: string; email?: string; emailError?: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/filing/${leadId}/acknowledgement-to-client/send-merged-letter-to-client`,
    { method: "POST", headers: { "Content-Type": "application/json" } }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink?: string; email?: string; emailError?: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to send merged letter to client";
    throw new Error(message);
  }
  return (json as ApiSuccessResponse<{ waLink?: string; email?: string; emailError?: string }>)
    .data;
}

/** Step 14: Fetch the exact WhatsApp + Email content for the Trademarks Receipt send (no send). */
export async function previewSendAcknowledgementTrademarksReceiptToClient(
  leadId: string
): Promise<TrademarksReceiptSendPreview> {
  const res = await apiFetch(
    `${API_BASE}/new-app/filing/${leadId}/acknowledgement-to-client/send-trademarks-receipt-to-client/preview`,
    { method: "POST", headers: { "Content-Type": "application/json" } }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<TrademarksReceiptSendPreview>
    | ApiErrorResponse;
  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to load trademarks receipt send preview";
    throw new Error(message);
  }
  return (json as ApiSuccessResponse<TrademarksReceiptSendPreview>).data;
}

export async function sendAcknowledgementTrademarksReceiptToClient(
  leadId: string
): Promise<{ waLink?: string; email?: string; emailError?: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/filing/${leadId}/acknowledgement-to-client/send-trademarks-receipt-to-client`,
    { method: "POST", headers: { "Content-Type": "application/json" } }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink?: string; email?: string; emailError?: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to send trademarks receipt to client";
    throw new Error(message);
  }
  return (json as ApiSuccessResponse<{ waLink?: string; email?: string; emailError?: string }>)
    .data;
}

/** Step 14: PDF types accepted by save-pdf-keys. Mirrors backend AcknowledgementPdfType. */
export type AcknowledgementPdfTypeValue =
  | "application_status"
  | "merged_letter"
  | "trademarks_receipt"
  | "expedited_receipt";

export interface SaveAcknowledgementPdfKeyPayload {
  type: AcknowledgementPdfTypeValue;
  url: string;
  ackApplicationId?: string;
}

export async function saveAcknowledgementPdfKey(
  leadId: string,
  payload: SaveAcknowledgementPdfKeyPayload
): Promise<{ key: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/filing/${leadId}/acknowledgement-to-client/save-pdf-keys`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ key: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to save PDF key";
    throw new Error(message);
  }
  return (json as ApiSuccessResponse<{ key: string }>).data;
}

export async function generateAcknowledgementMergedLetterWithStatus(
  leadId: string
): Promise<{ s3Key: string; downloadUrl: string; filename: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/filing/${leadId}/acknowledgement-to-client/generate-merged-letter-with-status`,
    { method: "POST", headers: { "Content-Type": "application/json" } }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ s3Key: string; downloadUrl: string; filename: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to generate merged letter with status";
    throw new Error(message);
  }
  return (json as ApiSuccessResponse<{ s3Key: string; downloadUrl: string; filename: string }>).data;
}

/** Generic presigned-URL upload helper for the acknowledgement step (uses /upload/presigned-urls). */
export interface AcknowledgementUploadUrl {
  key: string;
  url: string;
  bucket?: string;
}

export async function generateAcknowledgementUploadUrls(
  files: { filename: string; mimeType: string }[],
  folder: string
): Promise<AcknowledgementUploadUrl[]> {
  const res = await apiFetch(`${API_BASE}/upload/presigned-urls`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ files, type: "private", folder }),
  });
  const json = (await res.json()) as
    | ApiSuccessResponse<AcknowledgementUploadUrl[]>
    | ApiErrorResponse;
  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to generate upload URLs";
    throw new Error(message);
  }
  return (json as ApiSuccessResponse<AcknowledgementUploadUrl[]>).data;
}

/** Upload a single File to S3 via presigned PUT URL. Returns the stored S3 key. */
export async function uploadAcknowledgementPdf(
  file: File,
  folder: string
): Promise<string> {
  const [presigned] = await generateAcknowledgementUploadUrls(
    [{ filename: file.name, mimeType: file.type || "application/pdf" }],
    folder
  );
  if (!presigned?.url || !presigned?.key) {
    throw new Error("Upload URL not returned by server");
  }
  const putRes = await fetch(presigned.url, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": file.type || "application/pdf" },
  });
  if (!putRes.ok) {
    throw new Error(`Upload failed: ${putRes.status} ${putRes.statusText}`);
  }
  return presigned.key;
}

// --- Step 15: Filing of Expedited Application ---

export interface ExpeditedFilingStatus {
  isActive: boolean;
  acknowledgementNo: string | null;
  applications: AcknowledgementApplicationItem[];
  dateOfFiling: string | null;
  markName: string | null;
  dateOfFilingOfExpeditedApplication: string | null;
  expeditedReceiptPdfKey: string | null;
  expeditedReceiptPdfUrl: string | null;
}

export async function getExpeditedFilingStatus(
  leadId: string
): Promise<ExpeditedFilingStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/filing/${leadId}/expedited-filing/status`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<ExpeditedFilingStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to load expedited filing status";
    throw new Error(message);
  }
  return (json as ApiSuccessResponse<ExpeditedFilingStatus>).data;
}

export async function submitExpeditedFiling(
  leadId: string,
  payload: { dateOfFilingOfExpeditedApplication: string }
): Promise<ExpeditedFilingStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/filing/${leadId}/expedited-filing/submit`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<ExpeditedFilingStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to submit expedited filing";
    throw new Error(message);
  }
  return (json as ApiSuccessResponse<ExpeditedFilingStatus>).data;
}

export async function sendExpeditedReceiptToClient(
  leadId: string
): Promise<{ waLink?: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/filing/${leadId}/expedited-filing/send-receipt-to-client`,
    { method: "POST", headers: { "Content-Type": "application/json" } }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink?: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to send expedited receipt to client";
    throw new Error(message);
  }
  return (json as ApiSuccessResponse<{ waLink?: string }>).data;
}

export async function sendExpeditedFilingAutomatedMessage(
  leadId: string
): Promise<{ waLink?: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/filing/${leadId}/expedited-filing/send-automated-message`,
    { method: "POST", headers: { "Content-Type": "application/json" } }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink?: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to send expedited filing message";
    throw new Error(message);
  }
  return (json as ApiSuccessResponse<{ waLink?: string }>).data;
}

// --- Step 16: Invoice to Client for Expedited Application ---

export interface ExpeditedInvoiceStatus {
  isActive: boolean;
  invoiceNo: string | null;
  invoiceDate: string | null;
  includeInvoiceNoInProposal: boolean;
  expeditedInvoicePdfKey: string | null;
  expeditedInvoicePdfUrl: string | null;
}

export interface ExpeditedInvoicePreview {
  isActive: boolean;
  addressee: string;
  addresseeLine1: string | null;
  addresseeLine2: string | null;
  addresseeLine3: string | null;
  formOfAddress: string | null;
  mark: string | null;
  expeditedApplication: boolean;
  noOfApplications: number;
  dateOfFilingExpedited: string | null;
  govtFeeExpedited: number;
  discountExpedited: number;
  profFeeExpedited: number;
  collectedAdvance: number;
  totalCharges: number;
  invoiceNo: string | null;
  invoiceDate: string | null;
  includeInvoiceNoInProposal: boolean;
  expeditedInvoicePdfUrl: string | null;
}

export async function getExpeditedInvoiceStatus(
  leadId: string
): Promise<ExpeditedInvoiceStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/filing/${leadId}/expedited-invoice/status`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<ExpeditedInvoiceStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to load expedited invoice status";
    throw new Error(message);
  }
  return (json as ApiSuccessResponse<ExpeditedInvoiceStatus>).data;
}

export async function getExpeditedInvoicePreview(
  leadId: string
): Promise<ExpeditedInvoicePreview | null> {
  const res = await apiFetch(
    `${API_BASE}/new-app/filing/${leadId}/expedited-invoice/preview`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<ExpeditedInvoicePreview | null | object>
    | ApiErrorResponse;
  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to load expedited invoice preview";
    throw new Error(message);
  }
  const raw = (json as ApiSuccessResponse<ExpeditedInvoicePreview | null | object>).data;
  return raw && typeof raw === "object" && "isActive" in raw
    ? (raw as ExpeditedInvoicePreview)
    : null;
}

export async function saveExpeditedInvoice(
  leadId: string,
  payload: { invoiceDate?: string; includeInvoiceNoInProposal?: boolean }
): Promise<ExpeditedInvoiceStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/filing/${leadId}/expedited-invoice/save`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<ExpeditedInvoiceStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to save expedited invoice";
    throw new Error(message);
  }
  return (json as ApiSuccessResponse<ExpeditedInvoiceStatus>).data;
}

export async function generateExpeditedInvoice(
  leadId: string
): Promise<{ s3Key: string; downloadUrl: string; filename: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/filing/${leadId}/expedited-invoice/generate`,
    { method: "POST", headers: { "Content-Type": "application/json" } }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ s3Key: string; downloadUrl: string; filename: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to generate expedited invoice PDF";
    throw new Error(message);
  }
  return (json as ApiSuccessResponse<{ s3Key: string; downloadUrl: string; filename: string }>).data;
}

export interface SendExpeditedInvoiceToClientPayload {
  sendWhatsApp?: boolean;
  sendEmail?: boolean;
}

export async function sendExpeditedInvoiceToClient(
  leadId: string,
  payload: SendExpeditedInvoiceToClientPayload = {}
): Promise<{ waLink?: string; email?: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/filing/${leadId}/expedited-invoice/send-to-client`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink?: string; email?: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to send expedited invoice to client";
    throw new Error(message);
  }
  return (json as ApiSuccessResponse<{ waLink?: string; email?: string }>).data;
}

export interface ExpeditedInvoiceEmailPreview {
  to: string | null;
  subject: string;
  body: string;
}

export interface ExpeditedInvoiceSendPreview {
  sendWhatsApp: boolean;
  sendEmail: boolean;
  invoiceDocument: {
    key: string | null;
    url: string | null;
    filename: string;
  };
  whatsappMessage: string | null;
  emailPreview: ExpeditedInvoiceEmailPreview | null;
}

export async function previewSendExpeditedInvoiceToClient(
  leadId: string,
  payload: SendExpeditedInvoiceToClientPayload = {}
): Promise<ExpeditedInvoiceSendPreview> {
  const res = await apiFetch(
    `${API_BASE}/new-app/filing/${leadId}/expedited-invoice/send-to-client/preview`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<ExpeditedInvoiceSendPreview>
    | ApiErrorResponse;
  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to load expedited invoice send preview";
    throw new Error(message);
  }
  return (json as ApiSuccessResponse<ExpeditedInvoiceSendPreview>).data;
}

export async function sendExpeditedInvoicePaymentMessage(
  leadId: string
): Promise<{ waLink?: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/filing/${leadId}/expedited-invoice/send-payment-message`,
    { method: "POST", headers: { "Content-Type": "application/json" } }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink?: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to send payment message";
    throw new Error(message);
  }
  return (json as ApiSuccessResponse<{ waLink?: string }>).data;
}

export interface ExpeditedInvoicePaymentMessagePreview {
  invoiceDocumentGenerated: boolean;
  paymentMessage: string;
}

export async function previewSendExpeditedInvoicePaymentMessage(
  leadId: string
): Promise<ExpeditedInvoicePaymentMessagePreview> {
  const res = await apiFetch(
    `${API_BASE}/new-app/filing/${leadId}/expedited-invoice/send-payment-message/preview`,
    { method: "POST", headers: { "Content-Type": "application/json" } }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<ExpeditedInvoicePaymentMessagePreview>
    | ApiErrorResponse;
  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to load expedited payment message preview";
    throw new Error(message);
  }
  return (json as ApiSuccessResponse<ExpeditedInvoicePaymentMessagePreview>).data;
}

// --- Step 17: Payment from Client (Expedited) ---

export interface ExpeditedPaymentStatus {
  isActive: boolean;
  totalChargesExpedited: number | null;
  collectedAmountExpedited: number | null;
  outstandingAmountExpedited: number | null;
  markName: string | null;
}

export async function getExpeditedPaymentStatus(
  leadId: string
): Promise<ExpeditedPaymentStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/filing/${leadId}/expedited-payment/status`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<ExpeditedPaymentStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to load expedited payment status";
    throw new Error(message);
  }
  return (json as ApiSuccessResponse<ExpeditedPaymentStatus>).data;
}

export async function saveExpeditedPayment(
  leadId: string,
  payload: { collectedAmountExpedited?: number }
): Promise<ExpeditedPaymentStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/filing/${leadId}/expedited-payment/save`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<ExpeditedPaymentStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to save expedited payment";
    throw new Error(message);
  }
  return (json as ApiSuccessResponse<ExpeditedPaymentStatus>).data;
}

// --- Step 5: Stamp Paper Details to Vendor ---

/** Stamp vendor option for "Name of Stamp Vendor" dropdown (from app settings). */
export interface StampVendorOption {
  name: string;
  phone: string;
  isDefault?: boolean;
}

export async function getStampVendors(): Promise<StampVendorOption[]> {
  const res = await apiFetch(`${API_BASE}/app-settings/stamp-vendors`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });

  const json = (await res.json()) as
    | ApiSuccessResponse<StampVendorOption[]>
    | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to load stamp vendors";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<StampVendorOption[]>).data;
}

export interface StampPaperPerApplicationInput {
  classNumber: number;
  typeOfMark: string;
  stampPapersUa?: number;
  stampPapersPoa?: number;
}

export interface UpdateStampPaperDetailsToVendorPayload {
  stampPapersPerApplication?: StampPaperPerApplicationInput[];
  stampPapersForUa?: number;
  stampPapersForPoA?: number;
  stampVendorName?: string;
  stampVendorPhone?: string;
}

export interface StampPaperDetailsToVendorResponse {
  stampPapersForUa: number | null;
  stampPapersForPoA: number | null;
  stampVendorName: string | null;
  stampVendorPhone: string | null;
  amountPayableToStampVendor: number;
}

export async function updateStampPaperDetailsToVendor(
  leadId: string,
  payload: UpdateStampPaperDetailsToVendorPayload
): Promise<StampPaperDetailsToVendorResponse> {
  const res = await apiFetch(
    `${API_BASE}/new-app/filing/${leadId}/stamp-paper-details-to-vendor`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<StampPaperDetailsToVendorResponse>
    | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to update stamp paper details to vendor";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<StampPaperDetailsToVendorResponse>).data;
}

export async function getStampPaperPreview(
  leadId: string,
  type: "affidavit" | "poa"
): Promise<{ message: string; active: boolean }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/filing/${leadId}/stamp-paper/preview?type=${type}`,
    { method: "GET" }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<{ message: string; active: boolean }>
    | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to get stamp paper preview";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<{ message: string; active: boolean }>).data;
}

export async function sendStampPaperWhatsApp(
  leadId: string,
  type: "affidavit" | "poa"
): Promise<{ waLink: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/filing/${leadId}/stamp-paper/send-whatsapp?type=${type}`,
    { method: "POST", headers: { "Content-Type": "application/json" } }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink: string }>
    | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to send stamp paper to vendor";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<{ waLink: string }>).data;
}

// --- Step 6: Stamp Paper from Vendor ---

export interface UpdateStampPaperFromVendorPayload {
  amountPayableToStampVendor?: number;
  paidToVendor?: boolean;
}

export interface StampPaperFromVendorResponse {
  amountPayableToStampVendor: number;
  paidToVendor: boolean | null;
  stampVendorPaymentStatusDisplay: string;
}

export async function updateStampPaperFromVendor(
  leadId: string,
  payload: UpdateStampPaperFromVendorPayload
): Promise<StampPaperFromVendorResponse> {
  const res = await apiFetch(
    `${API_BASE}/new-app/filing/${leadId}/stamp-paper-from-vendor`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<StampPaperFromVendorResponse>
    | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to update stamp paper from vendor";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<StampPaperFromVendorResponse>).data;
}

// --- Step 10: Notarisation of Documents ---

export interface NotarisationPerApplicationPayload {
  classNumber: number;
  typeOfMark: string;
  amountPayableToNotary?: number;
  paidToNotary?: boolean;
}

export interface UpdateNotarisationOfDocumentsPayload {
  amountPayableToNotary?: number;
  paidToNotary?: boolean;
  notarisationPerApplication?: NotarisationPerApplicationPayload[];
}

export interface NotarisationPerApplicationResponse {
  classNumber: number;
  typeOfMark: string;
  stampPapersUa: number;
  amountPayableToNotary: number;
  paidToNotary: boolean | null;
  paymentStatusDisplay: string;
}

export interface NotarisationOfDocumentsResponse {
  stampPapersForUa: number | null;
  stampPapersUaTotal: number | null;
  amountPayableToNotary: number;
  paidToNotary: boolean | null;
  notaryPaymentStatusDisplay: string;
  notarisationPerApplication: NotarisationPerApplicationResponse[] | null;
}

export async function updateNotarisationOfDocuments(
  leadId: string,
  payload: UpdateNotarisationOfDocumentsPayload
): Promise<NotarisationOfDocumentsResponse> {
  const res = await apiFetch(
    `${API_BASE}/new-app/filing/${leadId}/notarisation-of-documents`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<NotarisationOfDocumentsResponse>
    | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to update notarisation of documents";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<NotarisationOfDocumentsResponse>).data;
}

// --- Step 7: Power of Attorney Preparation ---

export interface PowerOfAttorneyPdfMeta {
  key: string;
  url: string;
  filename: string;
}

export interface PowerOfAttorneyItem {
  poaNo: number;
  classNumber: number;
  typeOfMark: string;
  isRequired: boolean;
  mark: string | null;
  applicant: string | null;
  applicantAddress: string | null;
  applicantType: string | null;
  signatoryName: string | null;
  signatoryDesignation: string | null;
  dateOfPowerOfAttorney: string | null;
  dateOfPowerOfAttorneyFormatted: string | null;
  descriptionOfApplicant: string | null;
  templateVariant: string;
  templateFiles: string[];
  pdf: PowerOfAttorneyPdfMeta | null;
}

export interface PowerOfAttorneyContextResponse {
  powerOfAttorneys: PowerOfAttorneyItem[];
}

export async function getPowerOfAttorneyContext(
  leadId: string
): Promise<PowerOfAttorneyContextResponse> {
  const res = await apiFetch(
    `${API_BASE}/new-app/filing/${leadId}/power-of-attorney`,
    { method: "GET" }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<PowerOfAttorneyContextResponse>
    | ApiErrorResponse;

  if (!res.ok) {
    const raw =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to load Power of Attorney context";
    const message = Array.isArray(raw) ? raw[0] : String(raw);
    throw new Error(message);
  }

  const data = (json as ApiSuccessResponse<PowerOfAttorneyContextResponse & { powerOfAttorney?: PowerOfAttorneyItem }>).data;
  return normalizePowerOfAttorneyResponse(data);
}

export interface UpdatePowerOfAttorneyPayload {
  mark?: string;
  applicant?: string;
  applicantAddress?: string;
  applicantType?: string;
  signatoryName?: string;
  signatoryDesignation?: string;
  dateOfPowerOfAttorney?: string;
  descriptionOfApplicant?: string;
}

export async function updatePowerOfAttorneyEntry(
  leadId: string,
  payload: UpdatePowerOfAttorneyPayload
): Promise<PowerOfAttorneyContextResponse> {
  const res = await apiFetch(
    `${API_BASE}/new-app/filing/${leadId}/power-of-attorney`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<PowerOfAttorneyContextResponse>
    | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to update Power of Attorney entry";
    throw new Error(message);
  }

  const data = (json as ApiSuccessResponse<PowerOfAttorneyContextResponse & { powerOfAttorney?: PowerOfAttorneyItem }>).data;
  return normalizePowerOfAttorneyResponse(data);
}

function normalizePowerOfAttorneyResponse(
  data: PowerOfAttorneyContextResponse & { powerOfAttorney?: PowerOfAttorneyItem }
): PowerOfAttorneyContextResponse {
  if (data.powerOfAttorneys?.length) {
    return { powerOfAttorneys: data.powerOfAttorneys };
  }
  if (data.powerOfAttorney) {
    const item = data.powerOfAttorney;
    return {
      powerOfAttorneys: [
        {
          poaNo: item.poaNo ?? 1,
          classNumber: item.classNumber ?? 1,
          typeOfMark: item.typeOfMark ?? "Word",
          isRequired: item.isRequired ?? true,
          mark: item.mark,
          applicant: item.applicant,
          applicantAddress: item.applicantAddress,
          applicantType: item.applicantType,
          signatoryName: item.signatoryName,
          signatoryDesignation: item.signatoryDesignation,
          dateOfPowerOfAttorney: item.dateOfPowerOfAttorney,
          dateOfPowerOfAttorneyFormatted: item.dateOfPowerOfAttorneyFormatted,
          descriptionOfApplicant: item.descriptionOfApplicant,
          templateVariant: item.templateVariant,
          templateFiles: item.templateFiles ?? [],
          pdf: item.pdf,
        },
      ],
    };
  }
  return { powerOfAttorneys: [] };
}

export async function generatePowerOfAttorneyPdf(leadId: string): Promise<PowerOfAttorneyPdfMeta> {
  const res = await apiFetch(
    `${API_BASE}/new-app/filing/${leadId}/power-of-attorney/generate-pdf`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<PowerOfAttorneyPdfMeta>
    | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to generate Power of Attorney PDF";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<PowerOfAttorneyPdfMeta>).data;
}

// --- User Affidavit (Filing Step 8) ---

export interface UserAffidavitPdfMeta {
  key: string;
  url: string;
  filename: string;
}

export interface UserAffidavitItem {
  uaNo: number;
  classNumber: number;
  typeOfMark: string;
  isRequired: boolean;
  noOfApplications: number;
  mark: string | null;
  applicant: string | null;
  applicantAddress: string | null;
  applicantType: string | null;
  descriptionOfApplicant: string | null;
  signatoryName: string | null;
  signatoryGender: string | null;
  signatoryRelationWithGuardian: string | null;
  signatoryGuardianName: string | null;
  signatoryAge: string | null;
  signatoryDesignation: string | null;
  signatoryDetails: string | null;
  userDate: string | null;
  userDateFormatted: string | null;
  dateOfUserAffidavit: string | null;
  dateOfUserAffidavitFormatted: string | null;
  gsTypeA: string;
  gsTypeB: string;
  gsTypeC: string;
  goodsServices: string;
  supportingDocuments: { docNo: number; docName: string; docDetail: string; docDate: string }[];
  templateVariant: string;
  templateFiles: string[];
  pdf: UserAffidavitPdfMeta | null;
}

export interface UserAffidavitContextResponse {
  isStepActive: boolean;
  userAffidavits: UserAffidavitItem[];
}

export async function getUserAffidavitContext(
  leadId: string
): Promise<UserAffidavitContextResponse> {
  const res = await apiFetch(
    `${API_BASE}/new-app/filing/${leadId}/user-affidavit`,
    { method: "GET" }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<UserAffidavitContextResponse>
    | ApiErrorResponse;

  if (!res.ok) {
    const raw =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to load User Affidavit context";
    const message = Array.isArray(raw) ? raw[0] : String(raw);
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<UserAffidavitContextResponse>).data;
}

export interface UpdateUserAffidavitPayload {
  classNumber: number;
  typeOfMark: string;
  isRequired?: boolean;
  mark?: string;
  applicant?: string;
  applicantAddress?: string;
  applicantType?: string;
  signatoryName?: string;
  signatoryRelationWithGuardian?: string;
  signatoryGuardianName?: string;
  signatoryAge?: string;
  signatoryDesignation?: string;
  signatoryDetails?: string;
  dateOfUserAffidavit?: string;
}

export async function updateUserAffidavitEntry(
  leadId: string,
  payload: UpdateUserAffidavitPayload
): Promise<UserAffidavitContextResponse> {
  const res = await apiFetch(
    `${API_BASE}/new-app/filing/${leadId}/user-affidavit`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<UserAffidavitContextResponse>
    | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to update User Affidavit entry";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<UserAffidavitContextResponse>).data;
}

export async function generateUserAffidavitPdf(
  leadId: string,
  payload: { classNumber: number; typeOfMark: string }
): Promise<
  UserAffidavitPdfMeta & {
    uaNo: number;
    classNumber: number;
    typeOfMark: string;
  }
> {
  const res = await apiFetch(
    `${API_BASE}/new-app/filing/${leadId}/user-affidavit/generate-pdf`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<
        UserAffidavitPdfMeta & {
          uaNo: number;
          classNumber: number;
          typeOfMark: string;
        }
      >
    | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to generate User Affidavit PDF";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<
    UserAffidavitPdfMeta & {
      uaNo: number;
      classNumber: number;
      typeOfMark: string;
    }
  >).data;
}

// --- Invoice Advance Amount (Step 04) ---

export interface AdvanceAmountResponse {
  leadId: string;
  advanceAmount: number;
  collectedAmount: number;
}

export interface InvoiceAdvancePayload {
  advanceAmount?: number;
  sendWhatsapp?: boolean;
}

export async function getAdvanceAmount(
  leadId: string
): Promise<AdvanceAmountResponse> {
  const res = await apiFetch(
    `${API_BASE}/new-app/finance/${leadId}/advance-amount`,
    { method: "GET" }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<AdvanceAmountResponse>
    | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to load advance amount";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<AdvanceAmountResponse>).data;
}

export async function invoiceAdvance(
  leadId: string,
  payload: InvoiceAdvancePayload
): Promise<{ leadId: string; advanceAmount: number }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/finance/${leadId}/invoice-advance`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<{ leadId: string; advanceAmount: number }>
    | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to save invoice advance";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<{ leadId: string; advanceAmount: number }>)
    .data;
}

// --- Advance Amount from Client (Step 05) ---

export interface CollectAdvancePayload {
  collectedAmount: number;
}

export async function collectAdvance(
  leadId: string,
  payload: CollectAdvancePayload
): Promise<{ leadId: string; collectedAmount: number }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/finance/${leadId}/collect-advance`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<{ leadId: string; collectedAmount: number }>
    | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to save collected advance amount";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<{
    leadId: string;
    collectedAmount: number;
  }>).data;
}

// --- Class List to Client (Step 06) ---

export interface ClassListItemApi {
  classNumber: number;
  goodsServices: string[];
}

export interface SavedClassListResponse {
  markName?: string;
  classes?: ClassListItemApi[];
}

export interface Step6ContextResponse {
  advanceAmount: number;
  paymentDetails: {
    gpayPhonepe: string;
    accountHolder: string;
    accountNo: string;
    bank: string;
    branch: string;
    ifscCode: string;
  };
  savedClassList: SavedClassListResponse | null;
}

export interface GetListsByClassResponse {
  class: number;
  goodsServices: string[];
}

export interface SaveClassListPayload {
  classes: ClassListItemApi[];
}

export interface SaveClassListResponse {
  markId: string;
  classes: ClassListItemApi[];
  waLink?: string;
}

export async function getSavedClassList(
  leadId: string
): Promise<SavedClassListResponse | null> {
  const res = await apiFetch(
    `${API_BASE}/new-app/class-list/${leadId}/saved`,
    { method: "GET" }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<SavedClassListResponse>
    | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to load saved class list";
    throw new Error(message);
  }

  const data = (json as ApiSuccessResponse<SavedClassListResponse>).data;
  return data?.classes ? data : null;
}

export async function getStep6Context(
  leadId: string
): Promise<Step6ContextResponse> {
  const res = await apiFetch(
    `${API_BASE}/new-app/class-list/${leadId}/step-6-context`,
    { method: "GET" }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<Step6ContextResponse>
    | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to load step 6 context";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<Step6ContextResponse>).data;
}

export async function getListsByClass(
  classNum: number,
  search?: string
): Promise<GetListsByClassResponse> {
  const params = new URLSearchParams({ class: String(classNum) });
  if (search?.trim()) params.set("search", search.trim());

  const res = await apiFetch(
    `${API_BASE}/new-app/class-list/by-class?${params}`,
    { method: "GET" }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<GetListsByClassResponse>
    | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to load goods/services for class";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<GetListsByClassResponse>).data;
}

// --- Search Report to Client (Step 07) ---

export interface SearchReportClassItem {
  classNumber: number;
  similarity: boolean;
  fileUrls: string[];
}

export interface SearchReportDetailsClass {
  classNumber: number;
  goodsServices: string[];
  similarity: boolean | null;
  fileUrls: string[];
  fileDownloadUrls: string[];
}

export interface SearchReportDetailsResponse {
  lead: LeadDetails;
  classes: SearchReportDetailsClass[];
}

export interface GenerateSearchReportUploadUrlsPayload {
  files: { filename: string; mimeType: string }[];
}

export interface SearchReportUploadUrl {
  key: string;
  bucket: string;
  url: string;
}

export interface SubmitSearchReportPayload {
  nonDistinctiveness: boolean;
  classes: { class: number; similarity: boolean; fileUrls: string[] }[];
  sendWhatsapp?: boolean;
}

export async function getSearchReportDetails(
  leadId: string
): Promise<SearchReportDetailsResponse> {
  const res = await apiFetch(`${API_BASE}/new-app/${leadId}/all-discussion-details`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });

  const json = (await res.json()) as
    | ApiSuccessResponse<SearchReportDetailsResponse>
    | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to load search report details";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<SearchReportDetailsResponse>).data;
}

export async function generateSearchReportUploadUrls(
  leadId: string,
  payload: GenerateSearchReportUploadUrlsPayload
): Promise<SearchReportUploadUrl[]> {
  const res = await apiFetch(
    `${API_BASE}/new-app/discussion/${leadId}/search-report/upload-urls`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<SearchReportUploadUrl[]>
    | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to get upload URLs";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<SearchReportUploadUrl[]>).data;
}

export async function submitSearchReport(
  leadId: string,
  payload: SubmitSearchReportPayload
): Promise<{ waLink?: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/discussion/${leadId}/search-report/submit`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink?: string }>
    | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to submit search report";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<{ waLink?: string }>).data;
}

export async function saveClassList(
  leadId: string,
  payload: SaveClassListPayload
): Promise<SaveClassListResponse> {
  const res = await apiFetch(
    `${API_BASE}/new-app/class-list/${leadId}/save`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<SaveClassListResponse>
    | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to save class list";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<SaveClassListResponse>).data;
}

export async function sendClassListToClient(
  leadId: string
): Promise<{ waLink?: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/class-list/${leadId}/send-to-client`,
    { method: "POST", headers: { "Content-Type": "application/json" } }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink?: string }>
    | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to send class list to client";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<{ waLink?: string }>).data;
}

// --- Proposal to Client (Step 09) ---

export interface ProposalContextClass {
  classNumber: number;
  goodsServices: string[];
  similarity: boolean;
}

export interface ProposalContextResponse {
  addresseeOptions: string[];
  addresseeHonorificOptions: readonly string[];
  keralaDistricts: readonly string[];
  govtFeeOptions: number[];
  profFee: number;
  markName: string | null;
  classes: ProposalContextClass[];
  similarity: boolean;
  nonDistinctiveness: boolean | null;
  proposalNo: string | null;
  proposalDate: string;
  savedProposal: {
    addresseeDesignation: string | null;
    addresseeCustomName: string | null;
    firmName: string | null;
    place: string | null;
    addresseeHonorific: string | null;
    govtFee: number;
    profFeeDiscount: number;
    typesOfMarks: string[];
  };
}

export interface SaveProposalPayload {
  addresseeDesignation: string;
  addresseeCustomName?: string;
  firmName: string;
  place: string;
  addresseeHonorific: string;
  govtFee: number;
  profFeeDiscount?: number;
  typesOfMarks?: string[];
  proposalDate?: string;
}

/** Mirrors backend `getProposalPreview` — aggregated proposal for review (page 35–style fields). */
export interface ProposalPreviewClass {
  classNumber: number;
  similarity: boolean;
  goodServices: string;
  type: string;
  types: string[];
  paraText: string;
  paraTextBefore: string;
  paraTextRed: string;
}

export interface ProposalPreviewResponse {
  addressee: {
    designation: string;
    firmName: string;
    place: string;
    honorific: string;
  };
  markName: string;
  class: string[];
  classes: ProposalPreviewClass[];
  classesWithLabel: { type: string; classNumber: string }[];
  nonDistinctiveness: boolean;
  govtFee: number;
  profFee: number;
  profFeeDiscount: number;
  totalCharges: number;
  proposalNo: string | null;
  proposalDate: string | null;
  typesOfMarks: string[];
}

export async function getProposalPreview(
  leadId: string
): Promise<ProposalPreviewResponse> {
  const res = await apiFetch(
    `${API_BASE}/new-app/proposal/${leadId}/preview`,
    { method: "GET" }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<ProposalPreviewResponse>
    | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to load proposal preview";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<ProposalPreviewResponse>).data;
}

export async function getProposalContext(
  leadId: string
): Promise<ProposalContextResponse> {
  const res = await apiFetch(
    `${API_BASE}/new-app/proposal/${leadId}/context`,
    { method: "GET" }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<ProposalContextResponse>
    | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to load proposal context";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<ProposalContextResponse>).data;
}

export async function saveProposal(
  leadId: string,
  payload: SaveProposalPayload
): Promise<{ proposalNo: string; proposalDate: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/proposal/${leadId}/save`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<{ proposalNo: string; proposalDate: string }>
    | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to save proposal";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<{ proposalNo: string; proposalDate: string }>).data;
}

export async function sendProposalToClient(
  leadId: string
): Promise<{ waLink?: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/proposal/${leadId}/send-to-client`,
    { method: "POST", headers: { "Content-Type": "application/json" } }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink?: string }>
    | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to send proposal to client";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<{ waLink?: string }>).data;
}

// --- Confirmation from Client (Step 10) ---

export interface ConfirmationClassDetails {
  classNumber: number;
  goodsServices: string[];
  similarity: boolean | null;
  fileUrls: string[];
  typesOfMark: string[];
}

export interface AllDiscussionDetailsResponse {
  lead: LeadDetails;
  finance: { advanceAmount: number; collectedAmount: number } | null;
  proposal: { typesOfMarks: string[]; proposalNo: string | null; proposalDate: string | null } | null;
  confirmation: {
    noOfApplications: number;
    expeditedApplication: boolean;
    selectedClasses: { classNumber: number; typesOfMark: string[] }[];
  } | null;
  classes: ConfirmationClassDetails[];
}

export interface SaveConfirmationPayload {
  selectedTypesOfMark: { classNumber: number; types: string[] }[];
  noOfApplications: number;
  expeditedApplication: boolean;
}

export async function getAllDiscussionDetails(
  leadId: string
): Promise<AllDiscussionDetailsResponse> {
  const res = await apiFetch(`${API_BASE}/new-app/${leadId}/all-discussion-details`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });

  const json = (await res.json()) as
    | ApiSuccessResponse<AllDiscussionDetailsResponse>
    | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to load discussion details";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<AllDiscussionDetailsResponse>).data;
}

export async function saveConfirmation(
  leadId: string,
  payload: SaveConfirmationPayload
): Promise<{ leadId: string }> {
  const res = await apiFetch(`${API_BASE}/new-app/confirmation/${leadId}/save`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  const json = (await res.json()) as
    | ApiSuccessResponse<{ leadId: string }>
    | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to save confirmation";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<{ leadId: string }>).data;
}

// --- Invoice to Client (Step 11) ---
// Types match backend InvoicePreviewDto / buildInvoiceTotalsSummary / buildInvoicePerAppFees

export interface InvoiceTotalsSummaryDto {
  noOfApplications: number;
  govtFeeTotal: number;
  expeditedFeeTotal: number;
  profFeeLegalAdviceTotal: number;
  profFeeFilingTotal: number;
  profFeeFollowUpTotal: number;
  profFeeTotal: number;
  discountTotal: number;
  totalChargesBeforeDeductions: number;
  collectedAdvance: number;
  invoiceAmount: number;
}

export interface InvoicePerAppFeesDto {
  govtFeePerApp: number;
  expeditedFeePerApp: number;
  profFeeLegalAdvicePerApp: number;
  profFeeFilingPerApp: number;
  profFeeFollowUpPerApp: number;
  profFeePerApp: number;
  discountInProfFee: number;
}

export interface InvoicePreviewResponse {
  selectedClasses: string | null;
  selectedClassesLabel: string;
  addressee: {
    designation: string | null;
    companyName: string | null;
    location: string | null;
  };
  formOfAddressee: string | null;
  selectedTypeOfMark: string | null;
  expeditedApplication: boolean;
  invoiceNo: string | null;
  includeInvoiceNo: boolean;
  invoiceDate: string | null;
  invoiceDocumentUrl: string | null;
  invoiceDocumentFilename: string | null;
  markName: string | null;
  perAppFees: InvoicePerAppFeesDto;
  totalsSummary: InvoiceTotalsSummaryDto;
}

export async function getInvoicePreview(
  leadId: string
): Promise<InvoicePreviewResponse> {
  const res = await apiFetch(
    `${API_BASE}/new-app/invoice/${leadId}/preview`,
    { method: "GET" }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<InvoicePreviewResponse>
    | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to load invoice preview";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<InvoicePreviewResponse>).data;
}

export async function saveInvoice(
  leadId: string,
  payload: { invoiceDate?: string; includeInvoiceNo?: boolean; profFeeDiscount?: number }
): Promise<{ invoiceNo: string; invoiceDate: string; includeInvoiceNo: boolean }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/invoice/${leadId}/save`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<{ invoiceNo: string; invoiceDate: string; includeInvoiceNo: boolean }>
    | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to save invoice";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<{
    invoiceNo: string;
    invoiceDate: string;
    includeInvoiceNo: boolean;
  }>).data;
}

export async function generateInvoiceDocument(
  leadId: string
): Promise<{ s3Key: string; downloadUrl: string; filename: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/invoice/${leadId}/generate`,
    { method: "GET" }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<{ s3Key: string; downloadUrl: string; filename: string }>
    | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to generate invoice document";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<{ s3Key: string; downloadUrl: string; filename: string }>).data;
}

export async function sendInvoiceToClient(
  leadId: string
): Promise<{ waLink?: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/invoice/${leadId}/send-to-client`,
    { method: "POST", headers: { "Content-Type": "application/json" } }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink?: string }>
    | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to send invoice to client";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<{ waLink?: string }>).data;
}

export interface SendInvoiceToClientPreviewResponse {
  sendWhatsApp: boolean;
  sendEmail: boolean;
  invoiceDocument: {
    key: string | null;
    url: string | null;
    filename: string;
  };
  whatsappMessage: string | null;
  emailPreview: {
    to: string | null;
    subject: string;
    body: string;
  } | null;
  paymentMessagePreview: string | null;
}

export async function getInvoiceSendToClientPreview(
  leadId: string
): Promise<SendInvoiceToClientPreviewResponse> {
  const res = await apiFetch(
    `${API_BASE}/new-app/invoice/${leadId}/send-to-client/preview`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<SendInvoiceToClientPreviewResponse>
    | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to load invoice send preview";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<SendInvoiceToClientPreviewResponse>).data;
}

export async function sendInvoicePaymentMessage(
  leadId: string
): Promise<{ waLink: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/invoice/${leadId}/send-payment-message`,
    { method: "POST", headers: { "Content-Type": "application/json" } }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink: string }>
    | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to send invoice payment message";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<{ waLink: string }>).data;
}

export async function generateProposalDocument(
  leadId: string
): Promise<{ s3Key: string; downloadUrl: string; filename: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/proposal/${leadId}/generate`,
    { method: "GET" }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<{ s3Key: string; downloadUrl: string; filename: string }>
    | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to generate proposal document";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<{ s3Key: string; downloadUrl: string; filename: string }>).data;
}

// --- Payment from Client (Step 12) ---

export interface PaymentPreviewResponse {
  invoiceAmount: number | null;
  collectedAmount: number | null;
  outstandingAmount: number | null;
  markName: string | null;
}

export async function getPaymentPreview(
  leadId: string
): Promise<PaymentPreviewResponse> {
  const res = await apiFetch(
    `${API_BASE}/new-app/payment/${leadId}/preview`,
    { method: "GET" }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<PaymentPreviewResponse>
    | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to load payment preview";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<PaymentPreviewResponse>).data;
}

// --- FC Fail: Invoice for FC Failure (Substep 1B(iv)) ---

export interface FcReplyInvoiceTmRow {
  tmNo: string;
  invoiceAmount: number | null;
  individualInvoiceNo: string | null;
  discount: number | null;
  invoiceDate: string | null;
  collectedAmount: number | null;
  outstandingAmount: number | null;
  fcReplyInvoicePdfKey: string | null;
  fcReplyInvoicePdfUrl: string | null;
}

export interface FcReplyInvoiceStatus {
  invoiceAmount: number | null;
  discount: number | null;
  invoiceNo: string | null;
  invoiceDate: string | null;
  includeInvoiceNo: boolean;
  fcReplyInvoicePdfKey: string | null;
  fcReplyInvoicePdfUrl: string | null;
  collectedAmount?: number | null;
  outstandingAmount?: number | null;
  fullInvoiceAmount?: number | null;
  generatedPdfs?: Array<{
    id: string;
    invoiceMode: "per_tm";
    tmNos: string[];
    pdfKey: string;
    pdfUrl: string | null;
    sentAt: string | null;
    isActive: boolean;
    createdAt: string;
  }>;
  tmInvoiceRows: FcReplyInvoiceTmRow[];
}

export interface FcReplyInvoicePreview {
  dataLabel: string;
  addressee: string;
  addresseeLine1: string | null;
  addresseeLine2: string | null;
  addresseeLine3: string | null;
  formOfAddress: string | null;
  applications: Array<{
    tmNo: string;
    applicationDate: string;
    mark: string;
    typeOfMark: string;
    classNumber: number;
    reasonForFcFailure: string | null;
    invoiceAmount?: number | null;
    discount?: number | null;
    invoiceDate?: string | null;
    individualInvoiceNo?: string | null;
  }>;
  lastDateForFcReply: string | null;
  invoiceAmount: number | null;
  discount: number | null;
  invoiceDate: string | null;
  invoiceNo: string | null;
  includeInvoiceNo: boolean;
  selectedTmNos?: string[];
  invoiceMode?: "per_tm";
}

export interface SaveFcReplyInvoicePayload {
  invoiceDate?: string;
  includeInvoiceNo?: boolean;
  applications: Array<{
    tmNo: string;
    invoiceAmount: number;
    discount?: number;
    invoiceDate?: string;
    includeInvoiceNo?: boolean;
  }>;
}

export interface SendFcReplyInvoiceToClientPayload {
  sendWhatsApp?: boolean;
  sendEmail?: boolean;
  generatedPdfId?: string;
  tmNos?: string[];
}

/** Formalities Check status — per-lead list of applications with their current status. */
export interface FormalitiesCheckStatusApplication {
  tmNo: string;
  mark: string;
  classNumber: number;
  typeOfMark: string;
  applicationDate: string;
  status: string;
}

export interface FormalitiesCheckStatusResponse {
  acknowledgementNo: string | null;
  dateOfFiling: string | null;
  applications: FormalitiesCheckStatusApplication[];
}

export async function getFormalitiesCheckStatus(
  leadId: string
): Promise<FormalitiesCheckStatusResponse> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/formalities-check`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<FormalitiesCheckStatusResponse>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Formalities Check status"
    );
  }
  return (json as ApiSuccessResponse<FormalitiesCheckStatusResponse>).data;
}

/** FC Pass — WhatsApp preview / send (S3_01a) */
export interface FcPassInformClientPreviewResponse {
  message: string;
  applicationCount: number;
}

export async function getFcPassInformClientPreview(
  leadId: string,
  tmNos?: string[]
): Promise<FcPassInformClientPreviewResponse> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/formalities-check-pass/preview`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<FcPassInformClientPreviewResponse>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load FC Pass message preview"
    );
  }
  return (json as ApiSuccessResponse<FcPassInformClientPreviewResponse>).data;
}

export async function sendFcPassInformClientWhatsApp(
  leadId: string,
  tmNos?: string[]
): Promise<{ waLink: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/formalities-check-pass/send-whatsapp`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to send FC Pass WhatsApp"
    );
  }
  return (json as ApiSuccessResponse<{ waLink: string }>).data;
}

export async function getFcReplyInvoiceStatus(
  leadId: string
): Promise<FcReplyInvoiceStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/formalities-check-fail/invoice`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<FcReplyInvoiceStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load FC Reply invoice status"
    );
  }
  return (json as ApiSuccessResponse<FcReplyInvoiceStatus>).data;
}

export async function getFcReplyInvoicePreview(
  leadId: string
): Promise<FcReplyInvoicePreview | null> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/formalities-check-fail/invoice/preview`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<FcReplyInvoicePreview | null>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load FC Reply invoice preview"
    );
  }
  return (json as ApiSuccessResponse<FcReplyInvoicePreview | null>).data;
}

export async function saveFcReplyInvoice(
  leadId: string,
  payload: SaveFcReplyInvoicePayload
): Promise<FcReplyInvoiceStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/formalities-check-fail/invoice`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<FcReplyInvoiceStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save FC Reply invoice"
    );
  }
  return (json as ApiSuccessResponse<FcReplyInvoiceStatus>).data;
}

export async function generateFcReplyInvoicePdf(
  leadId: string,
  tmNos?: string[]
): Promise<{ s3Key: string; downloadUrl: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/formalities-check-fail/invoice/generate-pdf`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ s3Key: string; downloadUrl: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to generate FC Reply invoice PDF"
    );
  }
  return (json as ApiSuccessResponse<{ s3Key: string; downloadUrl: string }>)
    .data;
}

export async function sendFcReplyInvoiceToClient(
  leadId: string,
  payload?: SendFcReplyInvoiceToClientPayload
): Promise<{ waLink: string; emailSent?: boolean }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/formalities-check-fail/invoice/send-to-client`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload ?? { sendWhatsApp: true, sendEmail: true }),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink: string; emailSent?: boolean }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to send FC Reply invoice to client"
    );
  }
  return (json as ApiSuccessResponse<{ waLink: string; emailSent?: boolean }>).data;
}

export async function sendFcReplyInvoicePaymentMessage(
  leadId: string,
  payload?: SendFcReplyInvoiceToClientPayload
): Promise<{ waLink: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/formalities-check-fail/invoice/send-payment-message`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload ?? {}),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to send FC Reply invoice payment message"
    );
  }
  return (json as ApiSuccessResponse<{ waLink: string }>).data;
}

export interface FcReplyInvoiceEmailPreview {
  to: string | null;
  subject: string;
  body: string;
  filename: string | null;
  downloadUrl: string | null;
}

export interface FcReplyInvoicePaymentMessagePreview {
  message: string;
  invoiceAmount?: number;
  tmNos?: string[];
  emailPreview: FcReplyInvoiceEmailPreview;
}

export async function getFcReplyInvoicePaymentMessagePreview(
  leadId: string,
  payload?: SendFcReplyInvoiceToClientPayload
): Promise<FcReplyInvoicePaymentMessagePreview> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/formalities-check-fail/invoice/payment-message/preview`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload ?? {}),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<FcReplyInvoicePaymentMessagePreview>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to get FC Reply invoice payment message preview"
    );
  }
  return (json as ApiSuccessResponse<FcReplyInvoicePaymentMessagePreview>).data;
}

// --- FC Fail: Status (1B(i)) & Notice to Client (1B(ii)) ---

export interface FormalitiesCheckFailApplicationItem {
  ackApplicationId: string;
  tmNo: string;
  mark: string;
  classNumber: number;
  typeOfMark: string;
  applicationDate: string;
  status: string;
  dateOfFcNotice: string | null;
  reasonForFcFailure: string | null;
  lastDateForFcReply: string | null;
  noticeOfFcFailFileKey: string | null;
  noticeOfFcFailFilename: string | null;
  noticeOfFcFailFileUrl: string | null;
  dateOfFcReplyFiled: string | null;
  fcReplyFileKey: string | null;
  fcReplyFilename: string | null;
}

export interface FormalitiesCheckFailStatus {
  acknowledgementNo: string | null;
  dateOfFiling: string | null;
  applications: FormalitiesCheckFailApplicationItem[];
}

export async function getFormalitiesCheckFailStatus(
  leadId: string
): Promise<FormalitiesCheckFailStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/formalities-check-fail`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<FormalitiesCheckFailStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Formalities Check Fail status"
    );
  }
  return (json as ApiSuccessResponse<FormalitiesCheckFailStatus>).data;
}

export interface SetFcFailDataPayload {
  applications: Array<{
    tmNo: string;
    dateOfFcNotice?: string;
    lastDateForFcReply?: string;
    reasonForFcFailure?: string;
    noticeOfFcFailFileKey?: string;
    noticeOfFcFailFilename?: string;
  }>;
}

export async function setFormalitiesCheckFailData(
  leadId: string,
  payload: SetFcFailDataPayload
): Promise<FormalitiesCheckFailStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/formalities-check-fail`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<FormalitiesCheckFailStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save FC Fail status data"
    );
  }
  return (json as ApiSuccessResponse<FormalitiesCheckFailStatus>).data;
}

/** Substep 1B(ii): merge notice PDFs and send to client WhatsApp */
export interface FcFailNoticeToClientDocumentItem {
  tmNo: string;
  mark: string | null;
  filename: string | null;
  fileUrl: string | null;
}

export interface FcFailNoticeToClientPreview {
  recipientPhone: string | null;
  recipientEmail: string | null;
  whatsAppMessage: string;
  emailSubject: string;
  emailMessage: string;
  documents: FcFailNoticeToClientDocumentItem[];
}

/** Substep 1B(ii): Preview the Notice to Client (WhatsApp + Email) before sending. */
export async function getFcFailNoticeToClientPreview(
  leadId: string,
  tmNos?: string[]
): Promise<FcFailNoticeToClientPreview> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/formalities-check-fail/notice-to-client/preview`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<FcFailNoticeToClientPreview>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Notice to Client preview"
    );
  }
  return (json as ApiSuccessResponse<FcFailNoticeToClientPreview>).data;
}

export async function sendFcFailNoticeToClientWhatsApp(
  leadId: string,
  tmNos?: string[]
): Promise<{ waLink: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/formalities-check-fail/send-notice-to-whatsapp`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to send Notice of FC Fail to WhatsApp"
    );
  }
  return (json as ApiSuccessResponse<{ waLink: string }>).data;
}

// --- FC Fail: Call Client (Substep 1B(v)) ---

export async function recordCallClientFcFail(
  leadId: string,
  tmNos?: string[]
): Promise<void> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/formalities-check-fail/record-call-client`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );

  if (!res.ok) {
    const json = (await res.json()) as ApiErrorResponse;
    throw new Error(
      json?.message || json?.error || "Failed to record Call Client step"
    );
  }
}

// --- FC Fail: Inform Client (Substep 1B(iii)) ---

export async function getFcFailInformClientPreview(
  leadId: string,
  tmNos?: string[]
): Promise<{ message: string; applicationCount: number }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/formalities-check-fail/inform-client/preview`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<{ message: string; applicationCount: number }>
    | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to get FC Fail Inform Client preview";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<{ message: string; applicationCount: number }>).data;
}

export async function sendFcFailInformClientWhatsApp(
  leadId: string,
  tmNos?: string[]
): Promise<{ waLink: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/formalities-check-fail/inform-client/send-whatsapp`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink: string }>
    | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to send FC Fail Inform Client to WhatsApp";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<{ waLink: string }>).data;
}

// --- FC Fail: Documents from Client (Substep 1B(vii)) ---

export async function recordDocumentsFromClientFcFail(
  leadId: string,
  tmNos?: string[]
): Promise<void> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/formalities-check-fail/record-documents-from-client`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  if (!res.ok) {
    const json = (await res.json()) as ApiErrorResponse;
    throw new Error(
      json?.message || json?.error || "Failed to record documents step"
    );
  }
}

// --- FC Fail: Client Confirmation (Substep 1B(vi)) ---

export interface FcFailConfirmationStatus {
  /** Overall client decision: true = YES, false = NO, null = not yet set. */
  clientGoingAhead: boolean | null;
  /** Invoice Amount for FC Reply (fetched from invoice step). */
  invoiceAmount: number | null;
  /** Total collected amount. */
  collectedAmount: number | null;
  /** Per-TM rows. */
  applications?: Array<{
    tmNo: string;
    clientGoingAhead: boolean | null;
    collectedAmount: number | null;
    /** Per-TM invoice amount (from this TM's invoice row). */
    invoiceAmount?: number | null;
    /** Per-TM outstanding = this TM's invoice − this TM's collected. */
    outstandingAmount?: number | null;
  }>;
  /** Invoice Amount − Collected Amount. */
  outstandingAmount: number | null;
}

export interface SaveFcFailConfirmationPayload {
  applications: Array<{
    tmNo: string;
    clientGoingAhead: boolean;
    collectedAmount?: number;
  }>;
}

export async function getFcFailConfirmationFromClient(
  leadId: string
): Promise<FcFailConfirmationStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/formalities-check-fail/confirmation-from-client`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<FcFailConfirmationStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load FC Fail client confirmation"
    );
  }
  return (json as ApiSuccessResponse<FcFailConfirmationStatus>).data;
}

export async function saveFcFailConfirmationFromClient(
  leadId: string,
  payload: SaveFcFailConfirmationPayload
): Promise<void> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/formalities-check-fail/confirmation-from-client`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  if (!res.ok) {
    const json = (await res.json()) as ApiErrorResponse;
    throw new Error(
      json?.message || json?.error || "Failed to save FC Fail client confirmation"
    );
  }
}

export async function savePayment(
  leadId: string,
  payload: { collectedAmount?: number }
): Promise<void> {
  const res = await apiFetch(
    `${API_BASE}/new-app/payment/${leadId}/save`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );

  if (!res.ok) {
    const json = (await res.json()) as ApiErrorResponse;
    throw new Error(
      json?.message || json?.error || "Failed to save payment"
    );
  }
}

// --- FC Fail: Filing of Reply (Substep 1B(viii)) ---

export interface FcReplyFiledApplication {
  tmNo: string;
  dateOfFcReplyFiled: string | null;
  fcReplyFileKey: string | null;
  fcReplyFilename: string | null;
  fcReplyFileUrl: string | null;
}

export interface FcReplyFiledStatus {
  acknowledgementNo: string | null;
  dateOfFiling: string | null;
  lastDateForFcReply: string | null;
  applications: FcReplyFiledApplication[];
}

export interface FcReplyFiledPresignedUrl {
  tmNo: string;
  presignedUrl: string;
  fileKey: string;
}

export interface SetFcReplyFiledPayload {
  applications: Array<{
    tmNo: string;
    dateOfFcReplyFiled?: string;
    fcReplyFileKey?: string;
    fcReplyFilename?: string;
  }>;
}

export async function getFcReplyFiledStatus(
  leadId: string
): Promise<FcReplyFiledStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/formalities-check-fail/reply-filed`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<FcReplyFiledStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load filing of reply status"
    );
  }
  return (json as ApiSuccessResponse<FcReplyFiledStatus>).data;
}

export async function generateFcReplyFiledPresignedUrls(
  leadId: string,
  files: Array<{ tmNo: string; filename: string; mimeType: string }>
): Promise<FcReplyFiledPresignedUrl[]> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/formalities-check-fail/reply-filed/presigned-urls`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ files }),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ urls: FcReplyFiledPresignedUrl[] }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to generate presigned URLs"
    );
  }
  return (json as ApiSuccessResponse<{ urls: FcReplyFiledPresignedUrl[] }>).data.urls;
}

export async function saveFcReplyFiled(
  leadId: string,
  payload: SetFcReplyFiledPayload
): Promise<FcReplyFiledStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/formalities-check-fail/reply-filed`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<FcReplyFiledStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save filing of reply"
    );
  }
  return (json as ApiSuccessResponse<FcReplyFiledStatus>).data;
}

// --- FC Fail: Inform Client (Done) — Substep 1B(ix) ---

export async function getFcReplyFiledInformClientPreview(
  leadId: string,
  tmNos?: string[]
): Promise<{ message: string; applicationCount: number }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/formalities-check-fail/inform-client-reply-filed/preview`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ message: string; applicationCount: number }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to get Inform Client (Done) preview"
    );
  }
  return (json as ApiSuccessResponse<{ message: string; applicationCount: number }>).data;
}

export async function sendFcReplyFiledInformClientWhatsApp(
  leadId: string,
  tmNos?: string[]
): Promise<{ waLink: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/formalities-check-fail/inform-client-reply-filed/send-whatsapp`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to send Inform Client (Done) to WhatsApp"
    );
  }
  return (json as ApiSuccessResponse<{ waLink: string }>).data;
}

// --- Common: list applications for a lead filtered by status (and optional module) ---

export interface ApplicationsByStatusItem {
  tmNo: string;
  mark: string;
  classNumber: number;
  typeOfMark: string;
  applicationDate: string;
  status: string;
}

export interface ApplicationsByStatusResponse {
  acknowledgementNo: string | null;
  dateOfFiling: string | null;
  applications: ApplicationsByStatusItem[];
}

export async function getApplicationsByStatus(
  status: string,
  module: string,
  leadId?: string
): Promise<ApplicationsByStatusResponse> {
  const params = new URLSearchParams({ status, module });
  if (leadId) params.set("leadId", leadId);
  const res = await apiFetch(
    `${API_BASE}/new-app/status/applications?${params.toString()}`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<ApplicationsByStatusResponse>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load applications"
    );
  }
  return (json as ApiSuccessResponse<ApplicationsByStatusResponse>).data;
}

// --- Marked for Exam — Inform Client (Substep 2i) ---

export async function getMarkedForExamPreview(
  leadId: string,
  tmNos?: string[]
): Promise<{ message: string; applicationCount: number }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/marked-for-exam/preview`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ message: string; applicationCount: number }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to get Marked for Exam preview"
    );
  }
  return (json as ApiSuccessResponse<{ message: string; applicationCount: number }>).data;
}

export async function sendMarkedForExamWhatsApp(
  leadId: string,
  tmNos?: string[]
): Promise<{ waLink: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/marked-for-exam/send-whatsapp`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to send Marked for Exam to WhatsApp"
    );
  }
  return (json as ApiSuccessResponse<{ waLink: string }>).data;
}

// --- Marked for Exam / Examination Report — Accepted (Substep 3A) ---

export async function getExaminationReportAcceptedInformClientPreview(
  leadId: string,
  tmNos?: string[]
): Promise<{ message: string; applicationCount: number }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/examination-report-accepted/inform-client/preview`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ message: string; applicationCount: number }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to get Examination Report Accepted preview"
    );
  }
  return (json as ApiSuccessResponse<{ message: string; applicationCount: number }>).data;
}

export async function sendExaminationReportAcceptedInformClientWhatsApp(
  leadId: string,
  tmNos?: string[]
): Promise<{ waLink: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/examination-report-accepted/inform-client/send-whatsapp`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to send Examination Report Accepted to WhatsApp"
    );
  }
  return (json as ApiSuccessResponse<{ waLink: string }>).data;
}

// --- Marked for Exam / Examination Report — Accepted after Objection (Substep 3C) ---

export interface AcceptedAfterObjectionApplication {
  tmNo: string;
  mark: string;
  classNumber: number;
  typeOfMark: string;
  applicationDate: string;
  status: string;
  examinationReportAcceptanceFileKey: string | null;
  examinationReportAcceptanceFilename: string | null;
  examinationReportAcceptanceFileUrl: string | null;
}

export interface AcceptedAfterObjectionStatus {
  acknowledgementNo: string | null;
  dateOfFiling: string | null;
  applications: AcceptedAfterObjectionApplication[];
}

export async function getAcceptedAfterObjectionStatus(
  leadId: string,
  tmNos?: string[]
): Promise<AcceptedAfterObjectionStatus> {
  const params = tmNos?.length
    ? `?${tmNos.map((t) => `tmNos=${encodeURIComponent(t)}`).join("&")}`
    : "";
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/accepted-after-objection${params}`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<AcceptedAfterObjectionStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Accepted after Objection status"
    );
  }
  return (json as ApiSuccessResponse<AcceptedAfterObjectionStatus>).data;
}

export async function saveAcceptedAfterObjection(
  leadId: string,
  payload: {
    applications: Array<{
      tmNo: string;
      examinationReportAcceptanceFileKey?: string;
      examinationReportAcceptanceFilename?: string;
    }>;
  }
): Promise<AcceptedAfterObjectionStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/accepted-after-objection`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<AcceptedAfterObjectionStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save Accepted after Objection"
    );
  }
  return (json as ApiSuccessResponse<AcceptedAfterObjectionStatus>).data;
}

export async function sendAcceptedAfterObjectionPdfToWhatsApp(
  leadId: string,
  tmNos?: string[]
): Promise<{ waLink: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/accepted-after-objection/send-pdf-to-whatsapp`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to send Examination Report (after Objection) to WhatsApp"
    );
  }
  return (json as ApiSuccessResponse<{ waLink: string }>).data;
}

export async function getAcceptedAfterObjectionInformClientPreview(
  leadId: string,
  tmNos?: string[]
): Promise<{ messages: string[]; messageCount: number; applicationCount: number }> {
  const params = tmNos?.length
    ? `?${tmNos.map((t) => `tmNos=${encodeURIComponent(t)}`).join("&")}`
    : "";
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/accepted-after-objection/inform-client-preview${params}`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{
        messages: string[];
        messageCount: number;
        applicationCount: number;
      }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to get Accepted after Objection preview"
    );
  }
  return (json as ApiSuccessResponse<{
    messages: string[];
    messageCount: number;
    applicationCount: number;
  }>).data;
}

export async function sendAcceptedAfterObjectionInformClientWhatsApp(
  leadId: string,
  tmNos?: string[]
): Promise<{ waLinks: string[]; messageCount: number }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/accepted-after-objection/send-inform-client-to-whatsapp`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLinks: string[]; messageCount: number }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to send Accepted after Objection to WhatsApp"
    );
  }
  return (json as ApiSuccessResponse<{ waLinks: string[]; messageCount: number }>).data;
}

// --- Examination Report — Hearing after Objection (Step 3D — Show Cause Hearing) ---

export interface ShowCauseHearingApplication {
  tmNo: string;
  mark: string;
  typeOfMark: string;
  classNumber: number;
  applicationDate: string;
  userDate: string;
  dateOfHearing: string | null;
  hearingNoticeFileKey: string | null;
  hearingNoticeFilename: string | null;
  hearingNoticeFileUrl: string | null;
}

export interface ShowCauseHearingStatus {
  acknowledgementNo: string | null;
  dateOfFiling: string | null;
  applications: ShowCauseHearingApplication[];
  noOfApplicationsForHearing: number;
}

export async function getShowCauseHearingStatusDetails(
  leadId: string
): Promise<ShowCauseHearingStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/show-cause-hearing/status-details`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<ShowCauseHearingStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Hearing status details"
    );
  }
  return (json as ApiSuccessResponse<ShowCauseHearingStatus>).data;
}

// --- 3D(v) Invoice for Hearing (status / preview / save / generate PDF) ---

export interface HearingInvoiceTmRow {
  tmNo: string;
  defaultInvoiceAmount: number;
  invoiceAmount: number | null;
  individualInvoiceNo: string | null;
  includeInvoiceNo: boolean;
  discount: number | null;
  invoiceDate: string | null;
  collectedAmount: number | null;
  outstandingAmount: number | null;
  hearingInvoicePdfKey: string | null;
  hearingInvoicePdfUrl: string | null;
}

export interface HearingInvoiceStatus {
  invoiceAmount: number | null;
  defaultInvoiceAmount: number | null;
  invoiceNo: string | null;
  invoiceDate: string | null;
  includeInvoiceNo: boolean;
  hearingInvoicePdfKey: string | null;
  hearingInvoicePdfUrl: string | null;
  collectedAmount?: number | null;
  outstandingAmount?: number | null;
  fullInvoiceAmount?: number | null;
  fullDefaultInvoiceAmount?: number | null;
  tmInvoiceRows: HearingInvoiceTmRow[];
}

export interface HearingInvoicePreview {
  addressee: string;
  addresseeLine1: string | null;
  addresseeLine2: string | null;
  addresseeLine3: string | null;
  formOfAddress: string | null;
  applications: Array<{
    tmNo: string;
    applicationDate: string;
    mark: string;
    typeOfMark: string;
    classNumber: number;
    dateOfHearing: string | null;
    invoiceAmount: number;
    invoiceDate: string | null;
    individualInvoiceNo: string | null;
    includeInvoiceNo: boolean;
  }>;
  invoiceAmount: number;
  invoiceDate: string | null;
  invoiceNo: string | null;
  includeInvoiceNo: boolean;
  selectedTmNos?: string[];
  fullInvoiceAmount?: number;
}

export async function getHearingInvoiceStatus(
  leadId: string,
  tmNos?: string[]
): Promise<HearingInvoiceStatus> {
  const params = tmNos?.length
    ? `?${tmNos.map((t) => `tmNos=${encodeURIComponent(t)}`).join("&")}`
    : "";
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/hearing/invoice${params}`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<HearingInvoiceStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Hearing Invoice"
    );
  }
  return (json as ApiSuccessResponse<HearingInvoiceStatus>).data;
}

export async function getHearingInvoicePreview(
  leadId: string,
  tmNos?: string[]
): Promise<HearingInvoicePreview | null> {
  const params = tmNos?.length
    ? `?${tmNos.map((t) => `tmNos=${encodeURIComponent(t)}`).join("&")}`
    : "";
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/hearing/invoice/preview${params}`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<HearingInvoicePreview | null>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Hearing Invoice preview"
    );
  }
  return (json as ApiSuccessResponse<HearingInvoicePreview | null>).data;
}

export async function saveHearingInvoice(
  leadId: string,
  applications: Array<{
    tmNo: string;
    invoiceAmount: number;
    discount?: number;
    invoiceDate?: string;
    includeInvoiceNo?: boolean;
  }>
): Promise<HearingInvoiceStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/hearing/invoice`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ applications }),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<HearingInvoiceStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save Hearing Invoice"
    );
  }
  return (json as ApiSuccessResponse<HearingInvoiceStatus>).data;
}

export async function generateHearingInvoicePdf(
  leadId: string,
  tmNos?: string[]
): Promise<{ s3Key: string; downloadUrl: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/hearing/invoice/generate-pdf`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ s3Key: string; downloadUrl: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to generate Hearing Invoice PDF"
    );
  }
  return (json as ApiSuccessResponse<{ s3Key: string; downloadUrl: string }>).data;
}

// --- 3D(vii) Revised Hearing Invoice (3abr/3acr/3adr/3aer) — review item 68 ---
// Sent when the client confirms the hearing need not be attended for every
// application: the remaining marks get a revised 3D(v) invoice. These endpoints
// are independent of the 3D(vii) confirmation value; they only require hearing
// applications and a saved 3D(v) invoice.

export interface RevisedHearingInvoiceApplicationRow {
  tmNo: string;
  /** 3acr) per TM */
  invoiceAmount: number;
  discount: number;
  /** 3aer) per TM (DD-MM-YYYY) */
  invoiceDate: string | null;
  /** 3adr) per TM */
  invoiceNo: string | null;
  includeInvoiceNo?: boolean | null;
}

export interface RevisedHearingInvoiceStatus {
  /** 3ab) original */
  noOfApplicationsForHearing: number;
  /** 3ac) original */
  invoiceAmount: number | null;
  /** 3ad) original */
  invoiceNo: string | null;
  /** 3ae) original */
  invoiceDate: string | null;
  /** 3abr) revised */
  noOfAppsRevised: number | null;
  /** 3acr) revised (sum of per-TM amounts) */
  invoiceAmountRevised: number | null;
  /** 3adr) revised batch/header invoice no. */
  invoiceNoRevised: string | null;
  /** 3aer) revised header date */
  invoiceDateRevised: string | null;
  revisedSelectedTmNos: string[] | null;
  revisedApplications: RevisedHearingInvoiceApplicationRow[] | null;
  revisedIncludeInvoiceNo: boolean | null;
}

export async function getRevisedHearingInvoiceStatus(
  leadId: string,
  tmNos?: string[]
): Promise<RevisedHearingInvoiceStatus> {
  const params = tmNos?.length
    ? `?${tmNos.map((t) => `tmNos=${encodeURIComponent(t)}`).join("&")}`
    : "";
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/hearing/revised-invoice${params}`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<RevisedHearingInvoiceStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Revised Hearing Invoice"
    );
  }
  return (json as ApiSuccessResponse<RevisedHearingInvoiceStatus>).data;
}

export async function saveRevisedHearingInvoice(
  leadId: string,
  applications: Array<{
    tmNo: string;
    invoiceAmount: number;
    discount?: number;
    invoiceDate?: string;
    includeInvoiceNo?: boolean;
    invoiceNo?: string;
  }>
): Promise<RevisedHearingInvoiceStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/hearing/revised-invoice`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ applications }),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<RevisedHearingInvoiceStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save Revised Hearing Invoice"
    );
  }
  return (json as ApiSuccessResponse<RevisedHearingInvoiceStatus>).data;
}

export async function generateRevisedHearingInvoicePdf(
  leadId: string,
  tmNos?: string[]
): Promise<{ s3Key: string; downloadUrl: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/hearing/revised-invoice/generate-pdf`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ s3Key: string; downloadUrl: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to generate Revised Hearing Invoice PDF"
    );
  }
  return (json as ApiSuccessResponse<{ s3Key: string; downloadUrl: string }>).data;
}

// --- 3D(v) Hearing Invoice send / preview (WhatsApp + Email) ---

export interface HearingInvoiceEmailPreview {
  to: string | null;
  subject: string;
  body: string;
  filename: string | null;
  downloadUrl: string | null;
}

export interface HearingInvoicePaymentMessagePreview {
  pdfMessage: string;
  paymentMessage: string;
  emailPreview: HearingInvoiceEmailPreview;
}

function buildHearingTmNosQuery(tmNos?: string[]): string {
  if (!tmNos?.length) return "";
  const params = new URLSearchParams();
  for (const tm of tmNos) params.append("tmNos", tm);
  return `?${params.toString()}`;
}

export async function getHearingInvoicePaymentMessagePreview(
  leadId: string,
  tmNos?: string[]
): Promise<HearingInvoicePaymentMessagePreview> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/hearing/invoice/whatsapp-preview${buildHearingTmNosQuery(tmNos)}`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<HearingInvoicePaymentMessagePreview>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Hearing Invoice message preview"
    );
  }
  return (json as ApiSuccessResponse<HearingInvoicePaymentMessagePreview>).data;
}

export async function sendHearingInvoiceToClient(
  leadId: string,
  body: {
    sendWhatsApp?: boolean;
    sendEmail?: boolean;
    tmNos?: string[];
    generatedPdfId?: string;
  } = {}
): Promise<{ waLink: string; emailSent?: boolean }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/hearing/invoice/send-to-client`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink: string; emailSent?: boolean }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to send Hearing Invoice to client"
    );
  }
  return (json as ApiSuccessResponse<{ waLink: string; emailSent?: boolean }>).data;
}

export async function sendHearingInvoicePaymentMessage(
  leadId: string,
  tmNos?: string[]
): Promise<{ waLink: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/hearing/invoice/send-payment-message`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to send Hearing Invoice payment message"
    );
  }
  return (json as ApiSuccessResponse<{ waLink: string }>).data;
}

// --- 3D(viii) TM-M before Hearing Invoice ---

export interface TmMBeforeHearingInvoiceTmRow {
  tmNo: string;
  defaultInvoiceAmount: number;
  invoiceAmount: number | null;
  collectedAmount: number | null;
  outstandingAmount: number | null;
  invoiceDate: string | null;
  /** Whether the invoice number prints on the generated PDF (review item 14). */
  includeInvoiceNo?: boolean;
  tmMBeforeHearingInvoicePdfKey: string | null;
  tmMBeforeHearingInvoicePdfUrl: string | null;
}

export interface TmMBeforeHearingInvoiceStatus {
  invoiceAmount: number | null;
  defaultInvoiceAmount: number | null;
  invoiceNo: string | null;
  invoiceDate: string | null;
  noOfAppsForTmMBeforeHearing: number;
  includeInvoiceNo: boolean;
  tmMBeforeHearingInvoicePdfKey: string | null;
  tmMBeforeHearingInvoicePdfUrl: string | null;
  isActive: boolean;
  collectedAmount?: number | null;
  outstandingAmount?: number | null;
  fullInvoiceAmount?: number | null;
  fullDefaultInvoiceAmount?: number | null;
  tmInvoiceRows: TmMBeforeHearingInvoiceTmRow[];
}

export interface TmMBeforeHearingInvoicePreview {
  addressee: string;
  formOfAddress: string | null;
  applications: Array<{
    tmNo: string;
    applicationDate: string;
    mark: string;
    typeOfMark: string;
    classNumber: number;
    dateOfHearing: string | null;
    selected?: boolean;
    invoiceAmount?: number;
    invoiceDate?: string | null;
  }>;
  invoiceAmount: number;
  invoiceDate: string | null;
  invoiceNo: string | null;
  includeInvoiceNo: boolean;
  noOfAppsForTmMBeforeHearing: number;
}

export async function getTmMBeforeHearingInvoiceStatus(
  leadId: string,
  tmNos?: string[]
): Promise<TmMBeforeHearingInvoiceStatus> {
  const params = tmNos?.length
    ? `?${tmNos.map((t) => `tmNos=${encodeURIComponent(t)}`).join("&")}`
    : "";
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/hearing/tm-m-before/invoice${params}`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<TmMBeforeHearingInvoiceStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load TM-M before Hearing Invoice"
    );
  }
  return (json as ApiSuccessResponse<TmMBeforeHearingInvoiceStatus>).data;
}

export async function getTmMBeforeHearingInvoicePreview(
  leadId: string,
  tmNos?: string[]
): Promise<TmMBeforeHearingInvoicePreview | null> {
  const params = tmNos?.length
    ? `?${tmNos.map((t) => `tmNos=${encodeURIComponent(t)}`).join("&")}`
    : "";
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/hearing/tm-m-before/invoice/preview${params}`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<TmMBeforeHearingInvoicePreview | null>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load TM-M before Hearing Invoice preview"
    );
  }
  return (json as ApiSuccessResponse<TmMBeforeHearingInvoicePreview | null>)
    .data;
}

export async function saveTmMBeforeHearingInvoice(
  leadId: string,
  applications: Array<{
    tmNo: string;
    invoiceAmount?: number;
    discount?: number;
    invoiceDate?: string;
    includeInvoiceNo?: boolean;
  }>
): Promise<TmMBeforeHearingInvoiceStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/hearing/tm-m-before/invoice`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ applications }),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<TmMBeforeHearingInvoiceStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save TM-M before Hearing Invoice"
    );
  }
  return (json as ApiSuccessResponse<TmMBeforeHearingInvoiceStatus>).data;
}

export async function generateTmMBeforeHearingInvoicePdf(
  leadId: string,
  tmNos?: string[]
): Promise<{ s3Key: string; downloadUrl: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/hearing/tm-m-before/invoice/generate-pdf`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ s3Key: string; downloadUrl: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to generate TM-M before Hearing Invoice PDF"
    );
  }
  return (json as ApiSuccessResponse<{ s3Key: string; downloadUrl: string }>)
    .data;
}

// --- 3D(viii) TM-M before Hearing Invoice send / preview (WhatsApp + Email) ---

export interface TmMBeforeHearingInvoiceEmailPreview {
  to: string | null;
  subject: string;
  body: string;
  filename: string | null;
  downloadUrl: string | null;
}

export interface TmMBeforeHearingInvoicePaymentMessagePreview {
  pdfMessage: string;
  paymentMessage: string;
  emailPreview: TmMBeforeHearingInvoiceEmailPreview;
}

export async function getTmMBeforeHearingInvoicePaymentMessagePreview(
  leadId: string,
  tmNos?: string[]
): Promise<TmMBeforeHearingInvoicePaymentMessagePreview> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/hearing/tm-m-before/invoice/whatsapp-preview${buildHearingTmNosQuery(tmNos)}`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<TmMBeforeHearingInvoicePaymentMessagePreview>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load TM-M before Hearing Invoice message preview"
    );
  }
  return (
    json as ApiSuccessResponse<TmMBeforeHearingInvoicePaymentMessagePreview>
  ).data;
}

export async function sendTmMBeforeHearingInvoiceToClient(
  leadId: string,
  body: {
    sendWhatsApp?: boolean;
    sendEmail?: boolean;
    tmNos?: string[];
    generatedPdfId?: string;
  } = {}
): Promise<{ waLink: string; emailSent?: boolean }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/hearing/tm-m-before/invoice/send-to-client`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink: string; emailSent?: boolean }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to send TM-M before Hearing Invoice to client"
    );
  }
  return (json as ApiSuccessResponse<{ waLink: string; emailSent?: boolean }>)
    .data;
}

export async function sendTmMBeforeHearingInvoicePaymentMessage(
  leadId: string,
  tmNos?: string[]
): Promise<{ waLink: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/hearing/tm-m-before/invoice/send-payment-message`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to send TM-M before Hearing Invoice payment message"
    );
  }
  return (json as ApiSuccessResponse<{ waLink: string }>).data;
}

// --- 3D(ix) TM-M before Hearing — Collected Amount ---

export async function saveTmMBeforeHearingCollectedAmount(
  leadId: string,
  items: Array<{ tmNo: string; collectedAmount: number }>
): Promise<TmMBeforeHearingInvoiceStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/hearing/tm-m-before/collected-amount`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items }),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<TmMBeforeHearingInvoiceStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save Collected Amount for TM-M before Hearing"
    );
  }
  return (json as ApiSuccessResponse<TmMBeforeHearingInvoiceStatus>).data;
}

// --- 3D(x) File TM-M before Hearing ---

export interface TmMBeforeHearingFiledApplication {
  tmNo: string;
  dateOfTmMBeforeHearingFiled: string | null;
  tmMBeforeHearingFileKey: string | null;
  tmMBeforeHearingFilename: string | null;
  tmMBeforeHearingFileUrl: string | null;
}

export interface TmMBeforeHearingFiledStatus {
  acknowledgementNo: string | null;
  dateOfFiling: string | null;
  applications: TmMBeforeHearingFiledApplication[];
}

export async function getTmMBeforeHearingFiledStatus(
  leadId: string
): Promise<TmMBeforeHearingFiledStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/hearing/tm-m-before/filed`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<TmMBeforeHearingFiledStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load File TM-M before Hearing"
    );
  }
  return (json as ApiSuccessResponse<TmMBeforeHearingFiledStatus>).data;
}

export async function saveTmMBeforeHearingFiled(
  leadId: string,
  applications: Array<{
    tmNo: string;
    dateOfTmMBeforeHearingFiled?: string | null;
    tmMBeforeHearingFileKey?: string | null;
    tmMBeforeHearingFilename?: string | null;
  }>
): Promise<TmMBeforeHearingFiledStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/hearing/tm-m-before/filed`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ applications }),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<TmMBeforeHearingFiledStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save File TM-M before Hearing"
    );
  }
  return (json as ApiSuccessResponse<TmMBeforeHearingFiledStatus>).data;
}

// --- 3D(xi) Inform Client about Filing TM-M before Hearing ---

export async function getTmMBeforeHearingInformClientPreview(
  leadId: string,
  tmNos?: string[]
): Promise<{ message: string; applicationCount: number; tmNos: string[] }> {
  const params = tmNos?.length
    ? `?${tmNos.map((t) => `tmNos=${encodeURIComponent(t)}`).join("&")}`
    : "";
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/hearing/tm-m-before/inform-client-preview${params}`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{
        message: string;
        applicationCount: number;
        tmNos: string[];
      }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Inform Client (TM-M filing) preview"
    );
  }
  return (json as ApiSuccessResponse<{
    message: string;
    applicationCount: number;
    tmNos: string[];
  }>).data;
}

export async function sendTmMBeforeHearingInformClient(
  leadId: string,
  tmNos?: string[]
): Promise<{ waLink: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/hearing/tm-m-before/inform-client/send-whatsapp`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to send Inform Client (TM-M filing)"
    );
  }
  return (json as ApiSuccessResponse<{ waLink: string }>).data;
}

// --- 3D(xiii) Stamp Paper details to Vendor for Hearing ---

export interface StampPaperForHearingApplication {
  tmNo: string;
  stampPapersForHearing: number | null;
  amountPayableToStampVendorForHearing: number | null;
  paidToVendorForHearing?: boolean | null;
  stampVendorNameForHearing: string | null;
  stampVendorPhoneForHearing: string | null;
}

export interface StampPaperForHearingStatus {
  isActive: boolean;
  applications?: StampPaperForHearingApplication[];
  stampPapersForHearing: number | null;
  amountPayableToStampVendorForHearing: number | null;
  paidToVendorForHearing: boolean | null;
  paymentStatusDisplay: string;
  applicant: string | null;
  applicantAddress: string | null;
  applicantType: string | null;
  userDate: string | null;
  signatoryName: string | null;
  signatoryDesignation: string | null;
  stampVendors: Array<{ name: string; phone: string; isDefault?: boolean }>;
}

export async function getStampPaperForHearingStatus(
  leadId: string,
  tmNo?: string
): Promise<StampPaperForHearingStatus> {
  const query = tmNo ? `?tmNo=${encodeURIComponent(tmNo)}` : "";
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/hearing/stamp-paper-for-vendor${query}`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<StampPaperForHearingStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Stamp Paper for Hearing"
    );
  }
  return (json as ApiSuccessResponse<StampPaperForHearingStatus>).data;
}

export async function saveStampPaperForHearing(
  leadId: string,
  applications: Array<{
    tmNo: string;
    stampPapersForHearing: number;
    stampVendorNameForHearing: string;
    stampVendorPhoneForHearing: string;
  }>
): Promise<StampPaperForHearingStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/hearing/stamp-paper-for-vendor`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ applications }),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<StampPaperForHearingStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save Stamp Paper details"
    );
  }
  return (json as ApiSuccessResponse<StampPaperForHearingStatus>).data;
}

export async function getStampPaperForHearingMessagePreview(
  leadId: string,
  tmNos?: string[]
): Promise<{ message: string }> {
  const params = tmNos?.length
    ? `?${tmNos.map((t) => `tmNos=${encodeURIComponent(t)}`).join("&")}`
    : "";
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/hearing/stamp-paper-for-vendor/message-preview${params}`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ message: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Stamp Paper message preview"
    );
  }
  return (json as ApiSuccessResponse<{ message: string }>).data;
}

export async function sendStampPaperForHearingToVendor(
  leadId: string,
  tmNos?: string[]
): Promise<{ waLink: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/hearing/stamp-paper-for-vendor/send-whatsapp`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to send Stamp Paper details to vendor"
    );
  }
  return (json as ApiSuccessResponse<{ waLink: string }>).data;
}

// --- 3D(xii) Hearing — Collected Amount (Payment from Client) ---

export async function saveHearingCollectedAmount(
  leadId: string,
  applications: Array<{ tmNo: string; collectedAmount: number }>
): Promise<HearingInvoiceStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/hearing/collected-amount`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ applications }),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<HearingInvoiceStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save Collected Amount for Hearing"
    );
  }
  return (json as ApiSuccessResponse<HearingInvoiceStatus>).data;
}

/** 3D(vii) Confirmation from Client for Hearing — YES | NO | POSTPONE. */
export type HearingClientConfirmation = "YES" | "NO" | "POSTPONE";
/**
 * 3D(vii) suggested next step. The API returns `"PROCEED"` or a **workflow step
 * slug** — not shorthand. The earlier `"5B(i)" | "TM-M" | "3D(v)_revised"` members
 * were never returned by the backend and silently failed every comparison
 * (`getSuggestedNextStepForHearingConfirmation` returns `NewApplicationStep` values).
 * Compare against `NewApplicationStep.*`, not against these strings.
 */
export type HearingSuggestedNextStep =
  | "PROCEED"
  /** NO → S3_05B_I_STATUS_DETAILS_REFUSAL_ABANDONMENT */
  /** POSTPONE → S3_03D_VIII_A_SAVE_INVOICE_TM_M_BEFORE_HEARING */
  /** YES + needRevisedInvoice → S3_03D_VII_A_SAVE_REVISED_INVOICE_HEARING_1 */
  | string;

export interface HearingConfirmationApplication {
  tmNo: string;
  clientConfirmation: HearingClientConfirmation | null;
  needRevisedInvoice?: boolean | null;
}

export interface HearingConfirmationStatus {
  clientConfirmation: HearingClientConfirmation | null;
  suggestedNextStep: HearingSuggestedNextStep;
  applications: HearingConfirmationApplication[];
}

export async function getShowCauseHearingConfirmation(
  leadId: string,
  tmNos?: string[]
): Promise<HearingConfirmationStatus> {
  const params = tmNos?.length
    ? `?${tmNos.map((t) => `tmNos=${encodeURIComponent(t)}`).join("&")}`
    : "";
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/show-cause-hearing/confirmation-from-client${params}`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<HearingConfirmationStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Confirmation from Client for Hearing"
    );
  }
  return (json as ApiSuccessResponse<HearingConfirmationStatus>).data;
}

export async function saveShowCauseHearingConfirmation(
  leadId: string,
  applications: Array<{
    tmNo: string;
    clientConfirmation: HearingClientConfirmation;
    needRevisedInvoice?: boolean;
  }>
): Promise<HearingConfirmationStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/show-cause-hearing/confirmation-from-client`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ applications }),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<HearingConfirmationStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save Confirmation from Client for Hearing"
    );
  }
  return (json as ApiSuccessResponse<HearingConfirmationStatus>).data;
}

/** 3D(vi) Call Client for Hearing — record completed call (no body content). */
export async function recordShowCauseHearingCallClient(
  leadId: string,
  tmNos?: string[]
): Promise<void> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/show-cause-hearing/record-call-client`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<unknown>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to record Call Client step"
    );
  }
}

/** 3D(i) Inform Client (Showcause) — "Ready for Show Cause Hearing" preview. */
export async function getShowCauseHearingInformClientPreview(
  leadId: string,
  tmNos?: string[]
): Promise<{ message: string; applicationCount: number; tmNos: string[] }> {
  const params = tmNos?.length
    ? `?${tmNos.map((t) => `tmNos=${encodeURIComponent(t)}`).join("&")}`
    : "";
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/show-cause-hearing/preview${params}`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{
        message: string;
        applicationCount: number;
        tmNos: string[];
      }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Inform Client (Showcause) preview"
    );
  }
  return (json as ApiSuccessResponse<{
    message: string;
    applicationCount: number;
    tmNos: string[];
  }>).data;
}

/** 3D(ii) Status details of Hearing — PATCH per-TM Date of Hearing + Hearing Notice PDF key. */
export interface SetShowCauseHearingStatusPayload {
  applications: Array<{
    tmNo: string;
    dateOfHearing?: string | null;
    hearingNoticeFileKey?: string | null;
    hearingNoticeFilename?: string | null;
  }>;
}

export async function setShowCauseHearingStatusDetails(
  leadId: string,
  payload: SetShowCauseHearingStatusPayload
): Promise<ShowCauseHearingStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/show-cause-hearing/status-details`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<ShowCauseHearingStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save Hearing status details"
    );
  }
  return (json as ApiSuccessResponse<ShowCauseHearingStatus>).data;
}

/** 3D(iv) Inform Client about Hearing — preview text message (depends on Date of Hearing being set in 3D(ii)). */
export async function getShowCauseHearingInformClientAboutHearingPreview(
  leadId: string,
  tmNos?: string[]
): Promise<{ message: string; applicationCount: number; tmNos: string[] }> {
  const params = tmNos?.length
    ? `?${tmNos.map((t) => `tmNos=${encodeURIComponent(t)}`).join("&")}`
    : "";
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/show-cause-hearing/inform-client-preview${params}`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{
        message: string;
        applicationCount: number;
        tmNos: string[];
      }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Inform Client about Hearing preview"
    );
  }
  return (json as ApiSuccessResponse<{
    message: string;
    applicationCount: number;
    tmNos: string[];
  }>).data;
}

/** 3D(iv) Inform Client about Hearing — send hearing-date message to client WhatsApp. */
export async function sendShowCauseHearingInformClientAboutHearing(
  leadId: string,
  tmNos?: string[]
): Promise<{ waLink: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/show-cause-hearing/send-inform-client`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to send Inform Client about Hearing"
    );
  }
  return (json as ApiSuccessResponse<{ waLink: string }>).data;
}

/** 3D(iii) Notice to Client for Hearing — send merged Hearing Notice PDF(s) to client WhatsApp. */
export async function sendShowCauseHearingNoticeToClient(
  leadId: string,
  tmNos?: string[]
): Promise<{ waLink: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/show-cause-hearing/send-notice-to-client`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to send Hearing Notice to client WhatsApp"
    );
  }
  return (json as ApiSuccessResponse<{ waLink: string }>).data;
}

/** 3D(i) Inform Client (Showcause) — send "Ready for Show Cause Hearing" WhatsApp. */
export async function sendShowCauseHearingInformClient(
  leadId: string,
  tmNos?: string[]
): Promise<{ waLink: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/show-cause-hearing/send`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to send Inform Client (Showcause) to WhatsApp"
    );
  }
  return (json as ApiSuccessResponse<{ waLink: string }>).data;
}

// --- Examination Report — Accepted: status & PDF upload (Step 3A(i–ii)) ---

export interface ExaminationReportAcceptedApplication {
  tmNo: string;
  mark: string;
  classNumber: number;
  typeOfMark: string;
  applicationDate: string;
  status: string;
  examinationReportAcceptanceFileKey: string | null;
  examinationReportAcceptanceFilename: string | null;
  examinationReportAcceptanceFileUrl: string | null;
}

export interface ExaminationReportAcceptedStatus {
  acknowledgementNo: string | null;
  dateOfFiling: string | null;
  applications: ExaminationReportAcceptedApplication[];
}

export interface SetExaminationReportAcceptedPayload {
  applications: Array<{
    tmNo: string;
    examinationReportAcceptanceFileKey?: string;
    examinationReportAcceptanceFilename?: string;
  }>;
}

export async function getExaminationReportAcceptedStatus(
  leadId: string
): Promise<ExaminationReportAcceptedStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/examination-report-accepted`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<ExaminationReportAcceptedStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Examination Report Accepted status"
    );
  }
  return (json as ApiSuccessResponse<ExaminationReportAcceptedStatus>).data;
}

export async function saveExaminationReportAccepted(
  leadId: string,
  payload: SetExaminationReportAcceptedPayload
): Promise<ExaminationReportAcceptedStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/examination-report-accepted`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<ExaminationReportAcceptedStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save Examination Report Accepted"
    );
  }
  return (json as ApiSuccessResponse<ExaminationReportAcceptedStatus>).data;
}

export async function sendExaminationReportAcceptancePdfToWhatsApp(
  leadId: string,
  tmNos?: string[]
): Promise<{ waLink: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/examination-report-accepted/send-pdf-to-whatsapp`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to send Examination Report Acceptance PDF to WhatsApp"
    );
  }
  return (json as ApiSuccessResponse<{ waLink: string }>).data;
}

// --- Examination Report — Objected: Status of Objection (Step 3B(i)) ---

export interface ReasonForObjection {
  similarity?: boolean;
  nonDistinctive?: boolean;
  supportingDocuments?: boolean;
  others?: boolean;
  othersSpecify?: string;
  tmMYes?: boolean;
}

export interface SimilarMark {
  mark?: string | null;
  tmNo?: string | null;
  class?: string | null;
  goodsServices?: string | null;
  userDate?: string | null;
  place?: string | null;
}

export interface StatusOfObjectionApplication {
  tmNo: string;
  mark: string;
  classNumber: number;
  typeOfMark: string;
  applicationDate: string;
  status: string;
  dateOfExaminationReport: string | null;
  reasonForObjection: ReasonForObjection;
  similarMarks: SimilarMark[];
  lastDateForReplyToObjection: string | null;
  statementOfReasonForObjection: string[];
  examinationReportFileKey: string | null;
  examinationReportFilename: string | null;
  examinationReportFileUrl: string | null;
}

export interface StatusOfObjectionStatus {
  acknowledgementNo: string | null;
  dateOfFiling: string | null;
  applications: StatusOfObjectionApplication[];
  numberOfApplicationsForReplyToObjection: number;
}

export interface SetStatusOfObjectionPayload {
  applications: Array<{
    tmNo: string;
    dateOfExaminationReport?: string | null;
    reasonForObjection?: ReasonForObjection;
    similarMarks?: SimilarMark[];
    examinationReportFileKey?: string | null;
    examinationReportFilename?: string | null;
  }>;
}

export async function getStatusOfObjection(
  leadId: string
): Promise<StatusOfObjectionStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/status-of-objection`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<StatusOfObjectionStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Status of Objection"
    );
  }
  return (json as ApiSuccessResponse<StatusOfObjectionStatus>).data;
}

export async function saveStatusOfObjection(
  leadId: string,
  payload: SetStatusOfObjectionPayload
): Promise<StatusOfObjectionStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/status-of-objection`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<StatusOfObjectionStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save Status of Objection"
    );
  }
  return (json as ApiSuccessResponse<StatusOfObjectionStatus>).data;
}

export async function sendStatusOfObjectionPdfToWhatsApp(
  leadId: string,
  tmNos?: string[]
): Promise<{ waLink: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/status-of-objection/send-pdf-to-whatsapp`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to send Examination Report (Objection) to WhatsApp"
    );
  }
  return (json as ApiSuccessResponse<{ waLink: string }>).data;
}

export async function getStatusOfObjectionInformClientPreview(
  leadId: string,
  tmNos?: string[]
): Promise<{
  messages: string[];
  messageCount: number;
  applicationCount: number;
  tmNos: string[];
}> {
  const params = tmNos?.length
    ? `?${tmNos.map((t) => `tmNos=${encodeURIComponent(t)}`).join("&")}`
    : "";
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/status-of-objection/inform-client/preview${params}`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{
        messages: string[];
        messageCount: number;
        applicationCount: number;
        tmNos: string[];
      }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Inform Client about Objection preview"
    );
  }
  return (json as ApiSuccessResponse<{
    messages: string[];
    messageCount: number;
    applicationCount: number;
    tmNos: string[];
  }>).data;
}

export interface StatusOfObjectionConfirmationStatus {
  clientGoingAhead: boolean | null;
  applications?: Array<{
    tmNo: string;
    clientGoingAhead: boolean | null;
  }>;
  suggestedNextStep?: string;
}

export async function getStatusOfObjectionConfirmationFromClient(
  leadId: string,
  tmNos?: string[]
): Promise<StatusOfObjectionConfirmationStatus> {
  const params = tmNos?.length
    ? `?${tmNos.map((t) => `tmNos=${encodeURIComponent(t)}`).join("&")}`
    : "";
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/status-of-objection/confirmation-from-client${params}`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<StatusOfObjectionConfirmationStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Confirmation from Client"
    );
  }
  return (json as ApiSuccessResponse<StatusOfObjectionConfirmationStatus>).data;
}

export async function saveStatusOfObjectionConfirmationFromClient(
  leadId: string,
  payload: {
    applications: Array<{ tmNo: string; clientGoingAhead: boolean }>;
  }
): Promise<void> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/status-of-objection/confirmation-from-client`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  if (!res.ok) {
    const json = (await res.json().catch(() => ({}))) as ApiErrorResponse;
    throw new Error(
      json?.message ||
        json?.error ||
        "Failed to save Confirmation from Client"
    );
  }
}

export interface ReplyToObjectionInvoicePreview {
  dataLabel: string;
  addressee: string;
  addresseeLine1: string | null;
  addresseeLine2: string | null;
  addresseeLine3: string | null;
  formOfAddress: string | null;
  applications: Array<{
    tmNo: string;
    applicationDate: string;
    mark: string;
    typeOfMark: string;
    classNumber: number;
    statementOfReasonForObjection: string;
    computedInvoiceTotal: number;
    invoiceAmount: number | null;
    individualInvoiceNo?: string | null;
  }>;
  lastDateForReplyToObjection: string | null;
  professionalChargesReply: number | null;
  professionalChargesTmM: number | null;
  professionalChargesReplyPerApp: number;
  professionalChargesTmMPerApp: number;
  numberOfApplications: number;
  numberOfApplicationsWithTmM: number;
  totalProfessionalCharges: number | null;
  computedInvoiceTotal: number;
  invoiceAmount: number | null;
  invoiceDate: string | null;
  invoiceNo: string | null;
  includeInvoiceNo: boolean;
}

export interface ReplyToObjectionInvoiceTmRow {
  tmNo: string;
  computedInvoiceTotal: number;
  invoiceAmount: number | null;
  individualInvoiceNo: string | null;
  includeInvoiceNo: boolean;
  discount: number | null;
  invoiceDate: string | null;
  collectedAmount: number | null;
  outstandingAmount: number | null;
  replyToObjectionInvoicePdfKey: string | null;
  replyToObjectionInvoicePdfUrl: string | null;
}

export interface ReplyToObjectionInvoiceStatus {
  computedInvoiceTotal: number | null;
  invoiceAmount: number | null;
  professionalChargesReply: number | null;
  professionalChargesTmM: number | null;
  professionalChargesReplyPerApp: number | null;
  professionalChargesTmMPerApp: number | null;
  numberOfApplications: number | null;
  numberOfApplicationsWithTmM: number | null;
  invoiceNo: string | null;
  invoiceDate: string | null;
  includeInvoiceNo: boolean;
  replyToObjectionInvoicePdfKey: string | null;
  replyToObjectionInvoicePdfUrl: string | null;
  collectedAmount: number | null;
  outstandingAmount: number | null;
  fullInvoiceAmount?: number | null;
  message?: string | null;
  tmInvoiceRows: ReplyToObjectionInvoiceTmRow[];
}

export async function getReplyToObjectionInvoiceStatus(
  leadId: string,
  tmNo?: string
): Promise<ReplyToObjectionInvoiceStatus> {
  const params = tmNo ? `?tmNo=${encodeURIComponent(tmNo)}` : "";
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/reply-to-objection/invoice${params}`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<ReplyToObjectionInvoiceStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Reply to Objection Invoice"
    );
  }
  return (json as ApiSuccessResponse<ReplyToObjectionInvoiceStatus>).data;
}

export async function saveReplyToObjectionCollectedAmount(
  leadId: string,
  payload: {
    applications?: Array<{ tmNo: string; collectedAmount: number | null }>;
    collectedAmount?: number | null;
    tmNo?: string;
  }
): Promise<ReplyToObjectionInvoiceStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/reply-to-objection/collected-amount`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<ReplyToObjectionInvoiceStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save Collected Amount"
    );
  }
  return (json as ApiSuccessResponse<ReplyToObjectionInvoiceStatus>).data;
}

export async function saveReplyToObjectionInvoice(
  leadId: string,
  payload: {
    applications?: Array<{
      tmNo: string;
      invoiceAmount?: number;
      invoiceDate?: string;
      includeInvoiceNo?: boolean;
    }>;
  } = {}
): Promise<ReplyToObjectionInvoiceStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/reply-to-objection/invoice`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<ReplyToObjectionInvoiceStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save Reply to Objection Invoice"
    );
  }
  return (json as ApiSuccessResponse<ReplyToObjectionInvoiceStatus>).data;
}

export async function generateReplyToObjectionInvoicePdf(
  leadId: string,
  tmNos?: string[]
): Promise<{ s3Key: string; downloadUrl: string | null }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/reply-to-objection/invoice/generate-pdf`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ s3Key: string; downloadUrl: string | null }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to generate Reply to Objection Invoice PDF"
    );
  }
  return (json as ApiSuccessResponse<{
    s3Key: string;
    downloadUrl: string | null;
  }>).data;
}

export async function getReplyToObjectionInvoicePreview(
  leadId: string,
  tmNos?: string[]
): Promise<ReplyToObjectionInvoicePreview | null> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/reply-to-objection/invoice/preview`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<ReplyToObjectionInvoicePreview | null>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Reply to Objection Invoice preview"
    );
  }
  return (json as ApiSuccessResponse<ReplyToObjectionInvoicePreview | null>).data;
}

export async function sendReplyToObjectionInvoiceToClient(
  leadId: string,
  payload: { tmNos?: string[]; sendWhatsApp?: boolean } = {}
): Promise<{ waLink: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/reply-to-objection/invoice/send-to-client`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to send Reply to Objection Invoice to client"
    );
  }
  return (json as ApiSuccessResponse<{ waLink: string }>).data;
}

export async function sendReplyToObjectionInvoicePaymentMessage(
  leadId: string,
  payload: { tmNos?: string[] } = {}
): Promise<{ waLink: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/reply-to-objection/invoice/send-payment-message`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to send Reply to Objection Invoice payment message"
    );
  }
  return (json as ApiSuccessResponse<{ waLink: string }>).data;
}

export interface ReplyToObjectionInvoicePaymentMessagePreview {
  message: string;
  invoiceAmount: number;
  tmNos?: string[];
}

export async function getReplyToObjectionInvoicePaymentMessagePreview(
  leadId: string,
  payload: { tmNos?: string[] } = {}
): Promise<ReplyToObjectionInvoicePaymentMessagePreview> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/reply-to-objection/invoice/payment-message/preview`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<ReplyToObjectionInvoicePaymentMessagePreview>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load payment message preview"
    );
  }
  return (json as ApiSuccessResponse<ReplyToObjectionInvoicePaymentMessagePreview>)
    .data;
}

export interface ReplyToObjectionFiledApplication {
  tmNo: string;
  dateOfReplyToObjectionFiled: string | null;
  replyToObjectionFileKey: string | null;
  replyToObjectionFilename: string | null;
  replyToObjectionFileUrl: string | null;
}

export interface ReplyToObjectionFiledStatus {
  acknowledgementNo: string | null;
  dateOfFiling: string | null;
  applications: ReplyToObjectionFiledApplication[];
}

export async function getReplyToObjectionFiledStatus(
  leadId: string,
  tmNos?: string[]
): Promise<ReplyToObjectionFiledStatus> {
  const params = tmNos?.length
    ? `?${tmNos.map((t) => `tmNos=${encodeURIComponent(t)}`).join("&")}`
    : "";
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/reply-to-objection/reply-filed${params}`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<ReplyToObjectionFiledStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Reply to Objection filed status"
    );
  }
  return (json as ApiSuccessResponse<ReplyToObjectionFiledStatus>).data;
}

export async function saveReplyToObjectionFiled(
  leadId: string,
  payload: {
    applications: Array<{
      tmNo: string;
      dateOfReplyToObjectionFiled?: string | null;
      replyToObjectionFileKey?: string | null;
      replyToObjectionFilename?: string | null;
    }>;
  }
): Promise<ReplyToObjectionFiledStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/reply-to-objection/reply-filed`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<ReplyToObjectionFiledStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save Reply to Objection filed details"
    );
  }
  return (json as ApiSuccessResponse<ReplyToObjectionFiledStatus>).data;
}

export async function getReplyToObjectionFiledInformClientPreview(
  leadId: string,
  tmNos?: string[]
): Promise<{ message: string; applicationCount: number; tmNos: string[] }> {
  const params = tmNos?.length
    ? `?${tmNos.map((t) => `tmNos=${encodeURIComponent(t)}`).join("&")}`
    : "";
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/reply-to-objection/inform-client/preview${params}`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{
        message: string;
        applicationCount: number;
        tmNos: string[];
      }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Inform Client preview"
    );
  }
  return (json as ApiSuccessResponse<{
    message: string;
    applicationCount: number;
    tmNos: string[];
  }>).data;
}

export async function sendReplyToObjectionFiledInformClientWhatsApp(
  leadId: string,
  tmNos?: string[]
): Promise<{ waLink: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/reply-to-objection/inform-client/send-whatsapp`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to send Inform Client message to WhatsApp"
    );
  }
  return (json as ApiSuccessResponse<{ waLink: string }>).data;
}

export async function recordStatusOfObjectionDocumentsFromClient(
  leadId: string,
  tmNos?: string[]
): Promise<void> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/status-of-objection/record-documents-from-client`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  if (!res.ok) {
    const json = (await res.json().catch(() => ({}))) as ApiErrorResponse;
    throw new Error(
      json?.message ||
        json?.error ||
        "Failed to record Documents from Client for Reply to Objection"
    );
  }
}

export async function recordStatusOfObjectionCallClientReply(
  leadId: string,
  tmNos?: string[]
): Promise<void> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/status-of-objection/record-call-client-reply`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  if (!res.ok) {
    const json = (await res.json().catch(() => ({}))) as ApiErrorResponse;
    throw new Error(
      json?.message ||
        json?.error ||
        "Failed to record Call Client about Reply to Objection"
    );
  }
}

export async function sendStatusOfObjectionInformClientToWhatsApp(
  leadId: string,
  tmNos?: string[]
): Promise<{ waLinks: string[]; messageCount: number }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/status-of-objection/inform-client/send`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLinks: string[]; messageCount: number }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to send Inform Client about Objection to WhatsApp"
    );
  }
  return (json as ApiSuccessResponse<{
    waLinks: string[];
    messageCount: number;
  }>).data;
}

// --- Generic upload presigned URLs (shared by multiple stage uploads) ---

export interface UploadPresignedUrl {
  key: string;
  bucket: string;
  url: string;
}

export async function generateUploadPresignedUrls(
  files: Array<{ filename: string; mimeType: string }>,
  type: "private" | "public",
  folder: string
): Promise<UploadPresignedUrl[]> {
  const res = await apiFetch(`${API_BASE}/upload/presigned-urls`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ files, type, folder }),
  });
  const json = (await res.json()) as
    | ApiSuccessResponse<UploadPresignedUrl[]>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to generate upload URLs"
    );
  }
  return (json as ApiSuccessResponse<UploadPresignedUrl[]>).data;
}

// ---------------------------------------------------------------------------
// 3D(xiv) Stamp Paper Payment to Vendor for Hearing
// ---------------------------------------------------------------------------

export async function saveStampPaperPaymentToVendorHearing(
  leadId: string,
  items: Array<{
    tmNo: string;
    amountPayableToStampVendorForHearing?: number | null;
    paidToVendorForHearing: boolean;
  }>
): Promise<StampPaperForHearingStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/hearing/stamp-paper-payment-to-vendor`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(items),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<StampPaperForHearingStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save Stamp Paper payment to vendor"
    );
  }
  return (json as ApiSuccessResponse<StampPaperForHearingStatus>).data;
}

// ---------------------------------------------------------------------------
// 3D(xv) Documents from Client for Hearing — record-only
// ---------------------------------------------------------------------------

export async function recordDocumentsFromClientHearing(
  leadId: string,
  tmNos: string[]
): Promise<void> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/show-cause-hearing/record-documents-from-client`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ tmNos }),
    }
  );
  if (!res.ok) {
    const json = (await res.json().catch(() => ({}))) as ApiErrorResponse;
    throw new Error(
      json?.message ||
        json?.error ||
        "Failed to record Documents from Client for Hearing"
    );
  }
}

// ---------------------------------------------------------------------------
// 3D(xvi) User Affidavit for Hearing
// ---------------------------------------------------------------------------

export interface UserAffidavitForHearingTurnover {
  financialYear: string;
  amount: number;
}

export interface UserAffidavitForHearingPdfMeta {
  key: string;
  url: string | null;
  filename: string | null;
  generatedAt?: string | null;
}

export interface UserAffidavitForHearingItem {
  tmNo: string;
  classNumber: number | null;
  typeOfMark: string | null;
  mark: string | null;
  applicant: string | null;
  applicantAddress: string | null;
  applicantType: string | null;
  signatoryName: string | null;
  signatoryDesignation: string | null;
  signatoryGuardianName: string | null;
  signatoryRelationWithGuardian: string | null;
  descriptionOfApplicant: string | null;
  goodsOrServices: string | null;
  selectedClass: string | null;
  noOfApplications: number | null;
  gsTypeA: string | null;
  gsTypeB: string | null;
  gsTypeC: string | null;
  userDate: string | null;
  dateOfUserAffidavitForHearing: string | null;
  userDateForHearing: string | null;
  turnoverDetails: UserAffidavitForHearingTurnover[] | null;
  isSkipped: boolean;
  pdf: UserAffidavitForHearingPdfMeta | null;
}

export interface UserAffidavitForHearingStatus {
  isActive: boolean;
  userAffidavits: UserAffidavitForHearingItem[];
}

export async function getUserAffidavitForHearingStatus(
  leadId: string,
  tmNos?: string[]
): Promise<UserAffidavitForHearingStatus> {
  const params = tmNos?.length
    ? `?${tmNos.map((t) => `tmNos=${encodeURIComponent(t)}`).join("&")}`
    : "";
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/hearing/user-affidavit${params}`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<UserAffidavitForHearingStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load User Affidavit for Hearing"
    );
  }
  return (json as ApiSuccessResponse<UserAffidavitForHearingStatus>).data;
}

export async function saveUserAffidavitForHearing(
  leadId: string,
  applications: Array<{
    tmNo: string;
    dateOfUserAffidavitForHearing?: string | null;
    userDateForHearing?: string | null;
    turnoverDetails?: UserAffidavitForHearingTurnover[] | null;
    isSkipped?: boolean;
  }>
): Promise<UserAffidavitForHearingStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/hearing/user-affidavit`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ applications }),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<UserAffidavitForHearingStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save User Affidavit for Hearing"
    );
  }
  return (json as ApiSuccessResponse<UserAffidavitForHearingStatus>).data;
}

export async function generateUserAffidavitForHearingPdf(
  leadId: string,
  tmNo: string
): Promise<UserAffidavitForHearingPdfMeta & { tmNo: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/hearing/user-affidavit/generate-pdf`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ tmNo }),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<UserAffidavitForHearingPdfMeta & { tmNo: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to generate User Affidavit for Hearing PDF"
    );
  }
  return (json as ApiSuccessResponse<UserAffidavitForHearingPdfMeta & { tmNo: string }>)
    .data;
}

// ---------------------------------------------------------------------------
// 3D(xvii) Signature from Client for Hearing
// ---------------------------------------------------------------------------

export interface SignatureFromClientHearingStatus {
  isActive: boolean;
}

export async function getSignatureFromClientHearingStatus(
  leadId: string
): Promise<SignatureFromClientHearingStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/show-cause-hearing/signature-from-client`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<SignatureFromClientHearingStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Signature from Client for Hearing status"
    );
  }
  return (json as ApiSuccessResponse<SignatureFromClientHearingStatus>).data;
}

export async function recordSignatureFromClientHearing(
  leadId: string,
  tmNos: string[]
): Promise<void> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/show-cause-hearing/record-signature-from-client`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ tmNos }),
    }
  );
  if (!res.ok) {
    const json = (await res.json().catch(() => ({}))) as ApiErrorResponse;
    throw new Error(
      json?.message ||
        json?.error ||
        "Failed to record Signature from Client for Hearing"
    );
  }
}

// ---------------------------------------------------------------------------
// 3D(xviii) Notarisation of Documents for Hearing
// ---------------------------------------------------------------------------

export interface NotarisationForHearingApplication {
  tmNo: string;
  amountPayableToNotaryForHearing: number | null;
  paidToNotaryForHearing: boolean | null;
  paymentStatusDisplay?: string;
  userDate?: string | null;
}

export interface NotarisationForHearingStatus {
  isActive: boolean;
  stampPapersForHearing: number | null;
  applications: NotarisationForHearingApplication[];
}

export async function getNotarisationForHearingStatus(
  leadId: string
): Promise<NotarisationForHearingStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/hearing/notarisation`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<NotarisationForHearingStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Notarisation for Hearing"
    );
  }
  return (json as ApiSuccessResponse<NotarisationForHearingStatus>).data;
}

export async function saveNotaryPaymentForHearing(
  leadId: string,
  applications: Array<{
    tmNo: string;
    amountPayableToNotaryForHearing?: number | null;
    paidToNotaryForHearing: boolean;
  }>
): Promise<NotarisationForHearingStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/hearing/notarisation`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ applications }),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<NotarisationForHearingStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save Notary payment for Hearing"
    );
  }
  return (json as ApiSuccessResponse<NotarisationForHearingStatus>).data;
}

// ---------------------------------------------------------------------------
// 3D(xix) Scanning of Documents for Hearing — record-only
// ---------------------------------------------------------------------------

export async function recordScanningOfDocumentsForHearing(
  leadId: string,
  tmNos: string[]
): Promise<void> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/show-cause-hearing/record-scanning-of-documents`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ tmNos }),
    }
  );
  if (!res.ok) {
    const json = (await res.json().catch(() => ({}))) as ApiErrorResponse;
    throw new Error(
      json?.message ||
        json?.error ||
        "Failed to record Scanning of Documents for Hearing"
    );
  }
}

// ---------------------------------------------------------------------------
// 3D(xx) File Hearing Document — record-only
// ---------------------------------------------------------------------------

export async function recordFilingHearingDocument(
  leadId: string,
  tmNos?: string[]
): Promise<void> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/show-cause-hearing/record-filing-hearing-document`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  if (!res.ok) {
    const json = (await res.json().catch(() => ({}))) as ApiErrorResponse;
    throw new Error(
      json?.message || json?.error || "Failed to record File Hearing Document"
    );
  }
}

// ---------------------------------------------------------------------------
// 3D(xxi) Note on Hearing
// ---------------------------------------------------------------------------

export interface NoteOnHearingObjectionReasons {
  similarity: boolean;
  nonDistinctive: boolean;
  supportingDocuments: boolean;
  others: boolean;
  othersSpecify: string | null;
  tmMYes: boolean;
}

export interface NoteOnHearingSimilarMark {
  id: string;
  sortOrder: number;
  mark: string | null;
  tmNo: string | null;
  markClass: string | null;
  goodsServices: string | null;
  userDate: string | null;
  place: string | null;
}

export interface NoteOnHearingSimilarMarkNote {
  id?: string;
  similarMarkId: string | null;
  sortOrder: number;
  mark: string | null;
  tmNo: string | null;
  imageKey: string | null;
  imageUrl: string | null;
  status: string | null;
  expired: boolean | null;
  goodsServicesDifferent: boolean | null;
  userDateEarlier: boolean | null;
  state: string | null;
  note1: string | null;
  note2: string | null;
}

export interface NoteOnHearingApplicationData {
  index: number;
  ackApplicationId: string;
  tmNo: string;
  mark: string;
  typeOfMark: string;
  classNumber: number;
  goodsServices: string | null;
  imageOfMarkKey: string | null;
  imageOfMarkUrl: string | null;
  userDate: string | null;
  userDateForHearing: string | null;
  dateOfHearing: string | null;
  dateOfFiling: string | null;
  objectionReasons: NoteOnHearingObjectionReasons;
  similarMarks: NoteOnHearingSimilarMark[];
  similarMarkNotes: NoteOnHearingSimilarMarkNote[];
  noteNonDistinctive1: string | null;
  noteNonDistinctive2: string | null;
  supportingDocsUploaded: boolean | null;
  noteOtherObjections1: string | null;
  noteOtherObjections2: string | null;
  notePdfKey: string | null;
  notePdfUrl: string | null;
}

export interface NoteOnHearingStatus {
  acknowledgementNo: string;
  applicant: string | null;
  totalApplications: number;
  applications: NoteOnHearingApplicationData[];
}

export async function getNoteOnHearingStatus(
  leadId: string
): Promise<NoteOnHearingStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/note-on-hearing/status`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<NoteOnHearingStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Note on Hearing"
    );
  }
  return (json as ApiSuccessResponse<NoteOnHearingStatus>).data;
}

export async function saveNoteOnHearing(
  leadId: string,
  payload: {
    ackApplicationId: string;
    noteNonDistinctive1?: string | null;
    noteNonDistinctive2?: string | null;
    supportingDocsUploaded?: boolean | null;
    noteOtherObjections1?: string | null;
    noteOtherObjections2?: string | null;
    similarMarkNotes?: Array<{
      mark: string;
      tmNo: string;
      imageKey?: string | null;
      status?: string | null;
      expired?: boolean | null;
      goodsServicesDifferent?: boolean | null;
      userDateEarlier?: boolean | null;
      state?: string | null;
      note1?: string | null;
      note2?: string | null;
    }>;
  }
): Promise<NoteOnHearingApplicationData> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/note-on-hearing`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<NoteOnHearingStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save Note on Hearing"
    );
  }
  const status = (json as ApiSuccessResponse<NoteOnHearingStatus>).data;
  const updated = status.applications.find(
    (a) => a.ackApplicationId === payload.ackApplicationId
  );
  if (!updated) {
    throw new Error("Saved Note on Hearing application not found in response");
  }
  return updated;
}

export async function generateNoteOnHearingPdf(
  leadId: string,
  ackApplicationId: string
): Promise<{ pdfKey: string; pdfUrl: string | null }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/note-on-hearing/generate-pdf`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ackApplicationId }),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ pdfKey: string; pdfUrl: string | null }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to generate Note on Hearing PDF"
    );
  }
  return (json as ApiSuccessResponse<{ pdfKey: string; pdfUrl: string | null }>)
    .data;
}

export async function uploadNoteOnHearingSimilarMarkImage(
  leadId: string,
  payload: {
    file: File;
    ackApplicationId: string;
    mark: string;
    tmNo: string;
  }
): Promise<{ imageKey: string; imageUrl: string | null }> {
  const fd = new FormData();
  fd.append("file", payload.file);
  fd.append("ackApplicationId", payload.ackApplicationId);
  fd.append("mark", payload.mark);
  fd.append("tmNo", payload.tmNo);
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/note-on-hearing/upload-similar-mark-image`,
    {
      method: "POST",
      body: fd,
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ imageKey: string; imageUrl: string | null }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to upload similar mark image"
    );
  }
  return (json as ApiSuccessResponse<{ imageKey: string; imageUrl: string | null }>)
    .data;
}

// ---------------------------------------------------------------------------
// 3D(xxii) Attend Hearing
// ---------------------------------------------------------------------------

export type HearingStatusEnum = "ATTENDED" | "NOT_ATTENDED" | "DIDNT_HAPPEN";
export type HearingOfficerRemarkEnum =
  | "ACCEPTED"
  | "REFUSED"
  | "WILL_PASS_ON_MERITS"
  | "FILE_TM_M"
  | "DIRECTION_OF_OFFICER";

export interface AttendHearingOfficerDirection {
  index: number;
  direction: string;
}

export interface AttendHearingApplicationData {
  index: number;
  ackApplicationId: string;
  tmNo: string;
  mark: string;
  typeOfMark: string;
  classNumber: number;
  dateOfHearing: string | null;
  hearingStatus: HearingStatusEnum | null;
  hearingOfficerRemark: HearingOfficerRemarkEnum | null;
  officerDirections: AttendHearingOfficerDirection[];
  remarks: string | null;
}

export interface AttendHearingStatus {
  acknowledgementNo: string;
  applicant: string | null;
  totalApplications: number;
  applications: AttendHearingApplicationData[];
}

export async function getAttendHearingStatus(
  leadId: string
): Promise<AttendHearingStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/attend-hearing/status`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<AttendHearingStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Attend Hearing"
    );
  }
  return (json as ApiSuccessResponse<AttendHearingStatus>).data;
}

export async function saveAttendHearing(
  leadId: string,
  payload: {
    ackApplicationId: string;
    hearingStatus: HearingStatusEnum;
    hearingOfficerRemark?: HearingOfficerRemarkEnum | null;
    officerDirections?: AttendHearingOfficerDirection[];
    remarks?: string | null;
  }
): Promise<AttendHearingApplicationData> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/attend-hearing`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<AttendHearingApplicationData>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save Attend Hearing"
    );
  }
  return (json as ApiSuccessResponse<AttendHearingApplicationData>).data;
}

// ---------------------------------------------------------------------------
// 3D(xxiii) Notice to Client for hearing not happened
// ---------------------------------------------------------------------------

export interface HearingDidntHappenApplication {
  tmNo: string;
  mark: string;
  typeOfMark: string;
  classNumber: number;
  dateOfHearing: string | null;
  postponementNoticeFileKey: string | null;
  postponementNoticeFilename: string | null;
  postponementNoticeFileUrl: string | null;
}

export interface HearingDidntHappenStatus {
  acknowledgementNo: string;
  applicant: string | null;
  applications: HearingDidntHappenApplication[];
}

export async function getHearingDidntHappenStatus(
  leadId: string
): Promise<HearingDidntHappenStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/hearing-didnt-happen/status`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<HearingDidntHappenStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Notice to Client (hearing not happened)"
    );
  }
  return (json as ApiSuccessResponse<HearingDidntHappenStatus>).data;
}

export async function saveHearingDidntHappenPostponementNotice(
  leadId: string,
  applications: Array<{
    tmNo: string;
    postponementNoticeFileKey?: string | null;
    postponementNoticeFilename?: string | null;
  }>
): Promise<void> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/hearing-didnt-happen/postponement-notice`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ applications }),
    }
  );
  if (!res.ok) {
    const json = (await res.json().catch(() => ({}))) as ApiErrorResponse;
    throw new Error(
      json?.message || json?.error || "Failed to save Postponement Notice"
    );
  }
}

export async function sendHearingDidntHappenToClient(
  leadId: string,
  tmNos?: string[]
): Promise<{ waLink: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/hearing-didnt-happen/send-notice-to-client`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to send Notice to Client"
    );
  }
  return (json as ApiSuccessResponse<{ waLink: string }>).data;
}

// ---------------------------------------------------------------------------
// 3D(xxiv) Inform Client after hearing
// ---------------------------------------------------------------------------

export type InformAfterHearingScenario =
  | "DIDNT_HAPPEN"
  | "ACCEPTED_OR_MERITS"
  | "REFUSED"
  | "FILE_TM_M_ONLY"
  | "DIRECTION_ONLY"
  | "FILE_TM_M_AND_DIRECTION"
  | "NOT_APPLICABLE";

export interface InformAfterHearingScenarioGroup {
  scenario: InformAfterHearingScenario;
  applications: Array<{
    tmNo: string;
    mark: string;
    classNumber: number;
    dateOfFiling: string | null;
    hearingStatus: HearingStatusEnum | null;
    hearingOfficerRemark: HearingOfficerRemarkEnum | null;
  }>;
}

export interface InformAfterHearingStatus {
  acknowledgementNo: string;
  applicant: string | null;
  groups: InformAfterHearingScenarioGroup[];
}

export async function getInformAfterHearingStatus(
  leadId: string
): Promise<InformAfterHearingStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/inform-after-hearing/status`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<InformAfterHearingStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Inform Client after hearing"
    );
  }
  return (json as ApiSuccessResponse<InformAfterHearingStatus>).data;
}

export async function getInformAfterHearingPreview(
  leadId: string,
  scenario: InformAfterHearingScenario
): Promise<{ message: string; applicationCount: number }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/inform-after-hearing/preview?scenario=${encodeURIComponent(scenario)}`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ message: string; applicationCount: number }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Inform Client after hearing preview"
    );
  }
  return (json as ApiSuccessResponse<{ message: string; applicationCount: number }>)
    .data;
}

export async function sendInformAfterHearingScenario(
  leadId: string,
  scenario: InformAfterHearingScenario
): Promise<{ waLink: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/inform-after-hearing/send`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ scenario }),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to send Inform after hearing"
    );
  }
  return (json as ApiSuccessResponse<{ waLink: string }>).data;
}

export async function sendAllInformAfterHearing(
  leadId: string,
  tmNos?: string[]
): Promise<{ waLinks: string[]; messageCount: number }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/inform-after-hearing/send-all`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLinks: string[]; messageCount: number }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to send all Inform after hearing"
    );
  }
  return (json as ApiSuccessResponse<{ waLinks: string[]; messageCount: number }>)
    .data;
}

// ---------------------------------------------------------------------------
// 3D(xxv) Upload Documents after Hearing — record only
// ---------------------------------------------------------------------------

export async function recordUploadDocumentsAfterHearing(
  leadId: string,
  tmNos?: string[]
): Promise<void> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/upload-documents-after-hearing/record`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  if (!res.ok) {
    const json = (await res.json().catch(() => ({}))) as ApiErrorResponse;
    throw new Error(
      json?.message ||
        json?.error ||
        "Failed to record Upload Documents after Hearing"
    );
  }
}

// ---------------------------------------------------------------------------
// 3D(xxvi) Invoice for TM-M after Hearing
// ---------------------------------------------------------------------------

export interface TmMAfterHearingInvoiceTmRow {
  tmNo: string;
  mark: string;
  classNumber: number | null;
  invoiceAmount: number | null;
  computedInvoiceTotal: number | null;
  collectedAmount: number | null;
  outstandingAmount: number | null;
  invoiceDate: string | null;
  /** Whether the invoice number prints on the generated PDF (review item 14). */
  includeInvoiceNo?: boolean;
  tmMAfterHearingInvoicePdfKey: string | null;
  tmMAfterHearingInvoicePdfUrl: string | null;
}

export interface TmMAfterHearingInvoiceStatus {
  isActive?: boolean;
  invoiceNo: string | null;
  /** Whether the invoice number prints on the generated PDF (review item 14). */
  includeInvoiceNo?: boolean;
  invoiceDate: string | null;
  invoiceAmount: number | null;
  computedInvoiceTotal: number | null;
  noOfAppsForTmMAfterHearing: number;
  collectedAmount: number | null;
  outstandingAmount: number | null;
  tmMAfterHearingInvoicePdfKey: string | null;
  tmMAfterHearingInvoicePdfUrl: string | null;
  acknowledgementNo: string | null;
  dateOfFiling: string | null;
  addressee: string | null;
  formOfAddress: string | null;
  mark: string | null;
  selectedTypeOfMark: string | null;
  selectedClass: string | null;
  dateOfHearing: string | null;
  tmInvoiceRows: TmMAfterHearingInvoiceTmRow[];
}

export async function getTmMAfterHearingInvoiceStatus(
  leadId: string,
  tmNos?: string[]
): Promise<TmMAfterHearingInvoiceStatus> {
  const params = tmNos?.length
    ? `?${tmNos.map((t) => `tmNos=${encodeURIComponent(t)}`).join("&")}`
    : "";
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/hearing/tm-m-after/invoice${params}`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<TmMAfterHearingInvoiceStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load TM-M after Hearing Invoice"
    );
  }
  return (json as ApiSuccessResponse<TmMAfterHearingInvoiceStatus>).data;
}

export async function saveTmMAfterHearingInvoice(
  leadId: string,
  applications: Array<{
    tmNo: string;
    invoiceAmount: number;
    discount?: number;
    invoiceDate?: string;
    includeInvoiceNo?: boolean;
  }>
): Promise<TmMAfterHearingInvoiceStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/hearing/tm-m-after/invoice`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ applications }),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<TmMAfterHearingInvoiceStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save TM-M after Hearing Invoice"
    );
  }
  return (json as ApiSuccessResponse<TmMAfterHearingInvoiceStatus>).data;
}

export async function generateTmMAfterHearingInvoicePdf(
  leadId: string,
  tmNos?: string[]
): Promise<{ s3Key: string; downloadUrl: string | null }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/hearing/tm-m-after/invoice/generate-pdf`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ s3Key: string; downloadUrl: string | null }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to generate TM-M after Hearing Invoice PDF"
    );
  }
  return (json as ApiSuccessResponse<{ s3Key: string; downloadUrl: string | null }>)
    .data;
}

// --- 3D(xxvi) TM-M after Hearing Invoice send / preview (WhatsApp + Email) ---

export interface TmMAfterHearingInvoiceEmailPreview {
  to: string | null;
  subject: string;
  body: string;
  filename: string | null;
  downloadUrl: string | null;
}

export interface TmMAfterHearingInvoicePaymentMessagePreview {
  pdfMessage: string;
  paymentMessage: string;
  emailPreview: TmMAfterHearingInvoiceEmailPreview;
}

export async function getTmMAfterHearingInvoicePaymentMessagePreview(
  leadId: string,
  tmNos?: string[]
): Promise<TmMAfterHearingInvoicePaymentMessagePreview> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/hearing/tm-m-after/invoice/whatsapp-preview${buildHearingTmNosQuery(tmNos)}`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<TmMAfterHearingInvoicePaymentMessagePreview>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load TM-M after Hearing Invoice message preview"
    );
  }
  return (
    json as ApiSuccessResponse<TmMAfterHearingInvoicePaymentMessagePreview>
  ).data;
}

export async function sendTmMAfterHearingInvoiceToClient(
  leadId: string,
  body: {
    sendWhatsApp?: boolean;
    sendEmail?: boolean;
    tmNos?: string[];
  } = {}
): Promise<{ waLink: string; emailSent?: boolean }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/hearing/tm-m-after/invoice/send-to-client`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink: string; emailSent?: boolean }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to send TM-M after Hearing Invoice to client"
    );
  }
  return (json as ApiSuccessResponse<{ waLink: string; emailSent?: boolean }>)
    .data;
}

export async function sendTmMAfterHearingInvoicePaymentMessage(
  leadId: string,
  tmNos: string[]
): Promise<{ waLink: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/hearing/tm-m-after/invoice/send-payment-message`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ tmNos }),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to send TM-M after Hearing Invoice payment message"
    );
  }
  return (json as ApiSuccessResponse<{ waLink: string }>).data;
}

// ---------------------------------------------------------------------------
// 3D(xxvii) Payment from Client for TM-M after Hearing
// ---------------------------------------------------------------------------

export async function saveTmMAfterHearingCollectedAmount(
  leadId: string,
  items: Array<{ tmNo: string; collectedAmount: number }>
): Promise<TmMAfterHearingInvoiceStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/hearing/tm-m-after/collected-amount`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items }),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<TmMAfterHearingInvoiceStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save Collected Amount for TM-M after Hearing"
    );
  }
  return (json as ApiSuccessResponse<TmMAfterHearingInvoiceStatus>).data;
}

// ---------------------------------------------------------------------------
// 3D(xxviii) File TM-M after Hearing 1
// ---------------------------------------------------------------------------

export interface TmMAfterHearingFiledApplication {
  tmNo: string;
  dateOfTmMAfterHearingFiled: string | null;
  tmMAfterHearingFileKey: string | null;
  tmMAfterHearingFilename: string | null;
  tmMAfterHearingFileUrl: string | null;
}

export interface TmMAfterHearingFiledStatus {
  acknowledgementNo: string | null;
  dateOfFiling: string | null;
  /** Active only when 3D(xxii) officer direction is FILE_TM_M. */
  isActive: boolean;
  applications: TmMAfterHearingFiledApplication[];
}

export async function getTmMAfterHearingFiledStatus(
  leadId: string
): Promise<TmMAfterHearingFiledStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/hearing/tm-m-after/filed`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<TmMAfterHearingFiledStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load File TM-M after Hearing"
    );
  }
  return (json as ApiSuccessResponse<TmMAfterHearingFiledStatus>).data;
}

export async function saveTmMAfterHearingFiled(
  leadId: string,
  applications: Array<{
    tmNo: string;
    dateOfTmMAfterHearingFiled?: string | null;
    tmMAfterHearingFileKey?: string | null;
    tmMAfterHearingFilename?: string | null;
  }>
): Promise<TmMAfterHearingFiledStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/hearing/tm-m-after/filed`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ applications }),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<TmMAfterHearingFiledStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save File TM-M after Hearing"
    );
  }
  return (json as ApiSuccessResponse<TmMAfterHearingFiledStatus>).data;
}

// ---------------------------------------------------------------------------
// 3D(xxix) Inform Client about Filing TM-M after Hearing
// ---------------------------------------------------------------------------

export async function getInformClientTmMAfterHearingPreview(
  leadId: string,
  tmNos?: string[]
): Promise<{ message: string; applicationCount: number }> {
  const params = tmNos?.length
    ? `?${tmNos.map((t) => `tmNos=${encodeURIComponent(t)}`).join("&")}`
    : "";
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/hearing/tm-m-after/inform-client-preview${params}`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ message: string; applicationCount: number }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load preview"
    );
  }
  return (json as ApiSuccessResponse<{ message: string; applicationCount: number }>).data;
}

export async function sendInformClientTmMAfterHearing(
  leadId: string,
  tmNos?: string[]
): Promise<{ waLink: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/hearing/tm-m-after/inform-client/send-whatsapp`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to send Inform Client about Filing of TM-M after Hearing"
    );
  }
  return (json as ApiSuccessResponse<{ waLink: string }>).data;
}

// ---------------------------------------------------------------------------
// 3D(xxx) Subsequent Hearings
// ---------------------------------------------------------------------------

export interface SubsequentHearingOfficerDirection {
  index: number;
  direction: string;
}

export interface SubsequentHearingApplication {
  ackApplicationId?: string;
  tmNo: string;
  mark: string | null;
  typeOfMark?: string | null;
  classNumber: number | null;
  applicationDate?: string | null;
  dateOfHearing: string | null;
  hearingNoticeFileKey: string | null;
  hearingNoticeFilename: string | null;
  hearingNoticeFileUrl: string | null;
  hearingStatus: HearingStatusEnum | null;
  hearingOfficerRemark: HearingOfficerRemarkEnum | null;
  officerDirections?: SubsequentHearingOfficerDirection[];
  remarks?: string | null;
  postponementNoticeFileKey?: string | null;
  postponementNoticeFilename?: string | null;
  postponementNoticeFileUrl?: string | null;
}

export interface SubsequentHearingStatus {
  isActive?: boolean;
  acknowledgementNo: string | null;
  /**
   * Current hearing number (2, 3, 4, ...). Backend field name.
   * Kept as `hearingNumber` alias for backward-compat where used.
   */
  currentHearingNumber: number;
  hearingNumber?: number;
  fieldPrefix?: string;
  dateOfFiling?: string | null;
  applicant?: string | null;
  totalApplications?: number;
  applications: SubsequentHearingApplication[];
  /** Whether conditional steps (Stamp Paper / Affidavit / Notarisation) are active. */
  conditionalStepsActive?: boolean;
  conditionalStepsReason?: string;
  isInvoiceActive?: boolean;
}

export async function getSubsequentHearingStatus(
  leadId: string
): Promise<SubsequentHearingStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/subsequent-hearing/status`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<SubsequentHearingStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Subsequent Hearings"
    );
  }
  const data = (json as ApiSuccessResponse<SubsequentHearingStatus>).data;
  // Backend returns `currentHearingNumber`; expose `hearingNumber` alias.
  if (data && data.hearingNumber == null && data.currentHearingNumber != null) {
    data.hearingNumber = data.currentHearingNumber;
  }
  return data;
}

export async function saveSubsequentHearingDetails(
  leadId: string,
  hearingNumber: number,
  applications: Array<{
    tmNo: string;
    dateOfHearing?: string | null;
    hearingNoticeFileKey?: string | null;
    hearingNoticeFilename?: string | null;
  }>
): Promise<SubsequentHearingStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/subsequent-hearing/details`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ hearingNumber, applications }),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<SubsequentHearingStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save Subsequent Hearing details"
    );
  }
  return (json as ApiSuccessResponse<SubsequentHearingStatus>).data;
}

export async function getSubsequentHearingNoticePreview(
  leadId: string,
  tmNos?: string[]
): Promise<{ message: string }> {
  const params = tmNos?.length
    ? `?${tmNos.map((t) => `tmNos=${encodeURIComponent(t)}`).join("&")}`
    : "";
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/subsequent-hearing/notice/preview${params}`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ message: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Subsequent Hearing notice preview"
    );
  }
  return (json as ApiSuccessResponse<{ message: string }>).data;
}

export async function sendSubsequentHearingNotice(
  leadId: string,
  tmNos?: string[]
): Promise<{ waLink: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/subsequent-hearing/notice/send`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to send Subsequent Hearing notice"
    );
  }
  return (json as ApiSuccessResponse<{ waLink: string }>).data;
}

// --- 3D(xxx) Subsequent Hearing Outcome (attend) ---

export async function saveSubsequentHearingOutcome(
  leadId: string,
  hearingNumber: number,
  applications: Array<{
    tmNo: string;
    hearingStatus: HearingStatusEnum;
    hearingOfficerRemark?: HearingOfficerRemarkEnum | null;
    officerDirections?: SubsequentHearingOfficerDirection[];
    remarks?: string | null;
  }>
): Promise<SubsequentHearingStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/subsequent-hearing/outcome`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ hearingNumber, applications }),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<SubsequentHearingStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save Subsequent Hearing outcome"
    );
  }
  const data = (json as ApiSuccessResponse<SubsequentHearingStatus>).data;
  if (data && data.hearingNumber == null && data.currentHearingNumber != null) {
    data.hearingNumber = data.currentHearingNumber;
  }
  return data;
}

// --- 3D(xxx) Subsequent Hearing Postponement Notice ---

export async function saveSubsequentHearingPostponement(
  leadId: string,
  hearingNumber: number,
  applications: Array<{
    tmNo: string;
    postponementNoticeFileKey?: string | null;
    postponementNoticeFilename?: string | null;
  }>
): Promise<SubsequentHearingStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/subsequent-hearing/postponement`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ hearingNumber, applications }),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<SubsequentHearingStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save Subsequent Hearing postponement notice"
    );
  }
  const data = (json as ApiSuccessResponse<SubsequentHearingStatus>).data;
  if (data && data.hearingNumber == null && data.currentHearingNumber != null) {
    data.hearingNumber = data.currentHearingNumber;
  }
  return data;
}

// --- 3D(v) Subsequent Hearing Invoice ---

export interface SubsequentHearingInvoiceTmRow {
  tmNo: string;
  defaultInvoiceAmount: number;
  invoiceAmount: number | null;
  individualInvoiceNo: string | null;
  includeInvoiceNo: boolean;
  invoiceDate: string | null;
  collectedAmount: number | null;
  outstandingAmount: number | null;
}

export interface SubsequentHearingInvoiceStatus {
  isActive: boolean;
  hearingNumber: number;
  fieldPrefix: string;
  invoiceAmount: number | null;
  invoiceNo: string | null;
  invoiceDate: string | null;
  noOfApps: number;
  includeInvoiceNo: boolean;
  invoicePdfKey: string | null;
  invoicePdfUrl: string | null;
  collectedAmount: number | null;
  outstandingAmount: number | null;
  tmInvoiceRows: SubsequentHearingInvoiceTmRow[];
}

export interface SubsequentHearingInvoicePreviewApplication {
  tmNo: string;
  applicationDate: string;
  mark: string;
  typeOfMark: string;
  classNumber: number;
  dateOfHearing: string | null;
  selected: boolean;
}

export interface SubsequentHearingInvoicePreview {
  dataLabel: string;
  hearingNumber: number;
  acknowledgementNo: string | null;
  dateOfFiling: string | null;
  addressee: string;
  addresseeLine1: string | null;
  addresseeLine2: string | null;
  addresseeLine3: string | null;
  formOfAddress: string | null;
  applications: SubsequentHearingInvoicePreviewApplication[];
  invoiceAmount: number;
  invoiceDate: string | null;
  invoiceNo: string | null;
  includeInvoiceNo: boolean;
  noOfApps: number;
}

function buildSubsequentHearingTmNosQuery(
  hearingNumber: number,
  tmNos?: string[]
): string {
  const params = new URLSearchParams();
  params.append("hearingNumber", String(hearingNumber));
  if (tmNos?.length) {
    for (const tm of tmNos) params.append("tmNos", tm);
  }
  return `?${params.toString()}`;
}

export async function getSubsequentHearingInvoiceStatus(
  leadId: string,
  hearingNumber: number,
  tmNos?: string[]
): Promise<SubsequentHearingInvoiceStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/subsequent-hearing/invoice${buildSubsequentHearingTmNosQuery(
      hearingNumber,
      tmNos
    )}`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<SubsequentHearingInvoiceStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Subsequent Hearing Invoice"
    );
  }
  return (json as ApiSuccessResponse<SubsequentHearingInvoiceStatus>).data;
}

export async function getSubsequentHearingInvoicePreview(
  leadId: string,
  hearingNumber: number,
  tmNos?: string[]
): Promise<SubsequentHearingInvoicePreview | null> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/subsequent-hearing/invoice/preview${buildSubsequentHearingTmNosQuery(
      hearingNumber,
      tmNos
    )}`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<SubsequentHearingInvoicePreview | null>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Subsequent Hearing Invoice preview"
    );
  }
  return (json as ApiSuccessResponse<SubsequentHearingInvoicePreview | null>)
    .data;
}

export interface SubsequentHearingInvoiceWhatsAppPreview {
  pdfMessage: string;
  paymentMessage: string;
}

export async function getSubsequentHearingInvoiceWhatsAppPreview(
  leadId: string,
  hearingNumber: number,
  tmNos?: string[]
): Promise<SubsequentHearingInvoiceWhatsAppPreview> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/subsequent-hearing/invoice/whatsapp-preview${buildSubsequentHearingTmNosQuery(
      hearingNumber,
      tmNos
    )}`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<SubsequentHearingInvoiceWhatsAppPreview>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Subsequent Hearing Invoice message preview"
    );
  }
  return (json as ApiSuccessResponse<SubsequentHearingInvoiceWhatsAppPreview>)
    .data;
}

export async function saveSubsequentHearingInvoice(
  leadId: string,
  applications: Array<{
    tmNo: string;
    hearingNumber: number;
    invoiceAmount: number;
    invoiceDate?: string | null;
    includeInvoiceNo?: boolean | null;
  }>
): Promise<SubsequentHearingInvoiceStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/subsequent-hearing/invoice`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ applications }),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<SubsequentHearingInvoiceStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save Subsequent Hearing Invoice"
    );
  }
  return (json as ApiSuccessResponse<SubsequentHearingInvoiceStatus>).data;
}

export async function generateSubsequentHearingInvoicePdf(
  leadId: string,
  hearingNumber: number,
  tmNos?: string[]
): Promise<{ s3Key: string; downloadUrl: string; filename?: string }> {
  const params = new URLSearchParams();
  params.append("hearingNumber", String(hearingNumber));
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/subsequent-hearing/invoice/generate-pdf?${params.toString()}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{
        s3Key: string;
        downloadUrl: string;
        filename?: string;
      }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to generate Subsequent Hearing Invoice PDF"
    );
  }
  return (
    json as ApiSuccessResponse<{
      s3Key: string;
      downloadUrl: string;
      filename?: string;
    }>
  ).data;
}

export async function sendSubsequentHearingInvoiceToClient(
  leadId: string,
  body: {
    hearingNumber: number;
    sendWhatsApp?: boolean;
    sendEmail?: boolean;
    tmNos?: string[];
  }
): Promise<{ waLink: string; emailSent?: boolean }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/subsequent-hearing/invoice/send`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink: string; emailSent?: boolean }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to send Subsequent Hearing Invoice"
    );
  }
  return (json as ApiSuccessResponse<{ waLink: string; emailSent?: boolean }>)
    .data;
}

export async function saveSubsequentHearingCollectedAmount(
  leadId: string,
  hearingNumber: number,
  applications: Array<{ tmNo: string; collectedAmount: number }>
): Promise<SubsequentHearingInvoiceStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/subsequent-hearing/collected-amount`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ hearingNumber, applications }),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<SubsequentHearingInvoiceStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save Subsequent Hearing collected amount"
    );
  }
  return (json as ApiSuccessResponse<SubsequentHearingInvoiceStatus>).data;
}

// ---------------------------------------------------------------------------
// 3D(xlix) Order of Hearing to Client
// ---------------------------------------------------------------------------

export type OrderOfHearingOutcome =
  | "ACCEPTED_AFTER_HEARING"
  | "REFUSED_AFTER_HEARING"
  | "REFUSED_AFTER_NON_APPEARANCE";

export interface OrderOfHearingApplication {
  tmNo: string;
  mark: string | null;
  classNumber: number | null;
  orderOfHearingFileKey: string | null;
  orderOfHearingFilename: string | null;
  orderOfHearingFileUrl: string | null;
  outcomeOfHearing: OrderOfHearingOutcome | null;
}

export interface OrderOfHearingStatus {
  /**
   * Active only when 2l) User Date was "Proposed to be used" at filing for the scoped mark,
   * and the Order of Hearing was not already completed for it in Hearing 1.
   */
  isActive: boolean;
  inactiveReason: string | null;
  acknowledgementNo: string | null;
  dateOfFiling: string | null;
  applications: OrderOfHearingApplication[];
}

export async function getOrderOfHearingStatus(
  leadId: string,
  tmNos?: string[]
): Promise<OrderOfHearingStatus> {
  const params = tmNos?.length
    ? `?${tmNos.map((t) => `tmNos=${encodeURIComponent(t)}`).join("&")}`
    : "";
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/order-of-hearing/status${params}`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<OrderOfHearingStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Order of Hearing"
    );
  }
  return (json as ApiSuccessResponse<OrderOfHearingStatus>).data;
}

export async function saveOrderOfHearingDetails(
  leadId: string,
  applications: Array<{
    tmNo: string;
    orderOfHearingFileKey?: string | null;
    orderOfHearingFilename?: string | null;
    outcomeOfHearing?: OrderOfHearingOutcome | null;
  }>
): Promise<OrderOfHearingStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/order-of-hearing/details`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ applications }),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<OrderOfHearingStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save Order of Hearing"
    );
  }
  return (json as ApiSuccessResponse<OrderOfHearingStatus>).data;
}

export async function forwardOrderOfHearingPdfsToClient(
  leadId: string,
  tmNos?: string[]
): Promise<{ waLink: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/order-of-hearing/forward-pdfs`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to forward Order of Hearing PDFs"
    );
  }
  return (json as ApiSuccessResponse<{ waLink: string }>).data;
}

export async function getOrderOfHearingMessagePreview(
  leadId: string,
  scenario: OrderOfHearingOutcome,
  tmNos?: string[]
): Promise<{ message: string; applicationCount: number }> {
  const params = new URLSearchParams();
  params.set("scenario", scenario);
  if (tmNos?.length) {
    for (const t of tmNos) params.append("tmNos", t);
  }
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/order-of-hearing/message-preview?${params.toString()}`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ message: string; applicationCount: number }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Order of Hearing message preview"
    );
  }
  return (json as ApiSuccessResponse<{ message: string; applicationCount: number }>)
    .data;
}

export async function sendOrderOfHearingMessage(
  leadId: string,
  scenario: OrderOfHearingOutcome,
  tmNos?: string[]
): Promise<{ waLink: string; applicationCount: number }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/order-of-hearing/send-message`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ scenario, ...(tmNos?.length ? { tmNos } : {}) }),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink: string; applicationCount: number }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to send Order of Hearing message"
    );
  }
  return (json as ApiSuccessResponse<{ waLink: string; applicationCount: number }>)
    .data;
}

// ───────────────────────────────────────────────────────────────────────────
// Advertised in Journal — Standard Advertisement (S3_04)
// ───────────────────────────────────────────────────────────────────────────

export interface AdvertisedInJournalApplication {
  ackApplicationId: string;
  tmNo: string;
  mark: string;
  journalDate: string | null;
  lastDateForOpposition: string | null;
  orderFromRegistryFileKey: string | null;
  orderFromRegistryFilename: string | null;
  orderFromRegistryFileUrl: string | null;
  /**
   * Substep 4(iii): the scenario this application was last informed under, so the selection
   * is restored on reload (review item 79). Null until 4(iii) has been sent.
   */
  advertisedFrom: AdvertisedInJournalAdvertisedFrom | null;
}

export interface AdvertisedInJournalStatusResponse {
  acknowledgementNo: string | null;
  dateOfFiling: string | null;
  applications: AdvertisedInJournalApplication[];
  canForwardPdfs: boolean;
}

export interface SetAdvertisedInJournalApplicationInput {
  tmNo: string;
  journalDate?: string;
  orderFromRegistryFileKey?: string;
  orderFromRegistryFilename?: string;
}

export interface SetAdvertisedInJournalStatusPayload {
  applications: SetAdvertisedInJournalApplicationInput[];
}

export async function getAdvertisedInJournalStatus(
  leadId: string
): Promise<AdvertisedInJournalStatusResponse> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/advertised-in-journal/status-details`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<AdvertisedInJournalStatusResponse>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Advertised in Journal status"
    );
  }
  return (json as ApiSuccessResponse<AdvertisedInJournalStatusResponse>).data;
}

export async function setAdvertisedInJournalStatusDetails(
  leadId: string,
  payload: SetAdvertisedInJournalStatusPayload
): Promise<AdvertisedInJournalStatusResponse> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/advertised-in-journal/status-details`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<AdvertisedInJournalStatusResponse>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save Advertised in Journal status details"
    );
  }
  return (json as ApiSuccessResponse<AdvertisedInJournalStatusResponse>).data;
}

export type AdvertisedInJournalNoticePreview = {
  recipientPhone: string | null;
  recipientEmail: string | null;
  applicationNumbers: string;
  filename: string;
  apps: { tmNo: string; mark: string | null; filename: string | null }[];
  message: string;
  emailPreview: { to: string | null; subject: string; body: string };
};

export async function getAdvertisedInJournalNoticePreview(
  leadId: string,
  tmNos?: string[]
): Promise<AdvertisedInJournalNoticePreview> {
  const qs = tmNos?.length
    ? `?${tmNos.map((t) => `tmNos=${encodeURIComponent(t)}`).join("&")}`
    : "";
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/advertised-in-journal/send-notice-to-client/preview${qs}`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<AdvertisedInJournalNoticePreview>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Order from Registry preview"
    );
  }
  return (json as ApiSuccessResponse<AdvertisedInJournalNoticePreview>).data;
}

export async function sendAdvertisedInJournalNoticeToClient(
  leadId: string,
  opts: {
    tmNos?: string[];
    sendWhatsApp?: boolean;
    sendEmail?: boolean;
  } = {}
): Promise<{ waLink: string; email: string | null }> {
  const body: Record<string, unknown> = {};
  if (opts.tmNos?.length) body.tmNos = opts.tmNos;
  if (opts.sendWhatsApp !== undefined) body.sendWhatsApp = opts.sendWhatsApp;
  if (opts.sendEmail !== undefined) body.sendEmail = opts.sendEmail;
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/advertised-in-journal/send-notice-to-client`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink: string; email: string | null }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to send Advertised in Journal notice to client"
    );
  }
  return (json as ApiSuccessResponse<{ waLink: string; email: string | null }>)
    .data;
}

export type AdvertisedInJournalAdvertisedFrom =
  | "ACCEPTANCE"
  | "ACCEPTANCE_AFTER_OBJECTION"
  | "HEARING";

export interface InformClientAdvertisedInJournalPayload {
  advertisedFrom: AdvertisedInJournalAdvertisedFrom;
  statementOfReasonText?: string;
  tmNos?: string[];
}

/**
 * Substep 4(iii): build the scenario-specific message(s) without sending. Returns an array because
 * applications with differing objection reasons produce separate messages.
 */
export async function previewInformClientAdvertisedInJournal(
  leadId: string,
  payload: InformClientAdvertisedInJournalPayload
): Promise<{ messages: string[] }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/advertised-in-journal/inform-client/preview`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ messages: string[] }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Advertised in Journal message preview"
    );
  }
  return (json as ApiSuccessResponse<{ messages: string[] }>).data;
}

export async function informClientAdvertisedInJournal(
  leadId: string,
  payload: InformClientAdvertisedInJournalPayload
): Promise<{ waLink: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/advertised-in-journal/inform-client`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to send Advertised in Journal inform message"
    );
  }
  return (json as ApiSuccessResponse<{ waLink: string }>).data;
}

// ───────────────────────────────────────────────────────────────────────────
// Opposition Counter (S3_04a) — Status details for Counter
// ───────────────────────────────────────────────────────────────────────────

export interface OppositionCounterApplication {
  ackApplicationId: string;
  tmNo: string;
  mark: string;
  oppositionNo: string | null;
  dateOfOpposition: string | null;
  dateOfReceiptOfOpposition: string | null;
  nameOfOpponent: string | null;
  addressOfOpponent: string | null;
  placeOfOpponent: string | null;
  opponentsMark: string | null;
  imageOfOpponentsMarkFileKey: string | null;
  imageOfOpponentsMarkUrl: string | null;
  classOfOpponentsMark: string | null;
  goodsServicesOfOpponentsMark: string | null;
  userDateOfOpponentsMark: string | null;
  oppositionNoticeFileKey: string | null;
  oppositionNoticeFilename: string | null;
  oppositionNoticeFileUrl: string | null;
  lastDateOfFilingCounter: string | null;
}

export interface OppositionCounterStatusResponse {
  acknowledgementNo: string | null;
  dateOfFiling: string | null;
  applications: OppositionCounterApplication[];
  noOfApplicationsOpposed: number;
  canForwardPdfs: boolean;
}

export interface SetOppositionCounterApplicationInput {
  tmNo: string;
  oppositionNo?: string | null;
  dateOfOpposition?: string | null;
  dateOfReceiptOfOpposition?: string | null;
  nameOfOpponent?: string | null;
  addressOfOpponent?: string | null;
  placeOfOpponent?: string | null;
  opponentsMark?: string | null;
  imageOfOpponentsMarkFileKey?: string | null;
  classOfOpponentsMark?: string | null;
  goodsServicesOfOpponentsMark?: string | null;
  userDateOfOpponentsMark?: string | null;
  oppositionNoticeFileKey?: string | null;
  oppositionNoticeFilename?: string | null;
  lastDateOfFilingCounter?: string | null;
}

export interface SetOppositionCounterStatusPayload {
  applications: SetOppositionCounterApplicationInput[];
}

export async function getOppositionCounterStatus(
  leadId: string
): Promise<OppositionCounterStatusResponse> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/status-details`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<OppositionCounterStatusResponse>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Opposition Counter status"
    );
  }
  return (json as ApiSuccessResponse<OppositionCounterStatusResponse>).data;
}

export async function setOppositionCounterStatusDetails(
  leadId: string,
  payload: SetOppositionCounterStatusPayload
): Promise<OppositionCounterStatusResponse> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/status-details`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<OppositionCounterStatusResponse>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save Opposition Counter status details"
    );
  }
  return (json as ApiSuccessResponse<OppositionCounterStatusResponse>).data;
}

export type CounterNoticePreview = {
  recipientPhone: string | null;
  recipientEmail: string | null;
  applicationNumbers: string;
  filename: string;
  apps: { tmNo: string; mark: string | null; filename: string | null }[];
  message: string;
  emailPreview: { to: string | null; subject: string; body: string };
};

export async function getOppositionCounterNoticePreview(
  leadId: string,
  tmNos?: string[]
): Promise<CounterNoticePreview> {
  const qs = tmNos?.length
    ? `?${tmNos.map((t) => `tmNos=${encodeURIComponent(t)}`).join("&")}`
    : "";
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/send-notice-to-client/preview${qs}`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<CounterNoticePreview>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Opposition Notice preview"
    );
  }
  return (json as ApiSuccessResponse<CounterNoticePreview>).data;
}

export async function sendOppositionCounterNoticeToClient(
  leadId: string,
  opts: {
    tmNos?: string[];
    sendWhatsApp?: boolean;
    sendEmail?: boolean;
  } = {}
): Promise<{ waLink: string; email: string | null }> {
  const body: Record<string, unknown> = {};
  if (opts.tmNos?.length) body.tmNos = opts.tmNos;
  if (opts.sendWhatsApp !== undefined) body.sendWhatsApp = opts.sendWhatsApp;
  if (opts.sendEmail !== undefined) body.sendEmail = opts.sendEmail;
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/send-notice-to-client`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink: string; email: string | null }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to send Opposition Notice to client"
    );
  }
  return (json as ApiSuccessResponse<{ waLink: string; email: string | null }>)
    .data;
}

export async function getInformClientAboutCounterPreview(
  leadId: string,
  tmNos?: string[]
): Promise<{ message: string; applicationCount: number; tmNos: string[] }> {
  const qs = tmNos?.length
    ? `?${tmNos.map((t) => `tmNos=${encodeURIComponent(t)}`).join("&")}`
    : "";
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/inform-client-about-counter/preview${qs}`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{
        message: string;
        applicationCount: number;
        tmNos: string[];
      }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Inform Client about Counter preview"
    );
  }
  return (
    json as ApiSuccessResponse<{
      message: string;
      applicationCount: number;
      tmNos: string[];
    }>
  ).data;
}

export async function informClientAboutOppositionCounter(
  leadId: string,
  tmNos?: string[]
): Promise<{ waLink: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/inform-client-about-counter`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to send Inform Client about Counter"
    );
  }
  return (json as ApiSuccessResponse<{ waLink: string }>).data;
}

export async function recordCallClientForCounter(
  leadId: string,
  tmNos?: string[]
): Promise<void> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/record-call-client`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  if (!res.ok) {
    const json = (await res.json().catch(() => null)) as
      | ApiErrorResponse
      | null;
    throw new Error(
      json?.message ||
        json?.error ||
        "Failed to record Call Client for Counter"
    );
  }
}

// ───────────────────────────────────────────────────────────────────────────
// Opposition Counter — Confirmation from Client (4A(v))
// ───────────────────────────────────────────────────────────────────────────

export interface CounterConfirmationApplicationRow {
  tmNo: string;
  mark: string;
  clientGoingAhead: boolean | null;
}

export interface CounterConfirmationStatus {
  clientGoingAhead: boolean | null;
  applications: CounterConfirmationApplicationRow[];
  noOfApplicationsOpposed: number;
  noOfApplicationsForCounter: number;
  acknowledgementNo: string | null;
  /** Returned when all picks are NO — text hint about the next stage (Abandonment / Non Filing of Counter Hearing). */
  suggestedNextStepWhenNo?: string;
}

export async function getCounterConfirmationFromClient(
  leadId: string
): Promise<CounterConfirmationStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/confirmation-from-client`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<CounterConfirmationStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Confirmation from Client for Counter"
    );
  }
  return (json as ApiSuccessResponse<CounterConfirmationStatus>).data;
}

export async function saveCounterConfirmationFromClient(
  leadId: string,
  applications: { tmNo: string; clientGoingAhead: boolean }[],
  noOfApplicationsOpposed?: number,
  noOfApplicationsForCounter?: number
): Promise<CounterConfirmationStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/confirmation-from-client`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        applications,
        ...(noOfApplicationsOpposed != null ? { noOfApplicationsOpposed } : {}),
        ...(noOfApplicationsForCounter != null ? { noOfApplicationsForCounter } : {}),
      }),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<CounterConfirmationStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save Confirmation from Client for Counter"
    );
  }
  return (json as ApiSuccessResponse<CounterConfirmationStatus>).data;
}

// ───────────────────────────────────────────────────────────────────────────
// Opposition Counter — Invoice (4A(vi))
// ───────────────────────────────────────────────────────────────────────────

export interface CounterInvoiceTmRow {
  tmNo: string;
  defaultInvoiceAmount: number;
  invoiceAmount: number;
  discount: number;
  individualInvoiceNo: string | null;
  includeInvoiceNo: boolean;
  invoiceDate: string | null;
  collectedAmount: number | null;
  outstandingAmount: number | null;
}

export interface CounterInvoiceStatus {
  noOfApps: number;
  invoiceAmount: number | null;
  /** Acknowledgement-wide: Rs.2700 × noOfApps */
  govtFee: number | null;
  /** Acknowledgement-wide: Rs.7000 × noOfApps */
  professionalCharges: number | null;
  /** Per-application unit fees — use these when the surrounding amount is for one application. */
  govtFeePerApp: number;
  professionalChargesPerApp: number;
  discount: number | null;
  invoiceNo: string | null;
  invoiceDate: string | null;
  includeInvoiceNo: boolean;
  collectedAmount: number | null;
  outstandingAmount: number | null;
  counterInvoicePdfKey: string | null;
  counterInvoicePdfUrl: string | null;
  tmInvoiceRows: CounterInvoiceTmRow[];
}

export interface CounterInvoicePreviewApplication {
  tmNo: string;
  applicationDate: string;
  mark: string;
  typeOfMark: string;
  classNumber: number;
  nameOfOpponent: string | null;
  placeOfOpponent: string | null;
}

export interface CounterInvoicePreview {
  dataLabel: string;
  addressee: string;
  addresseeLine1: string | null;
  addresseeLine2: string | null;
  addresseeLine3: string | null;
  formOfAddress: string | null;
  acknowledgementNo: string | null;
  dateOfFiling: string | null;
  mark: string | null;
  typeOfMark: string | null;
  classNumber: number | null;
  applications: CounterInvoicePreviewApplication[];
  lastDateOfFilingCounter: string | null;
  govtFee: number;
  professionalCharges: number;
  discount: number;
  invoiceAmount: number;
  invoiceDate: string | null;
  invoiceNo: string | null;
  includeInvoiceNo: boolean;
  tmInvoiceRows?: CounterInvoiceTmRow[];
}

export async function getCounterInvoice(
  leadId: string
): Promise<CounterInvoiceStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/invoice`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<CounterInvoiceStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Counter Invoice"
    );
  }
  return (json as ApiSuccessResponse<CounterInvoiceStatus>).data;
}

export async function getCounterInvoicePreview(
  leadId: string
): Promise<CounterInvoicePreview | null> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/invoice/preview`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<CounterInvoicePreview | null>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Counter Invoice preview"
    );
  }
  return (json as ApiSuccessResponse<CounterInvoicePreview | null>).data;
}

export interface CounterInvoiceEmailPreview {
  to: string | null;
  subject: string;
  body: string;
  filename: string | null;
  downloadUrl: string | null;
}

export interface CounterInvoiceWhatsAppPreview {
  pdfMessage: string;
  paymentMessage: string;
  emailPreview: CounterInvoiceEmailPreview;
}

export async function getCounterInvoiceWhatsAppPreview(
  leadId: string,
  tmNos?: string[]
): Promise<CounterInvoiceWhatsAppPreview> {
  const params = new URLSearchParams();
  if (tmNos?.length) {
    for (const tmNo of tmNos) params.append("tmNos", tmNo);
  }
  const qs = params.toString();
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/invoice/whatsapp-preview${
      qs ? `?${qs}` : ""
    }`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<CounterInvoiceWhatsAppPreview>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Counter Invoice message preview"
    );
  }
  return (json as ApiSuccessResponse<CounterInvoiceWhatsAppPreview>).data;
}

export interface SaveCounterInvoiceApplicationInput {
  tmNo: string;
  invoiceAmount: number;
  discount?: number;
  invoiceDate?: string;
  includeInvoiceNo?: boolean;
}

export async function saveCounterInvoice(
  leadId: string,
  applications: SaveCounterInvoiceApplicationInput[]
): Promise<CounterInvoiceStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/invoice`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ applications }),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<CounterInvoiceStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save Counter Invoice"
    );
  }
  return (json as ApiSuccessResponse<CounterInvoiceStatus>).data;
}

export async function generateCounterInvoicePdf(
  leadId: string
): Promise<{ key: string; url: string; filename: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/invoice/generate-pdf`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<GeneratedPdfPayload>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to generate Counter Invoice PDF"
    );
  }
  return toGeneratedPdf((json as ApiSuccessResponse<GeneratedPdfPayload>).data);
}

export async function sendCounterInvoiceToClient(
  leadId: string,
  options?: { tmNos?: string[]; sendWhatsApp?: boolean; sendEmail?: boolean }
): Promise<{ waLink?: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/invoice/send-to-client`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        sendWhatsApp: options?.sendWhatsApp ?? true,
        sendEmail: options?.sendEmail ?? false,
        ...(options?.tmNos?.length ? { tmNos: options.tmNos } : {}),
      }),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink?: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to send Counter Invoice to client"
    );
  }
  return (json as ApiSuccessResponse<{ waLink?: string }>).data;
}

export async function sendCounterInvoicePaymentMessage(
  leadId: string,
  tmNos?: string[]
): Promise<{ waLink?: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/invoice/send-payment-message`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink?: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to send Counter payment message"
    );
  }
  return (json as ApiSuccessResponse<{ waLink?: string }>).data;
}

// ───────────────────────────────────────────────────────────────────────────
// Opposition Counter — Collected Amount (4A(vii))
// ───────────────────────────────────────────────────────────────────────────

export async function saveCounterCollectedAmount(
  leadId: string,
  applications: { tmNo: string; collectedAmount: number }[]
): Promise<CounterInvoiceStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/collected-amount`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ applications }),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<CounterInvoiceStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save Collected Amount"
    );
  }
  return (json as ApiSuccessResponse<CounterInvoiceStatus>).data;
}

// ───────────────────────────────────────────────────────────────────────────
// Opposition Counter — Prepare Counter (4A(viii))
// ───────────────────────────────────────────────────────────────────────────

export interface PrepareCounterApplicationItem {
  counterNo: number;
  ackApplicationId: string;
  tmNo: string;
  mark: string;
  applicant: string | null;
  applicantAddress: string | null;
  applicantType: string | null;
  descriptionOfApplicant: string | null;
  signatoryDetails: string | null;
  signatoryName: string | null;
  signatoryAge: number | null;
  signatoryGuardianName: string | null;
  signatoryRelationWithGuardian: string | null;
  signatoryDesignation: string | null;
  userDate: string | null;
  classNumber: number | null;
  goodsServices: string | null;
  classWithGoodsServices: string | null;
  imageOfMarkUrl: string | null;
  oppositionNo: string | null;
  dateOfOpposition: string | null;
  dateOfReceiptOfOpposition: string | null;
  oppositionNoWithDate: string | null;
  nameOfOpponent: string | null;
  addressOfOpponent: string | null;
  opponentsMark: string | null;
  imageOfOpponentsMarkUrl: string | null;
  classOfOpponentsMark: string | null;
  goodsServicesOfOpponentsMark: string | null;
  userDateOfOpponentsMark: string | null;
  /** 4u) raw YYYY-MM-DD, so 4A(viii) can prefill its picker. */
  dateOfFilingCounterStatement: string | null;
  dateOfFilingCounterStatementFormatted: string | null;
  dateOfNoticeOfCounterStatement?: string | null;
  lastDateForEvidenceByOpponent?: string | null;
  lastDateForEvidenceByOpponentExpired?: boolean;
  noOfParagraphsInOpponentIntro: number | null;
  paragraphWiseReplies: Array<{ heading: string; text: string }>;
  opponentAttorneyName: string | null;
  opponentAttorneyAddress: string | null;
  opponentAttorneyEmail: string | null;
  dateOfEvidenceAffidavit: string | null;
  gsTypeA: string;
  gsTypeB: string;
  gsTypeC: string;
  counterStatementPdfKey: string | null;
  counterStatementPdfUrl: string | null;
  skipped: boolean;
}

export interface PrepareCounterStatus {
  acknowledgementNo: string | null;
  counterStatements: PrepareCounterApplicationItem[];
}

export interface PrepareCounterPreview {
  mark: string | null;
  goodsServices: string | null;
  classNumber: number | null;
  applicant: string | null;
  applicantAddress: string | null;
  applicantType: string | null;
  descriptionOfApplicant: string | null;
  userDate: string | null;
  signatoryDetails: string | null;
  signatoryName: string | null;
  signatoryAge: number | null;
  signatoryGuardianName: string | null;
  signatoryRelationWithGuardian: string | null;
  signatoryDesignation: string | null;
  imageOfMarkUrl: string | null;
  gsTypeA: string;
  gsTypeB: string;
  gsTypeC: string;
  oppositionNo: string | null;
  dateOfOpposition: string | null;
  dateOfReceiptOfOpposition: string | null;
  nameOfOpponent: string | null;
  addressOfOpponent: string | null;
  opponentsMark: string | null;
  imageOfOpponentsMarkUrl: string | null;
  classOfOpponentsMark: string | null;
  goodsServicesOfOpponentsMark: string | null;
  userDateOfOpponentsMark: string | null;
  noOfParagraphsInOpponentIntro: number | null;
  paragraphWiseReplies: Array<{ heading: string; text: string }>;
  opponentAttorneyName: string | null;
  opponentAttorneyAddress: string | null;
  opponentAttorneyEmail: string | null;
  dateOfEvidenceAffidavit: string | null;
  dateOfFilingCounterStatement: string | null;
  dateOfFilingCounterStatementFormatted: string | null;
  dateOfNoticeOfCounterStatement?: string | null;
  lastDateForEvidenceByOpponent?: string | null;
  lastDateForEvidenceByOpponentExpired?: boolean;
  skipped: boolean;
}

export async function getPrepareCounterStatus(
  leadId: string
): Promise<PrepareCounterStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/prepare`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<PrepareCounterStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Prepare Counter status"
    );
  }
  return (json as ApiSuccessResponse<PrepareCounterStatus>).data;
}

export async function getPrepareCounterPreview(
  leadId: string,
  ackApplicationId: string
): Promise<PrepareCounterPreview> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/prepare/${ackApplicationId}/preview`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<PrepareCounterPreview>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Prepare Counter preview"
    );
  }
  return (json as ApiSuccessResponse<PrepareCounterPreview>).data;
}

export interface SavePrepareCounterApplicationInput {
  tmNo: string;
  noOfParagraphsInOpponentIntro?: number | null;
  paragraphWiseReplies?: Array<{ heading?: string; text?: string }>;
  opponentAttorneyName?: string | null;
  opponentAttorneyAddress?: string | null;
  opponentAttorneyEmail?: string | null;
  dateOfEvidenceAffidavit?: string | null;
  dateOfFilingCounterStatement?: string | null;
  dateOfNoticeOfCounterStatement?: string | null;
  skipped?: boolean;
}

export async function savePrepareCounter(
  leadId: string,
  applications: SavePrepareCounterApplicationInput[]
): Promise<PrepareCounterStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/prepare`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ applications }),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<PrepareCounterStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save Prepare Counter"
    );
  }
  return (json as ApiSuccessResponse<PrepareCounterStatus>).data;
}

export async function generatePrepareCounterPdf(
  leadId: string,
  ackApplicationId: string
): Promise<{ key: string; url: string; filename: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/prepare/${ackApplicationId}/generate-pdf`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<GeneratedPdfPayload>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to generate Counter Statement PDF"
    );
  }
  return toGeneratedPdf((json as ApiSuccessResponse<GeneratedPdfPayload>).data);
}

// ───────────────────────────────────────────────────────────────────────────
// Opposition Counter — File Counter (4A(ix)) + Inform Client (4A(x))
// ───────────────────────────────────────────────────────────────────────────

export interface FileCounterApplicationRow {
  acknowledgementId: string;
  tmNo: string;
  opponentName?: string | null;
  opponentCountry?: string | null;
  classOfOpponentsMark?: string | null;
  goodsServicesOfOpponentsMark?: string | null;
  filingDate?: string | null;
  lastDateOfFilingCounter?: string | null;
  pdfUploaded: boolean;
}

export interface FileCounterStatus {
  acknowledgementId: string | null;
  applications: FileCounterApplicationRow[];
}

export async function getFileCounterStatus(
  leadId: string
): Promise<FileCounterStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/file-counter`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<FileCounterApplicationRow[]>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load File Counter status"
    );
  }
  const rows =
    (json as ApiSuccessResponse<FileCounterApplicationRow[]>).data ?? [];
  return {
    acknowledgementId: rows[0]?.acknowledgementId ?? null,
    applications: rows,
  };
}

export interface FileCounterPresignedUrl {
  tmNo: string;
  fileKey: string;
  presignedUrl: string;
}

export async function generateFileCounterPresignedUrls(
  leadId: string,
  acknowledgementId: string,
  files: { tmNo: string; fileName: string; mimeType: string }[]
): Promise<FileCounterPresignedUrl[]> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/file-counter/presigned-urls`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ acknowledgementId, files }),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ urls: FileCounterPresignedUrl[] }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to generate Counter Statement upload URLs"
    );
  }
  return (json as ApiSuccessResponse<{ urls: FileCounterPresignedUrl[] }>).data
    .urls;
}

export interface SaveFileCounterApplicationInput {
  tmNo: string;
  filingDate?: string;
  counterStatementPdfKey?: string | null;
  counterStatementPdfFilename?: string | null;
}

export async function saveFileCounter(
  leadId: string,
  acknowledgementId: string,
  applications: SaveFileCounterApplicationInput[]
): Promise<FileCounterStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/file-counter`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ acknowledgementId, applications }),
    }
  );
  if (!res.ok) {
    const json = (await res.json().catch(() => null)) as
      | ApiErrorResponse
      | null;
    throw new Error(
      json?.message || json?.error || "Failed to save File Counter"
    );
  }
  // PATCH returns only `{ success: true, message }` — re-fetch GET to return
  // the latest list of opposed applications so the caller has fresh state.
  return getFileCounterStatus(leadId);
}

export async function getFileCounterInformClientPreview(
  leadId: string,
  tmNos?: string[]
): Promise<{ message: string }> {
  const qs = tmNos?.length
    ? `?${tmNos.map((t) => `tmNos=${encodeURIComponent(t)}`).join("&")}`
    : "";
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/file-counter/inform-client/preview${qs}`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ message: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Inform Client preview"
    );
  }
  return (json as ApiSuccessResponse<{ message: string }>).data;
}

export async function sendFileCounterInformClientWhatsApp(
  leadId: string,
  tmNos?: string[]
): Promise<{ waLink?: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/file-counter/inform-client/send-whatsapp`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink?: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to send Inform Client about Filing of Counter"
    );
  }
  return (json as ApiSuccessResponse<{ waLink?: string }>).data;
}

// ───────────────────────────────────────────────────────────────────────────
// Opposition Counter — Notice of Counter by Registry (4A(xi)) + Inform (4A(xii))
// ───────────────────────────────────────────────────────────────────────────

/**
 * Per-row data for Notice of Counter — same shape the Prepare Counter GET
 * returns (4v Date of Notice of Counter Statement + 4w Last Date for Evidence
 * by Opponent are computed/displayed there too).
 */
export type NoticeOfCounterRow = PrepareCounterApplicationItem;

export async function getNoticeOfCounter(
  leadId: string
): Promise<NoticeOfCounterRow[]> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/notice`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<NoticeOfCounterRow[]>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Notice of Counter status"
    );
  }
  return (json as ApiSuccessResponse<NoticeOfCounterRow[]>).data ?? [];
}

export interface SaveNoticeOfCounterApplicationInput {
  tmNo: string;
  noticeDate?: string | null;
  lastDate?: string | null;
}

export async function saveNoticeOfCounter(
  leadId: string,
  acknowledgementId: string,
  applications: SaveNoticeOfCounterApplicationInput[]
): Promise<void> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/notice`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ acknowledgementId, applications }),
    }
  );
  if (!res.ok) {
    const json = (await res.json().catch(() => null)) as
      | ApiErrorResponse
      | null;
    throw new Error(
      json?.message ||
        json?.error ||
        "Failed to save Notice of Counter Statement date"
    );
  }
}

export interface InformClientNoticeOfCounterPreview {
  message: string;
  applicationCount: number;
  tmNos: string[];
}

export async function getInformClientNoticeOfCounterPreview(
  leadId: string,
  tmNos?: string[]
): Promise<InformClientNoticeOfCounterPreview> {
  const params = new URLSearchParams();
  if (tmNos?.length) {
    for (const t of tmNos) params.append("tmNos", t);
  }
  const qs = params.toString() ? `?${params.toString()}` : "";
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/inform-client-notice-of-counter/preview${qs}`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<InformClientNoticeOfCounterPreview>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Inform Client about Notice of Counter preview"
    );
  }
  return (json as ApiSuccessResponse<InformClientNoticeOfCounterPreview>).data;
}

export async function sendInformClientNoticeOfCounterWhatsApp(
  leadId: string,
  tmNos?: string[]
): Promise<{ waLink?: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/inform-client-notice-of-counter/send-whatsapp`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink?: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to send Inform Client about Notice of Counter"
    );
  }
  return (json as ApiSuccessResponse<{ waLink?: string }>).data;
}

// ───────────────────────────────────────────────────────────────────────────
// Opposition Counter — Status details for Evidence by Opponent (4A(xiii))
// ───────────────────────────────────────────────────────────────────────────

export interface EvidenceByOpponentApplicationRow {
  ackApplicationId: string;
  tmNo: string;
  mark: string;
  lastDateForEvidenceByOpponent: string | null;
  opponentEvidenceFiled: boolean | null;
  dateOfEvidenceByOpponent: string | null;
  dateOfReceiptOfEvidenceByOpponent: string | null;
  lastDateOfFilingEvidence: string | null;
  evidenceByOpponentFileKey: string | null;
  evidenceByOpponentFilename: string | null;
  evidenceByOpponentFileUrl: string | null;
}

export interface EvidenceByOpponentStatus {
  acknowledgementNo: string | null;
  applications: EvidenceByOpponentApplicationRow[];
}

export async function getEvidenceByOpponentStatus(
  leadId: string
): Promise<EvidenceByOpponentStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/evidence-by-opponent/status-details`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<EvidenceByOpponentStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Evidence by Opponent status"
    );
  }
  return (json as ApiSuccessResponse<EvidenceByOpponentStatus>).data;
}

export interface SaveEvidenceByOpponentApplicationInput {
  tmNo: string;
  opponentEvidenceFiled?: boolean | null;
  dateOfEvidenceByOpponent?: string | null;
  dateOfReceiptOfEvidenceByOpponent?: string | null;
  lastDateOfFilingEvidence?: string | null;
  evidenceByOpponentFileKey?: string | null;
  evidenceByOpponentFilename?: string | null;
}

export async function saveEvidenceByOpponentStatus(
  leadId: string,
  applications: SaveEvidenceByOpponentApplicationInput[]
): Promise<void> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/evidence-by-opponent/status-details`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ applications }),
    }
  );
  if (!res.ok) {
    const json = (await res.json().catch(() => null)) as
      | ApiErrorResponse
      | null;
    throw new Error(
      json?.message ||
        json?.error ||
        "Failed to save Evidence by Opponent status"
    );
  }
}

// ───────────────────────────────────────────────────────────────────────────
// Opposition Counter — Notice to Client for Evidence by Opponent (4A(xiv))
// ───────────────────────────────────────────────────────────────────────────

export type EvidenceByOpponentNoticePreview = {
  recipientPhone: string | null;
  recipientEmail: string | null;
  applicationNumbers: string;
  filename: string;
  apps: { tmNo: string; mark: string | null; filename: string | null }[];
  message: string;
  emailPreview: { to: string | null; subject: string; body: string };
};

export type EvidenceByOpponentNoticeSendResult = {
  waLink?: string;
  email?: string;
  filename: string;
  errors?: { whatsapp?: string; email?: string };
};

export async function getEvidenceByOpponentNoticeToClientPreview(
  leadId: string,
  tmNos?: string[]
): Promise<EvidenceByOpponentNoticePreview> {
  const qs = tmNos?.length
    ? `?${tmNos.map((t) => `tmNos=${encodeURIComponent(t)}`).join("&")}`
    : "";
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/evidence-by-opponent/send-notice-to-client/preview${qs}`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<EvidenceByOpponentNoticePreview>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Evidence by Opponent notice preview"
    );
  }
  return (json as ApiSuccessResponse<EvidenceByOpponentNoticePreview>).data;
}

export async function sendEvidenceByOpponentNoticeToClient(
  leadId: string,
  tmNos?: string[],
  channels?: { sendWhatsApp?: boolean; sendEmail?: boolean }
): Promise<EvidenceByOpponentNoticeSendResult> {
  const body: {
    tmNos?: string[];
    sendWhatsApp?: boolean;
    sendEmail?: boolean;
  } = {};
  if (tmNos?.length) body.tmNos = tmNos;
  if (channels?.sendWhatsApp !== undefined) body.sendWhatsApp = channels.sendWhatsApp;
  if (channels?.sendEmail !== undefined) body.sendEmail = channels.sendEmail;
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/evidence-by-opponent/send-notice-to-client`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<EvidenceByOpponentNoticeSendResult>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to forward Evidence by Opponent to client"
    );
  }
  return (json as ApiSuccessResponse<EvidenceByOpponentNoticeSendResult>).data;
}

// ───────────────────────────────────────────────────────────────────────────
// Opposition Counter — Inform Client about Evidence by Opponent (4A(xv))
// ───────────────────────────────────────────────────────────────────────────

export interface InformClientEvidenceByOpponentPreview {
  message: string;
  applicationCount: number;
  opponentEvidenceFiled: boolean;
  tmNumbers: string[];
  lastDateOfFilingEvidence: string | null;
  usageFromDate: string | null;
}

export async function getInformClientEvidenceByOpponentPreview(
  leadId: string,
  tmNos?: string[]
): Promise<InformClientEvidenceByOpponentPreview> {
  const params = new URLSearchParams();
  if (tmNos?.length) {
    for (const t of tmNos) params.append("tmNos", t);
  }
  const qs = params.toString() ? `?${params.toString()}` : "";
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/evidence-by-opponent/inform-client/preview${qs}`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<InformClientEvidenceByOpponentPreview>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Inform Client about Evidence by Opponent preview"
    );
  }
  return (json as ApiSuccessResponse<InformClientEvidenceByOpponentPreview>)
    .data;
}

export async function sendInformClientEvidenceByOpponentWhatsApp(
  leadId: string,
  tmNos?: string[]
): Promise<{ waLink?: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/evidence-by-opponent/inform-client/send-whatsapp`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink?: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to send Inform Client about Evidence by Opponent"
    );
  }
  return (json as ApiSuccessResponse<{ waLink?: string }>).data;
}

// ───────────────────────────────────────────────────────────────────────────
// Opposition Counter — Call Client for Evidence (4A(xvi))
// ───────────────────────────────────────────────────────────────────────────

export async function recordCallClientForEvidence(
  leadId: string,
  tmNos?: string[]
): Promise<void> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/record-call-client-evidence`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  if (!res.ok) {
    const json = (await res.json().catch(() => null)) as
      | ApiErrorResponse
      | null;
    throw new Error(
      json?.message ||
        json?.error ||
        "Failed to record Call Client for Evidence"
    );
  }
}

// ───────────────────────────────────────────────────────────────────────────
// Opposition Counter — Confirmation from Client for Evidence (4A(xvii))
// ───────────────────────────────────────────────────────────────────────────

export interface EvidenceConfirmationApplicationRow {
  tmNo: string;
  mark: string;
  clientGoingAhead: boolean | null;
}

export interface EvidenceConfirmationStatus {
  clientGoingAhead: boolean | null;
  applications: EvidenceConfirmationApplicationRow[];
  noOfApplicationsOpposed: number;
  noOfApplicationsForCounter: number;
  noOfApplicationsForEvidence: number;
  acknowledgementNo: string | null;
  /** Returned when all picks are NO — text hint about the next stage (Abandonment / Non Filing of Evidence Hearing). */
  suggestedNextStepWhenNo?: string;
}

export async function getEvidenceConfirmationFromClient(
  leadId: string
): Promise<EvidenceConfirmationStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/confirmation-from-client-evidence`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<EvidenceConfirmationStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Confirmation from Client for Evidence"
    );
  }
  return (json as ApiSuccessResponse<EvidenceConfirmationStatus>).data;
}

export async function saveEvidenceConfirmationFromClient(
  leadId: string,
  applications: { tmNo: string; clientGoingAhead: boolean }[],
  noOfApplicationsForEvidence?: number
): Promise<EvidenceConfirmationStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/confirmation-from-client-evidence`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        applications,
        ...(noOfApplicationsForEvidence !== undefined
          ? { noOfApplicationsForEvidence }
          : {}),
      }),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<EvidenceConfirmationStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save Confirmation from Client for Evidence"
    );
  }
  return (json as ApiSuccessResponse<EvidenceConfirmationStatus>).data;
}

// ───────────────────────────────────────────────────────────────────────────
// Opposition Counter — Invoice for Evidence (4A(xviii))
// ───────────────────────────────────────────────────────────────────────────

export interface EvidenceInvoiceTmRow {
  tmNo: string;
  defaultInvoiceAmount: number;
  invoiceAmount: number;
  discount: number;
  individualInvoiceNo: string | null;
  includeInvoiceNo: boolean;
  invoiceDate: string | null;
  collectedAmount: number | null;
  outstandingAmount: number | null;
  noOfExhibitsForEvidence: number | null;
}

export interface EvidenceInvoiceStatus {
  noOfApps: number;
  invoiceAmount: number | null;
  professionalCharges: number | null;
  discount: number | null;
  invoiceNo: string | null;
  invoiceDate: string | null;
  includeInvoiceNo: boolean;
  collectedAmount: number | null;
  outstandingAmount: number | null;
  evidenceInvoicePdfKey: string | null;
  evidenceInvoicePdfUrl: string | null;
  evidenceInvoiceGeneratedPdfId: string | null;
  tmInvoiceRows: EvidenceInvoiceTmRow[];
}

export interface EvidenceInvoicePreviewApplication {
  tmNo: string;
  applicationDate: string;
  mark: string;
  typeOfMark: string;
  classNumber: number;
  nameOfOpponent: string | null;
  placeOfOpponent: string | null;
}

export interface EvidenceInvoicePreview {
  dataLabel: string;
  addressee: string;
  addresseeLine1: string | null;
  addresseeLine2: string | null;
  addresseeLine3: string | null;
  formOfAddress: string | null;
  acknowledgementNo: string | null;
  dateOfFiling: string | null;
  mark: string | null;
  typeOfMark: string | null;
  classNumber: number | null;
  applications: EvidenceInvoicePreviewApplication[];
  noOfApplicationsForEvidence: number;
  lastDateOfFilingEvidence: string | null;
  professionalCharges: number;
  discount: number;
  invoiceAmount: number;
  invoiceDate: string | null;
  invoiceNo: string | null;
  includeInvoiceNo: boolean;
  tmInvoiceRows?: EvidenceInvoiceTmRow[];
}

export async function getEvidenceInvoice(
  leadId: string
): Promise<EvidenceInvoiceStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/evidence-invoice`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<EvidenceInvoiceStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Evidence Invoice"
    );
  }
  return (json as ApiSuccessResponse<EvidenceInvoiceStatus>).data;
}

export async function getEvidenceInvoiceWhatsAppPreview(
  leadId: string,
  tmNos?: string[]
): Promise<{
  pdfMessage: string;
  paymentMessage: string;
  emailPreview: {
    to: string | null;
    subject: string;
    body: string;
    filename: string | null;
    downloadUrl: string | null;
  };
}> {
  const params = new URLSearchParams();
  if (tmNos?.length) tmNos.forEach((t) => params.append("tmNos", t));
  const query = params.toString() ? `?${params.toString()}` : "";
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/evidence-invoice/whatsapp-preview${query}`,
    { method: "GET" }
  );
  type Payload = {
    pdfMessage: string;
    paymentMessage: string;
    emailPreview: {
      to: string | null;
      subject: string;
      body: string;
      filename: string | null;
      downloadUrl: string | null;
    };
  };
  const json = (await res.json()) as
    | ApiSuccessResponse<Payload>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Evidence Invoice WhatsApp preview"
    );
  }
  return (json as ApiSuccessResponse<Payload>).data;
}

export async function getEvidenceInvoicePreview(
  leadId: string
): Promise<EvidenceInvoicePreview | null> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/evidence-invoice/preview`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<EvidenceInvoicePreview | null>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Evidence Invoice preview"
    );
  }
  return (json as ApiSuccessResponse<EvidenceInvoicePreview | null>).data;
}

export interface SaveEvidenceInvoiceApplicationInput {
  tmNo: string;
  invoiceAmount: number;
  discount?: number;
  invoiceDate?: string;
  includeInvoiceNo?: boolean;
}

export async function saveEvidenceInvoice(
  leadId: string,
  applications: SaveEvidenceInvoiceApplicationInput[]
): Promise<EvidenceInvoiceStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/evidence-invoice`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ applications }),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<EvidenceInvoiceStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save Evidence Invoice"
    );
  }
  return (json as ApiSuccessResponse<EvidenceInvoiceStatus>).data;
}

export async function generateEvidenceInvoicePdf(
  leadId: string,
  tmNos?: string[]
): Promise<{ key: string; url: string; filename: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/evidence-invoice/generate-pdf`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<GeneratedPdfPayload>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to generate Evidence Invoice PDF"
    );
  }
  return toGeneratedPdf((json as ApiSuccessResponse<GeneratedPdfPayload>).data);
}

export async function sendEvidenceInvoiceToClient(
  leadId: string,
  options?: { tmNos?: string[]; sendWhatsApp?: boolean; sendEmail?: boolean }
): Promise<{ waLink?: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/evidence-invoice/send-to-client`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        sendWhatsApp: options?.sendWhatsApp ?? true,
        sendEmail: options?.sendEmail ?? false,
        ...(options?.tmNos?.length ? { tmNos: options.tmNos } : {}),
      }),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink?: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to send Evidence Invoice to client"
    );
  }
  return (json as ApiSuccessResponse<{ waLink?: string }>).data;
}

export async function sendEvidenceInvoicePaymentMessage(
  leadId: string,
  tmNos?: string[]
): Promise<{ waLink?: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/evidence-invoice/send-payment-message`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink?: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to send Evidence payment message"
    );
  }
  return (json as ApiSuccessResponse<{ waLink?: string }>).data;
}

// ───────────────────────────────────────────────────────────────────────────
// Opposition Counter — Payment from Client for Evidence (4A(xix))
// ───────────────────────────────────────────────────────────────────────────

export async function saveEvidenceCollectedAmount(
  leadId: string,
  applications: { tmNo: string; collectedAmount: number }[]
): Promise<EvidenceInvoiceStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/evidence-collected-amount`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ applications }),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<EvidenceInvoiceStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save Evidence Collected Amount"
    );
  }
  return (json as ApiSuccessResponse<EvidenceInvoiceStatus>).data;
}

// ───────────────────────────────────────────────────────────────────────────
// Opposition Counter — Stamp Paper details to Vendor for Evidence (4A(xx))
// ───────────────────────────────────────────────────────────────────────────

export interface StampPaperForEvidenceTmRow {
  tmNo: string;
  stampPapersForEvidence: number | null;
  defaultAmountPayableToStampVendorForEvidence: number | null;
  amountPayableToStampVendorForEvidence: number | null;
  paidToVendorForEvidence: boolean | null;
  paymentStatusDisplay: string;
  stampVendorNameForEvidence: string | null;
  stampVendorPhoneForEvidence: string | null;
}

export interface StampPaperForEvidenceStatus {
  isActive: boolean;
  applications?: StampPaperForEvidenceTmRow[];
  stampPapersForEvidence: number | null;
  amountPayableToStampVendorForEvidence: number | null;
  applicant: string | null;
  applicantAddress: string | null;
  applicantType: string | null;
  userDate: string | null;
  signatoryName: string | null;
  signatoryDesignation: string | null;
}

export async function getStampPaperForEvidenceStatus(
  leadId: string
): Promise<StampPaperForEvidenceStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/stamp-paper-for-vendor`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<StampPaperForEvidenceStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Stamp Paper details to Vendor for Evidence"
    );
  }
  return (json as ApiSuccessResponse<StampPaperForEvidenceStatus>).data;
}

export interface SaveStampPaperForEvidenceApplicationInput {
  tmNo: string;
  stampPapersForEvidence: number;
  stampVendorNameForEvidence: string;
  stampVendorPhoneForEvidence: string;
}

export async function saveStampPaperForEvidenceDetails(
  leadId: string,
  applications: SaveStampPaperForEvidenceApplicationInput[]
): Promise<StampPaperForEvidenceStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/stamp-paper-for-vendor`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ applications }),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<StampPaperForEvidenceStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save Stamp Paper details to Vendor for Evidence"
    );
  }
  return (json as ApiSuccessResponse<StampPaperForEvidenceStatus>).data;
}

export async function getStampPaperForEvidenceMessagePreview(
  leadId: string,
  tmNos?: string[]
): Promise<{ message: string }> {
  const params = tmNos?.length
    ? `?${tmNos.map((t) => `tmNos=${encodeURIComponent(t)}`).join("&")}`
    : "";
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/stamp-paper-for-vendor/message-preview${params}`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ message: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Stamp Paper message preview"
    );
  }
  return (json as ApiSuccessResponse<{ message: string }>).data;
}

export async function sendStampPaperForEvidenceToVendor(
  leadId: string,
  tmNos?: string[]
): Promise<{ waLink?: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/stamp-paper-for-vendor/send-whatsapp`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink?: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to send Stamp Paper details to vendor"
    );
  }
  return (json as ApiSuccessResponse<{ waLink?: string }>).data;
}

// ───────────────────────────────────────────────────────────────────────────
// Opposition Counter — Stamp Paper Payment to Vendor for Evidence (4A(xxi))
// ───────────────────────────────────────────────────────────────────────────

export interface SaveStampPaperPaymentForEvidenceItemInput {
  tmNo: string;
  amountPayableToStampVendorForEvidence?: number | null;
  paidToVendorForEvidence: boolean;
}

export async function saveStampPaperPaymentToVendorForEvidence(
  leadId: string,
  items: SaveStampPaperPaymentForEvidenceItemInput[]
): Promise<StampPaperForEvidenceStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/stamp-paper-payment-to-vendor`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items }),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<StampPaperForEvidenceStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save Stamp Paper payment to Vendor for Evidence"
    );
  }
  return (json as ApiSuccessResponse<StampPaperForEvidenceStatus>).data;
}

// ───────────────────────────────────────────────────────────────────────────
// Opposition Counter — Documents from Client for Evidence (4A(xxii))
// ───────────────────────────────────────────────────────────────────────────

export interface DocumentsFromClientForEvidenceTmRow {
  tmNo: string;
  noOfExhibitsForEvidence: number | null;
}

export interface DocumentsFromClientForEvidenceStatus {
  noOfExhibitsForEvidence: number | null;
  applications: DocumentsFromClientForEvidenceTmRow[];
}

export async function getDocumentsFromClientForEvidenceStatus(
  leadId: string
): Promise<DocumentsFromClientForEvidenceStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/documents-from-client-for-evidence`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<DocumentsFromClientForEvidenceStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Documents from Client for Evidence"
    );
  }
  return (json as ApiSuccessResponse<DocumentsFromClientForEvidenceStatus>)
    .data;
}

export interface SaveDocumentsFromClientForEvidenceApplicationInput {
  tmNo: string;
  noOfExhibitsForEvidence: number;
}

export async function saveDocumentsFromClientForEvidence(
  leadId: string,
  applications: SaveDocumentsFromClientForEvidenceApplicationInput[]
): Promise<DocumentsFromClientForEvidenceStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/documents-from-client-for-evidence`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ applications }),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<DocumentsFromClientForEvidenceStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save Documents from Client for Evidence"
    );
  }
  return (json as ApiSuccessResponse<DocumentsFromClientForEvidenceStatus>)
    .data;
}

// ───────────────────────────────────────────────────────────────────────────
// Opposition Counter — Preparation of Evidence Affidavit (4A(xxiii))
// ───────────────────────────────────────────────────────────────────────────

export interface EvidenceAffidavitItem {
  evidenceAffidavitNo: number;
  ackApplicationId: string;
  tmNo: string;
  mark: string;
  applicant: string | null;
  applicantAddress: string | null;
  applicantType: string | null;
  signatoryDetails: string | null;
  userDate: string | null;
  classWithGoodsServices: string | null;
  gsTypeA: string;
  gsTypeB: string;
  gsTypeC: string;
  descriptionOfApplicant: string | null;
  oppositionNoWithDate: string | null;
  nameOfOpponent: string | null;
  addressOfOpponent: string | null;
  opponentsMark: string | null;
  imageOfOpponentsMarkUrl: string | null;
  classOfOpponentsMark: string | null;
  goodsServicesOfOpponentsMark: string | null;
  userDateOfOpponentsMark: string | null;
  noOfExhibitsForEvidence: number | null;
  noOfParagraphsInOpponentIntro: number | null;
  paragraphWiseReplies: Array<{ heading: string; text: string }>;
  nameOfOpponentsAttorney: string | null;
  addressOfOpponentsAttorney: string | null;
  emailOfOpponentsAttorney: string | null;
  dateOfEvidenceAffidavitFormatted: string | null;
  evidenceAffidavitPdfKey: string | null;
  evidenceAffidavitPdfUrl: string | null;
  skipped: boolean;
  applicantTypeVariant: string;
}

export interface EvidenceAffidavitStatus {
  acknowledgementNo: string | null;
  evidenceAffidavits: EvidenceAffidavitItem[];
}

export async function getEvidenceAffidavitStatus(
  leadId: string
): Promise<EvidenceAffidavitStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/evidence-affidavit`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<EvidenceAffidavitStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Evidence Affidavit status"
    );
  }
  return (json as ApiSuccessResponse<EvidenceAffidavitStatus>).data;
}

export interface SaveEvidenceAffidavitApplicationInput {
  tmNo: string;
  noOfParagraphsInOpponentIntro?: number | null;
  paragraphWiseReplies?: Array<{ heading?: string; text?: string }>;
  nameOfOpponentsAttorney?: string | null;
  addressOfOpponentsAttorney?: string | null;
  emailOfOpponentsAttorney?: string | null;
  dateOfEvidenceAffidavit?: string | null;
  skipped?: boolean;
}

export async function saveEvidenceAffidavit(
  leadId: string,
  applications: SaveEvidenceAffidavitApplicationInput[]
): Promise<void> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/evidence-affidavit`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ applications }),
    }
  );
  if (!res.ok) {
    const json = (await res.json().catch(() => null)) as
      | ApiErrorResponse
      | null;
    throw new Error(
      json?.message ||
        json?.error ||
        "Failed to save Evidence Affidavit data"
    );
  }
}

export async function generateEvidenceAffidavitPdf(
  leadId: string,
  ackApplicationId: string
): Promise<{ key: string; url: string; filename: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/evidence-affidavit/${ackApplicationId}/generate-pdf`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: "{}",
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<GeneratedPdfPayload>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to generate Evidence Affidavit PDF"
    );
  }
  return toGeneratedPdf((json as ApiSuccessResponse<GeneratedPdfPayload>).data);
}

// ───────────────────────────────────────────────────────────────────────────
// Opposition Counter — Signature from Client for Affidavit (4A(xxiv))
// ───────────────────────────────────────────────────────────────────────────

export async function recordSignatureFromClientForAffidavit(
  leadId: string,
  tmNos?: string[]
): Promise<void> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/record-signature-from-client-for-affidavit`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  if (!res.ok) {
    const json = (await res.json().catch(() => null)) as
      | ApiErrorResponse
      | null;
    throw new Error(
      json?.message ||
        json?.error ||
        "Failed to record Signature from Client for Affidavit"
    );
  }
}

// ───────────────────────────────────────────────────────────────────────────
// Opposition Counter — Notarisation of Documents for Evidence (4A(xxv))
// ───────────────────────────────────────────────────────────────────────────

export interface NotarisationForEvidenceApplicationRow {
  tmNo: string;
  /** 4zf) Number of Stamp Papers needed for Evidence for this TM */
  stampPapersForEvidence: number | null;
  defaultAmountPayableToNotaryForEvidence: number | null;
  amountPayableToNotaryForEvidence: number | null;
  paidToNotaryForEvidence: boolean | null;
  paymentStatusDisplay: string;
}

export interface NotarisationForEvidenceStatus {
  applications?: NotarisationForEvidenceApplicationRow[];
  /** 4zf) Total across TMs. Per-application count lives on `applications[].stampPapersForEvidence`. */
  stampPapersForEvidence: number | null;
  amountPayableToNotaryForEvidenceAutoCalculated: number | null;
  amountPayableToNotaryForEvidence: number | null;
  paidToNotaryForEvidence: boolean | null;
  paymentStatusDisplay: string;
}

export async function getNotarisationForEvidenceStatus(
  leadId: string
): Promise<NotarisationForEvidenceStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/notarisation-for-evidence`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<NotarisationForEvidenceStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Notarisation for Evidence status"
    );
  }
  return (json as ApiSuccessResponse<NotarisationForEvidenceStatus>).data;
}

export interface SaveNotaryPaymentForEvidenceItemInput {
  tmNo: string;
  amountPayableToNotaryForEvidence?: number | null;
  paidToNotaryForEvidence?: boolean;
}

export async function saveNotaryPaymentForEvidence(
  leadId: string,
  applications: SaveNotaryPaymentForEvidenceItemInput[]
): Promise<NotarisationForEvidenceStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/notarisation-for-evidence`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ applications }),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<NotarisationForEvidenceStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save Notary payment for Evidence"
    );
  }
  return (json as ApiSuccessResponse<NotarisationForEvidenceStatus>).data;
}

// ───────────────────────────────────────────────────────────────────────────
// Opposition Counter — Scanning of Documents for Evidence (4A(xxvi))
// ───────────────────────────────────────────────────────────────────────────

export async function recordScanningOfDocumentsForEvidence(
  leadId: string,
  tmNos?: string[]
): Promise<void> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/record-scanning-documents-for-evidence`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  if (!res.ok) {
    const json = (await res.json().catch(() => null)) as
      | ApiErrorResponse
      | null;
    throw new Error(
      json?.message ||
        json?.error ||
        "Failed to record Scanning of Documents for Evidence"
    );
  }
}

// ───────────────────────────────────────────────────────────────────────────
// Opposition Counter — File Evidence Affidavit (4A(xxvii))
// ───────────────────────────────────────────────────────────────────────────

export interface FileEvidenceAffidavitApplicationRow {
  ackApplicationId: string;
  tmNo: string;
  mark: string | null;
  dateOfFilingEvidenceAffidavit: string | null;
  lastDateForRejoinderByOpponent: string | null;
  filedPdfUploaded: boolean;
  filedEvidenceAffidavitPdfKey: string | null;
  filedEvidenceAffidavitPdfUrl: string | null;
}

export interface FileEvidenceAffidavitStatus {
  acknowledgementId: string | null;
  acknowledgementNo: string | null;
  applications: FileEvidenceAffidavitApplicationRow[];
}

export async function getFileEvidenceAffidavitStatus(
  leadId: string
): Promise<FileEvidenceAffidavitStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/file-evidence-affidavit`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<FileEvidenceAffidavitStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load File Evidence Affidavit status"
    );
  }
  return (json as ApiSuccessResponse<FileEvidenceAffidavitStatus>).data;
}

export interface SaveFileEvidenceAffidavitApplicationInput {
  tmNo: string;
  dateOfFilingEvidenceAffidavit?: string | null;
  lastDateForRejoinderByOpponent?: string | null;
  filedEvidenceAffidavitPdfKey?: string | null;
}

export async function saveFileEvidenceAffidavit(
  leadId: string,
  acknowledgementId: string,
  applications: SaveFileEvidenceAffidavitApplicationInput[]
): Promise<void> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/file-evidence-affidavit`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ acknowledgementId, applications }),
    }
  );
  if (!res.ok) {
    const json = (await res.json().catch(() => null)) as
      | ApiErrorResponse
      | null;
    throw new Error(
      json?.message ||
        json?.error ||
        "Failed to save File Evidence Affidavit"
    );
  }
}

export interface FiledEvidenceAffidavitPresignedUrlItem {
  tmNo: string;
  presignedUrl: string;
  fileKey: string;
  mimeType: string;
}

export interface GeneratePresignedUrlsForFiledEvidenceAffidavitInput {
  tmNo: string;
  fileName: string;
  mimeType: string;
}

export async function generateFiledEvidenceAffidavitPresignedUrls(
  leadId: string,
  acknowledgementId: string,
  files: GeneratePresignedUrlsForFiledEvidenceAffidavitInput[]
): Promise<{ urls: FiledEvidenceAffidavitPresignedUrlItem[] }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/file-evidence-affidavit/presigned-urls`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ acknowledgementId, files }),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ urls: FiledEvidenceAffidavitPresignedUrlItem[] }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to generate filed Evidence Affidavit upload URLs"
    );
  }
  return (
    json as ApiSuccessResponse<{
      urls: FiledEvidenceAffidavitPresignedUrlItem[];
    }>
  ).data;
}

// ───────────────────────────────────────────────────────────────────────────
// Opposition Counter — Inform Client about Filing of Evidence (4A(xxviii))
// ───────────────────────────────────────────────────────────────────────────

export interface InformClientFilingOfEvidencePreview {
  message: string;
  applicationCount: number;
  tmNumbers: string[];
}

export async function getInformClientFilingOfEvidencePreview(
  leadId: string,
  tmNos?: string[]
): Promise<InformClientFilingOfEvidencePreview> {
  const params = new URLSearchParams();
  if (tmNos?.length) {
    for (const t of tmNos) params.append("tmNos", t);
  }
  const qs = params.toString() ? `?${params.toString()}` : "";
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/inform-client-filing-of-evidence/preview${qs}`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<InformClientFilingOfEvidencePreview>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Inform Client about Filing of Evidence preview"
    );
  }
  return (json as ApiSuccessResponse<InformClientFilingOfEvidencePreview>)
    .data;
}

export async function sendInformClientFilingOfEvidenceWhatsApp(
  leadId: string,
  tmNos?: string[]
): Promise<{ waLink?: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/inform-client-filing-of-evidence/send-whatsapp`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink?: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to send Inform Client about Filing of Evidence"
    );
  }
  return (json as ApiSuccessResponse<{ waLink?: string }>).data;
}

// ───────────────────────────────────────────────────────────────────────────
// Opposition Counter — Download Rejoinder Affidavit (4A(xxix))
// ───────────────────────────────────────────────────────────────────────────

export interface DownloadRejoinderAffidavitApplicationRow {
  ackApplicationId: string;
  tmNo: string;
  mark: string | null;
  dateOfRejoinderByOpponent: string | null;
  rejoinderPdfUploaded: boolean;
  rejoinderAffidavitPdfKey: string | null;
  rejoinderAffidavitPdfUrl: string | null;
}

export interface DownloadRejoinderAffidavitStatus {
  acknowledgementId: string | null;
  acknowledgementNo: string | null;
  applications: DownloadRejoinderAffidavitApplicationRow[];
}

export async function getDownloadRejoinderAffidavitStatus(
  leadId: string
): Promise<DownloadRejoinderAffidavitStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/download-rejoinder-affidavit`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<DownloadRejoinderAffidavitStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Download Rejoinder Affidavit status"
    );
  }
  return (json as ApiSuccessResponse<DownloadRejoinderAffidavitStatus>).data;
}

export interface SaveDownloadRejoinderAffidavitApplicationInput {
  tmNo: string;
  dateOfRejoinderByOpponent?: string | null;
  rejoinderAffidavitPdfKey?: string | null;
}

export async function saveDownloadRejoinderAffidavit(
  leadId: string,
  acknowledgementId: string,
  applications: SaveDownloadRejoinderAffidavitApplicationInput[]
): Promise<void> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/download-rejoinder-affidavit`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ acknowledgementId, applications }),
    }
  );
  if (!res.ok) {
    const json = (await res.json().catch(() => null)) as
      | ApiErrorResponse
      | null;
    throw new Error(
      json?.message ||
        json?.error ||
        "Failed to save Download Rejoinder Affidavit"
    );
  }
}

export interface RejoinderAffidavitPresignedUrlItem {
  tmNo: string;
  presignedUrl: string;
  fileKey: string;
  mimeType: string;
}

export interface GeneratePresignedUrlsForRejoinderAffidavitInput {
  tmNo: string;
  fileName: string;
  mimeType: string;
}

export async function generateRejoinderAffidavitPresignedUrls(
  leadId: string,
  acknowledgementId: string,
  files: GeneratePresignedUrlsForRejoinderAffidavitInput[]
): Promise<{ urls: RejoinderAffidavitPresignedUrlItem[] }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/download-rejoinder-affidavit/presigned-urls`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ acknowledgementId, files }),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ urls: RejoinderAffidavitPresignedUrlItem[] }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to generate Rejoinder Affidavit upload URLs"
    );
  }
  return (
    json as ApiSuccessResponse<{
      urls: RejoinderAffidavitPresignedUrlItem[];
    }>
  ).data;
}

// ───────────────────────────────────────────────────────────────────────────
// Opposition Counter — Status details of Opposition Hearing (4A(xxx))
// ───────────────────────────────────────────────────────────────────────────

export interface OppositionHearingStatusApplicationRow {
  tmNo: string;
  mark: string;
  typeOfMark: string;
  classNumber: number;
  applicationDate: string;
  dateOfOppositionHearing: string | null;
  oppositionHearingNoticeFileKey: string | null;
  oppositionHearingNoticeFilename: string | null;
  oppositionHearingNoticeFileUrl: string | null;
}

export interface OppositionHearingStatus {
  acknowledgementNo: string | null;
  dateOfFiling: string | null;
  applications: OppositionHearingStatusApplicationRow[];
  noOfApplicationsForOppositionHearing: number;
}

export async function getOppositionHearingStatus(
  leadId: string
): Promise<OppositionHearingStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/opposition-hearing/status-details`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<OppositionHearingStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Opposition Hearing status"
    );
  }
  return (json as ApiSuccessResponse<OppositionHearingStatus>).data;
}

export interface SaveOppositionHearingApplicationInput {
  tmNo: string;
  dateOfOppositionHearing?: string | null;
  oppositionHearingNoticeFileKey?: string | null;
  oppositionHearingNoticeFilename?: string | null;
}

export async function setOppositionHearingStatusDetails(
  leadId: string,
  applications: SaveOppositionHearingApplicationInput[]
): Promise<OppositionHearingStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/opposition-hearing/status-details`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ applications }),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<OppositionHearingStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save Opposition Hearing status details"
    );
  }
  return (json as ApiSuccessResponse<OppositionHearingStatus>).data;
}

// ───────────────────────────────────────────────────────────────────────────
// Opposition Counter — Notice to Client for Opposition Hearing (4A(xxxi))
// ───────────────────────────────────────────────────────────────────────────

export type OppositionHearingNoticePreview = {
  recipientPhone: string | null;
  recipientEmail: string | null;
  filename: string;
  apps: { tmNo: string; mark: string | null; filename: string | null }[];
  message: string;
  emailPreview: { to: string | null; subject: string; body: string };
};

export type OppositionHearingNoticeSendResult = {
  waLink?: string;
  email?: string;
  filename: string;
  errors?: { whatsapp?: string; email?: string };
};

export async function getOppositionHearingNoticeToClientPreview(
  leadId: string,
  tmNos?: string[]
): Promise<OppositionHearingNoticePreview> {
  const qs = tmNos?.length
    ? `?${tmNos.map((t) => `tmNos=${encodeURIComponent(t)}`).join("&")}`
    : "";
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/opposition-hearing/send-notice-to-client/preview${qs}`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<OppositionHearingNoticePreview>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Opposition Hearing notice preview"
    );
  }
  return (json as ApiSuccessResponse<OppositionHearingNoticePreview>).data;
}

export async function sendOppositionHearingNoticeToClient(
  leadId: string,
  tmNos?: string[],
  channels?: { sendWhatsApp?: boolean; sendEmail?: boolean }
): Promise<OppositionHearingNoticeSendResult> {
  const body: {
    tmNos?: string[];
    sendWhatsApp?: boolean;
    sendEmail?: boolean;
  } = {};
  if (tmNos?.length) body.tmNos = tmNos;
  if (channels?.sendWhatsApp !== undefined) body.sendWhatsApp = channels.sendWhatsApp;
  if (channels?.sendEmail !== undefined) body.sendEmail = channels.sendEmail;
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/opposition-hearing/send-notice-to-client`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<OppositionHearingNoticeSendResult>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to send Opposition Hearing Notice to client"
    );
  }
  return (json as ApiSuccessResponse<OppositionHearingNoticeSendResult>).data;
}

// ───────────────────────────────────────────────────────────────────────────
// Opposition Counter — Inform Client about Opposition Hearing (4A(xxxii))
// ───────────────────────────────────────────────────────────────────────────

export interface InformClientOppositionHearingPreview {
  message: string;
  applicationCount: number;
  tmNumbers: string[];
}

export async function getInformClientOppositionHearingPreview(
  leadId: string,
  tmNos?: string[]
): Promise<InformClientOppositionHearingPreview> {
  const params = new URLSearchParams();
  if (tmNos?.length) {
    for (const t of tmNos) params.append("tmNos", t);
  }
  const qs = params.toString() ? `?${params.toString()}` : "";
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/opposition-hearing/inform-client-preview${qs}`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<InformClientOppositionHearingPreview>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Inform Client about Opposition Hearing preview"
    );
  }
  return (json as ApiSuccessResponse<InformClientOppositionHearingPreview>)
    .data;
}

export async function sendInformClientOppositionHearing(
  leadId: string,
  tmNos?: string[]
): Promise<{ waLink?: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/opposition-hearing/inform-client`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink?: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to send Inform Client about Opposition Hearing"
    );
  }
  return (json as ApiSuccessResponse<{ waLink?: string }>).data;
}

// ───────────────────────────────────────────────────────────────────────────
// Opposition Counter — Invoice to Client for Opposition Hearing (4A(xxxiii))
// ───────────────────────────────────────────────────────────────────────────

export interface OppositionHearingInvoiceTmRow {
  tmNo: string;
  defaultInvoiceAmount: number;
  invoiceAmount: number;
  discount: number;
  individualInvoiceNo: string | null;
  includeInvoiceNo: boolean;
  invoiceDate: string | null;
  collectedAmount: number | null;
  outstandingAmount: number | null;
}

export interface OppositionHearingInvoiceStatus {
  noOfApps: number;
  invoiceAmount: number | null;
  professionalCharges: number | null;
  professionalChargesPerApp: number | null;
  discount: number | null;
  invoiceNo: string | null;
  invoiceDate: string | null;
  includeInvoiceNo: boolean;
  collectedAmount: number | null;
  outstandingAmount: number | null;
  oppositionHearingInvoicePdfKey: string | null;
  oppositionHearingInvoicePdfUrl: string | null;
  tmInvoiceRows: OppositionHearingInvoiceTmRow[];
}

export interface OppositionHearingInvoicePreviewApplication {
  tmNo: string;
  applicationDate: string;
  mark: string;
  typeOfMark: string;
  classNumber: number;
  hearingDate: string | null;
  invoiceAmount: number;
  invoiceDate: string | null;
}

export interface OppositionHearingInvoicePreview {
  dataLabel: string;
  addressee: string;
  addresseeLine1: string | null;
  addresseeLine2: string | null;
  addresseeLine3: string | null;
  formOfAddress: string | null;
  acknowledgementNo: string | null;
  dateOfFiling: string | null;
  mark: string | null;
  typeOfMark: string | null;
  classNumber: number | null;
  applications: OppositionHearingInvoicePreviewApplication[];
  noOfApplicationsForOppositionHearing: number;
  professionalChargesPerApp: number;
  professionalCharges: number;
  discount: number;
  invoiceAmount: number;
  invoiceDate: string | null;
  invoiceNo: string | null;
  includeInvoiceNo: boolean;
}

export async function getOppositionHearingInvoiceStatus(
  leadId: string
): Promise<OppositionHearingInvoiceStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/opposition-hearing-invoice`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<OppositionHearingInvoiceStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Opposition Hearing Invoice status"
    );
  }
  return (json as ApiSuccessResponse<OppositionHearingInvoiceStatus>).data;
}

export async function getOppositionHearingInvoicePreview(
  leadId: string
): Promise<OppositionHearingInvoicePreview | null> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/opposition-hearing-invoice/preview`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<OppositionHearingInvoicePreview | null>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Opposition Hearing Invoice preview"
    );
  }
  return (json as ApiSuccessResponse<OppositionHearingInvoicePreview | null>)
    .data;
}

export interface SaveOppositionHearingInvoiceApplicationInput {
  tmNo: string;
  invoiceAmount: number;
  invoiceDate: string;
  includeInvoiceNo: boolean;
}

export async function saveOppositionHearingInvoice(
  leadId: string,
  applications: SaveOppositionHearingInvoiceApplicationInput[]
): Promise<OppositionHearingInvoiceStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/opposition-hearing-invoice`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ applications }),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<OppositionHearingInvoiceStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save Opposition Hearing Invoice"
    );
  }
  return (json as ApiSuccessResponse<OppositionHearingInvoiceStatus>).data;
}

export async function generateOppositionHearingInvoicePdf(
  leadId: string,
  tmNos?: string[]
): Promise<{ key: string; url: string; filename: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/opposition-hearing-invoice/generate-pdf`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<GeneratedPdfPayload>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to generate Opposition Hearing Invoice PDF"
    );
  }
  return toGeneratedPdf((json as ApiSuccessResponse<GeneratedPdfPayload>).data);
}

export async function sendOppositionHearingInvoiceToClient(
  leadId: string,
  options?: { tmNos?: string[]; sendWhatsApp?: boolean; sendEmail?: boolean }
): Promise<{ waLink?: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/opposition-hearing-invoice/send-to-client`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        sendWhatsApp: options?.sendWhatsApp ?? true,
        sendEmail: options?.sendEmail ?? false,
        ...(options?.tmNos?.length ? { tmNos: options.tmNos } : {}),
      }),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink?: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to send Opposition Hearing Invoice to client"
    );
  }
  return (json as ApiSuccessResponse<{ waLink?: string }>).data;
}

export interface OppositionHearingInvoiceEmailPreview {
  to: string | null;
  subject: string;
  body: string;
  filename: string | null;
  downloadUrl: string | null;
}

export interface OppositionHearingInvoiceWhatsAppPreview {
  pdfMessage: string;
  paymentMessage: string;
  emailPreview: OppositionHearingInvoiceEmailPreview;
}

export async function getOppositionHearingInvoiceWhatsAppPreview(
  leadId: string,
  tmNos?: string[]
): Promise<OppositionHearingInvoiceWhatsAppPreview> {
  const params = new URLSearchParams();
  if (tmNos?.length) {
    for (const tmNo of tmNos) params.append("tmNos", tmNo);
  }
  const qs = params.toString();
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/opposition-hearing-invoice/whatsapp-preview${
      qs ? `?${qs}` : ""
    }`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<OppositionHearingInvoiceWhatsAppPreview>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Opposition Hearing Invoice message preview"
    );
  }
  return (json as ApiSuccessResponse<OppositionHearingInvoiceWhatsAppPreview>)
    .data;
}

export async function sendOppositionHearingInvoicePaymentMessage(
  leadId: string,
  tmNos?: string[]
): Promise<{ waLink?: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/opposition-hearing-invoice/send-payment-message`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink?: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to send Opposition Hearing payment message"
    );
  }
  return (json as ApiSuccessResponse<{ waLink?: string }>).data;
}

export async function saveOppositionHearingCollectedAmount(
  leadId: string,
  applications: { tmNo: string; collectedAmount?: number | null }[]
): Promise<OppositionHearingInvoiceStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/opposition-hearing-collected-amount`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ applications }),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<OppositionHearingInvoiceStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save Opposition Hearing collected amount"
    );
  }
  return (json as ApiSuccessResponse<OppositionHearingInvoiceStatus>).data;
}

// ───────────────────────────────────────────────────────────────────────────
// Opposition Counter — Call Client for Opposition Hearing (4A(xxxiv))
// ───────────────────────────────────────────────────────────────────────────

export async function recordCallClientOppositionHearing(
  leadId: string,
  tmNos?: string[]
): Promise<void> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/opposition-hearing/record-call-client`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  if (!res.ok) {
    const json = (await res.json().catch(() => null)) as
      | ApiErrorResponse
      | null;
    throw new Error(
      json?.message ||
        json?.error ||
        "Failed to record Call Client for Opposition Hearing"
    );
  }
}

// ───────────────────────────────────────────────────────────────────────────
// Opposition Counter — Confirmation from Client for Opposition Hearing (4A(xxxv))
// ───────────────────────────────────────────────────────────────────────────

export type OppositionHearingClientConfirmation = "YES" | "NO" | "POSTPONE";

export interface OppositionHearingConfirmationApplicationRow {
  tmNo: string;
  clientConfirmation: OppositionHearingClientConfirmation | null;
  suggestedNextStep?: string;
}

export interface OppositionHearingConfirmationStatus {
  clientConfirmation: OppositionHearingClientConfirmation | null;
  applications: OppositionHearingConfirmationApplicationRow[];
  noOfApplicationsForOppositionHearing: number | null;
  suggestedNextStep?: string;
}

export async function getOppositionHearingConfirmation(
  leadId: string,
  tmNos?: string[]
): Promise<OppositionHearingConfirmationStatus> {
  const params = new URLSearchParams();
  if (tmNos?.length) {
    for (const t of tmNos) params.append("tmNos", t);
  }
  const qs = params.toString() ? `?${params.toString()}` : "";
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/opposition-hearing/confirmation-from-client${qs}`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<OppositionHearingConfirmationStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Confirmation from Client for Opposition Hearing"
    );
  }
  return (json as ApiSuccessResponse<OppositionHearingConfirmationStatus>)
    .data;
}

export async function saveOppositionHearingConfirmation(
  leadId: string,
  applications: {
    tmNo: string;
    clientConfirmation: OppositionHearingClientConfirmation;
  }[]
): Promise<OppositionHearingConfirmationStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/opposition-hearing/confirmation-from-client`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ applications }),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<OppositionHearingConfirmationStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save Confirmation from Client for Opposition Hearing"
    );
  }
  return (json as ApiSuccessResponse<OppositionHearingConfirmationStatus>)
    .data;
}

// ───────────────────────────────────────────────────────────────────────────
// Opposition Counter — Invoice for TM-M before Opposition Hearing (4A(xxxvi))
// ───────────────────────────────────────────────────────────────────────────

export interface TmMBeforeOppositionHearingInvoiceStatus {
  invoiceAmount: number | null;
  invoiceNo: string | null;
  invoiceDate: string | null;
  noOfAppsForTmMBeforeOppositionHearing: number;
  includeInvoiceNo: boolean;
  tmMBeforeOppositionHearingInvoicePdfKey: string | null;
  tmMBeforeOppositionHearingInvoicePdfUrl: string | null;
  isActive: boolean;
  collectedAmount: number | null;
  outstandingAmount: number | null;
}

export interface TmMBeforeOppositionHearingInvoicePreviewApplication {
  tmNo: string;
  applicationDate: string;
  mark: string;
  typeOfMark: string;
  classNumber: number;
  dateOfOppositionHearing: string | null;
  selected: boolean;
}

export interface TmMBeforeOppositionHearingInvoicePreview {
  dataLabel: string;
  acknowledgementNo: string | null;
  addressee: string;
  addresseeLine1: string | null;
  addresseeLine2: string | null;
  addresseeLine3: string | null;
  formOfAddress: string | null;
  applications: TmMBeforeOppositionHearingInvoicePreviewApplication[];
  invoiceAmount: number;
  invoiceDate: string | null;
  invoiceNo: string | null;
  includeInvoiceNo: boolean;
  noOfAppsForTmMBeforeOppositionHearing: number;
}

export async function getTmMBeforeOppositionHearingInvoiceStatus(
  leadId: string
): Promise<TmMBeforeOppositionHearingInvoiceStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/tm-m-before-opposition-hearing-invoice/status`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<TmMBeforeOppositionHearingInvoiceStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load TM-M before Opposition Hearing Invoice"
    );
  }
  return (
    json as ApiSuccessResponse<TmMBeforeOppositionHearingInvoiceStatus>
  ).data;
}

export async function getTmMBeforeOppositionHearingInvoicePreview(
  leadId: string
): Promise<TmMBeforeOppositionHearingInvoicePreview | null> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/tm-m-before-opposition-hearing-invoice/preview`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<TmMBeforeOppositionHearingInvoicePreview | null>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load TM-M before Opposition Hearing Invoice preview"
    );
  }
  return (
    json as ApiSuccessResponse<TmMBeforeOppositionHearingInvoicePreview | null>
  ).data;
}

export interface SaveTmMBeforeOppositionHearingInvoiceInput {
  invoiceDate?: string;
  invoiceAmount?: number;
  includeInvoiceNo?: boolean;
  selectedTmNos?: string[];
}

export async function saveTmMBeforeOppositionHearingInvoice(
  leadId: string,
  body: SaveTmMBeforeOppositionHearingInvoiceInput
): Promise<TmMBeforeOppositionHearingInvoiceStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/tm-m-before-opposition-hearing-invoice`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<TmMBeforeOppositionHearingInvoiceStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save TM-M before Opposition Hearing Invoice"
    );
  }
  return (
    json as ApiSuccessResponse<TmMBeforeOppositionHearingInvoiceStatus>
  ).data;
}

export async function generateTmMBeforeOppositionHearingInvoicePdf(
  leadId: string
): Promise<{ key: string; url: string; filename: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/tm-m-before-opposition-hearing-invoice/generate-pdf`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: "{}",
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<GeneratedPdfPayload>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to generate TM-M before Opposition Hearing Invoice PDF"
    );
  }
  return toGeneratedPdf((json as ApiSuccessResponse<GeneratedPdfPayload>).data);
}

export async function sendTmMBeforeOppositionHearingInvoiceToClient(
  leadId: string,
  options?: { sendWhatsApp?: boolean; sendEmail?: boolean }
): Promise<{ waLink?: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/tm-m-before-opposition-hearing-invoice/send-to-client`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        sendWhatsApp: options?.sendWhatsApp ?? true,
        sendEmail: options?.sendEmail ?? false,
      }),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink?: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to send TM-M before Opposition Hearing Invoice to client"
    );
  }
  return (json as ApiSuccessResponse<{ waLink?: string }>).data;
}

// ───────────────────────────────────────────────────────────────────────────
// Opposition Counter — Payment for TM-M before Opposition Hearing (4A(xxxvii))
// ───────────────────────────────────────────────────────────────────────────

export async function saveTmMBeforeOppositionHearingCollectedAmount(
  leadId: string,
  collectedAmount: number
): Promise<TmMBeforeOppositionHearingInvoiceStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/tm-m-before-opposition-hearing-collected-amount`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ collectedAmount }),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<TmMBeforeOppositionHearingInvoiceStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save TM-M before Opposition Hearing collected amount"
    );
  }
  return (
    json as ApiSuccessResponse<TmMBeforeOppositionHearingInvoiceStatus>
  ).data;
}

export interface TmMBeforeOppositionHearingInvoiceEmailPreview {
  to: string | null;
  subject: string;
  body: string;
  filename: string | null;
  downloadUrl: string | null;
}

export interface TmMBeforeOppositionHearingInvoiceWhatsAppPreview {
  pdfMessage: string;
  paymentMessage: string;
  emailPreview: TmMBeforeOppositionHearingInvoiceEmailPreview;
}

export async function getTmMBeforeOppositionHearingInvoiceWhatsAppPreview(
  leadId: string
): Promise<TmMBeforeOppositionHearingInvoiceWhatsAppPreview> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/tm-m-before-opposition-hearing-invoice/whatsapp-preview`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<TmMBeforeOppositionHearingInvoiceWhatsAppPreview>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load TM-M before Opposition Hearing Invoice message preview"
    );
  }
  return (
    json as ApiSuccessResponse<TmMBeforeOppositionHearingInvoiceWhatsAppPreview>
  ).data;
}

// ───────────────────────────────────────────────────────────────────────────
// Opposition Counter — File TM-M before Opposition Hearing (4A(xxxviii))
// ───────────────────────────────────────────────────────────────────────────

export interface TmMBeforeOppositionHearingFiledApplicationRow {
  tmNo: string;
  mark: string;
  typeOfMark: string;
  classNumber: number;
  dateOfOppositionHearing: string | null;
  dateOfTmMBeforeOppositionHearingFiled: string | null;
  tmMBeforeOppositionHearingFileKey: string | null;
  tmMBeforeOppositionHearingFilename: string | null;
  tmMBeforeOppositionHearingFileUrl: string | null;
}

export interface TmMBeforeOppositionHearingFiledStatus {
  acknowledgementNo: string | null;
  dateOfFiling: string | null;
  applications: TmMBeforeOppositionHearingFiledApplicationRow[];
  isActive: boolean;
}

export async function getTmMBeforeOppositionHearingFiledStatus(
  leadId: string
): Promise<TmMBeforeOppositionHearingFiledStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/tm-m-before-opposition-hearing/filed`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<TmMBeforeOppositionHearingFiledStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load File TM-M before Opposition Hearing status"
    );
  }
  return (json as ApiSuccessResponse<TmMBeforeOppositionHearingFiledStatus>)
    .data;
}

export interface SaveTmMBeforeOppositionHearingFiledApplicationInput {
  tmNo: string;
  dateOfTmMBeforeOppositionHearingFiled?: string | null;
  tmMBeforeOppositionHearingFileKey?: string | null;
  tmMBeforeOppositionHearingFilename?: string | null;
}

export async function saveTmMBeforeOppositionHearingFiled(
  leadId: string,
  applications: SaveTmMBeforeOppositionHearingFiledApplicationInput[]
): Promise<TmMBeforeOppositionHearingFiledStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/tm-m-before-opposition-hearing/filed`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ applications }),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<TmMBeforeOppositionHearingFiledStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save File TM-M before Opposition Hearing"
    );
  }
  return (json as ApiSuccessResponse<TmMBeforeOppositionHearingFiledStatus>)
    .data;
}

// ───────────────────────────────────────────────────────────────────────────
// Opposition Counter — Inform Client about Filing of TM-M before OH (4A(xxxix))
// ───────────────────────────────────────────────────────────────────────────

export interface InformClientTmMBeforeOppositionHearingPreview {
  message: string;
  applicationCount: number;
  tmNumbers: string[];
}

export async function getInformClientTmMBeforeOppositionHearingPreview(
  leadId: string
): Promise<InformClientTmMBeforeOppositionHearingPreview> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/tm-m-before-opposition-hearing/inform-client-preview`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<InformClientTmMBeforeOppositionHearingPreview>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Inform Client about TM-M before Opposition Hearing preview"
    );
  }
  return (
    json as ApiSuccessResponse<InformClientTmMBeforeOppositionHearingPreview>
  ).data;
}

export async function sendInformClientTmMBeforeOppositionHearingWhatsApp(
  leadId: string
): Promise<{ waLink?: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/tm-m-before-opposition-hearing/inform-client/send-whatsapp`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: "{}",
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink?: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to send Inform Client about TM-M before Opposition Hearing"
    );
  }
  return (json as ApiSuccessResponse<{ waLink?: string }>).data;
}

// ───────────────────────────────────────────────────────────────────────────
// Opposition Counter — Payment from Client for Opposition Hearing (4A(xl))
// ───────────────────────────────────────────────────────────────────────────

export interface OppositionHearingPaymentStatus {
  invoiceAmount: number | null;
  collectedAmount: number | null;
  outstandingAmount: number | null;
  noOfAppsForOppositionHearing: number;
}

export async function getOppositionHearingPaymentStatus(
  leadId: string
): Promise<OppositionHearingPaymentStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/opposition-hearing-payment`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<OppositionHearingPaymentStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Opposition Hearing Payment status"
    );
  }
  return (json as ApiSuccessResponse<OppositionHearingPaymentStatus>).data;
}

export async function saveOppositionHearingPayment(
  leadId: string,
  collectedAmount: number
): Promise<OppositionHearingPaymentStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/opposition-hearing-payment`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ collectedAmount }),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<OppositionHearingPaymentStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save Opposition Hearing Payment"
    );
  }
  return (json as ApiSuccessResponse<OppositionHearingPaymentStatus>).data;
}

// ───────────────────────────────────────────────────────────────────────────
// Opposition Counter — Documents from Client for Opposition Hearing (4A(xli))
// ───────────────────────────────────────────────────────────────────────────

/** Record-only step (no body); workflow advances to step 4A(xlii). */
export async function progressDocumentsFromClientOppositionHearing(
  leadId: string
): Promise<void> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/documents-from-client-opposition-hearing`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    }
  );
  if (!res.ok) {
    const json = (await res.json().catch(() => ({}))) as ApiErrorResponse;
    throw new Error(
      json?.message ||
        json?.error ||
        "Failed to record Documents from Client for Opposition Hearing"
    );
  }
}

// ───────────────────────────────────────────────────────────────────────────
// Opposition Counter — Filing of Opposition Hearing Document (4A(xlii))
// ───────────────────────────────────────────────────────────────────────────

/** Record-only step (no body); workflow advances to step 4A(xliii). */
export async function progressFilingOppositionHearingDocument(
  leadId: string
): Promise<void> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/filing-opposition-hearing-document`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    }
  );
  if (!res.ok) {
    const json = (await res.json().catch(() => ({}))) as ApiErrorResponse;
    throw new Error(
      json?.message ||
        json?.error ||
        "Failed to record Filing of Opposition Hearing Document"
    );
  }
}

// ───────────────────────────────────────────────────────────────────────────
// Opposition Counter — Note on Opposition Hearing (4A(xliii))
// ───────────────────────────────────────────────────────────────────────────

export interface NoteOnOppositionHearingObjectionReasons {
  similarity: boolean;
  nonDistinctive: boolean;
  supportingDocuments: boolean;
  others: boolean;
  othersSpecify: string | null;
  tmMYes: boolean;
}

export interface NoteOnOppositionHearingSimilarMark {
  id: string;
  sortOrder: number;
  mark: string | null;
  tmNo: string | null;
  markClass: string | null;
  goodsServices: string | null;
  userDate: string | null;
  place: string | null;
}

export interface NoteOnOppositionHearingSimilarMarkNote {
  id?: string;
  similarMarkId: string | null;
  sortOrder: number;
  mark: string | null;
  tmNo: string | null;
  imageKey: string | null;
  imageUrl: string | null;
  userDate: string | null;
  goodsServices: string | null;
  address: string | null;
  note1: string | null;
  note2: string | null;
}

export interface NoteOnOppositionHearingOppositionInfo {
  oppositionNo: string | null;
  dateOfOpposition: string | null;
  nameOfOpponent: string | null;
  addressOfOpponent: string | null;
}

export interface NoteOnOppositionHearingApplicationData {
  index: number;
  ackApplicationId: string;
  tmNo: string;
  mark: string;
  typeOfMark: string;
  classNumber: number;
  goodsServices: string | null;
  imageOfMarkKey: string | null;
  imageOfMarkUrl: string | null;
  userDate: string | null;
  userDateForHearing: string | null;
  dateOfFiling: string | null;
  dateOfOppositionHearing: string | null;
  objectionReasons: NoteOnOppositionHearingObjectionReasons;
  similarMarks: NoteOnOppositionHearingSimilarMark[];
  oppositionInfo: NoteOnOppositionHearingOppositionInfo;
  similarMarkNotes: NoteOnOppositionHearingSimilarMarkNote[];
  noteOtherOpposition1: string | null;
  noteOtherOpposition2: string | null;
  notePdfKey: string | null;
  notePdfUrl: string | null;
}

export interface NoteOnOppositionHearingStatus {
  acknowledgementNo: string;
  applicant: string | null;
  totalApplications: number;
  applications: NoteOnOppositionHearingApplicationData[];
}

export async function getNoteOnOppositionHearingStatus(
  leadId: string
): Promise<NoteOnOppositionHearingStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/note-on-opposition-hearing/status`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<NoteOnOppositionHearingStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Note on Opposition Hearing status"
    );
  }
  return (json as ApiSuccessResponse<NoteOnOppositionHearingStatus>).data;
}

export interface SaveNoteOnOppositionHearingSimilarMarkInput {
  mark: string;
  tmNo: string;
  imageKey?: string | null;
  userDate?: string | null;
  goodsServices?: string | null;
  address?: string | null;
  note1?: string | null;
  note2?: string | null;
}

export async function saveNoteOnOppositionHearing(
  leadId: string,
  payload: {
    ackApplicationId: string;
    noteOtherOpposition1?: string | null;
    noteOtherOpposition2?: string | null;
    similarMarkNotes?: SaveNoteOnOppositionHearingSimilarMarkInput[];
  }
): Promise<NoteOnOppositionHearingApplicationData> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/note-on-opposition-hearing`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<NoteOnOppositionHearingApplicationData>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save Note on Opposition Hearing"
    );
  }
  return (json as ApiSuccessResponse<NoteOnOppositionHearingApplicationData>).data;
}

// ───────────────────────────────────────────────────────────────────────────
// Opposition Counter — Attend Opposition Hearing (4A(xliv))
// ───────────────────────────────────────────────────────────────────────────

export type AttendOppositionHearingStatusValue =
  | "ATTENDED"
  | "NOT_ATTENDED"
  | "DIDNT_HAPPEN";

export type AttendOppositionHearingOfficerRemarkValue =
  | "ACCEPTED"
  | "REFUSED"
  | "WILL_PASS_ON_MERITS"
  | "FILE_TM_M"
  | "DIRECTION_OF_OFFICER";

export interface AttendOppositionHearingOfficerDirection {
  index: number;
  direction: string;
}

export interface AttendOppositionHearingApplicationData {
  index: number;
  ackApplicationId: string;
  tmNo: string;
  mark: string;
  typeOfMark: string;
  classNumber: number;
  dateOfOppositionHearing: string | null;
  hearingStatus: AttendOppositionHearingStatusValue | null;
  hearingOfficerRemark: AttendOppositionHearingOfficerRemarkValue | null;
  officerDirections: AttendOppositionHearingOfficerDirection[];
  remarks: string | null;
}

export interface AttendOppositionHearingStatusResponse {
  acknowledgementNo: string;
  applicant: string | null;
  totalApplications: number;
  applications: AttendOppositionHearingApplicationData[];
}

export async function getAttendOppositionHearingStatus(
  leadId: string
): Promise<AttendOppositionHearingStatusResponse> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/attend-opposition-hearing/status`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<AttendOppositionHearingStatusResponse>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Attend Opposition Hearing status"
    );
  }
  return (json as ApiSuccessResponse<AttendOppositionHearingStatusResponse>).data;
}

export async function saveAttendOppositionHearing(
  leadId: string,
  payload: {
    ackApplicationId: string;
    hearingStatus: AttendOppositionHearingStatusValue;
    hearingOfficerRemark?: AttendOppositionHearingOfficerRemarkValue | null;
    officerDirections?: AttendOppositionHearingOfficerDirection[];
    remarks?: string | null;
  }
): Promise<AttendOppositionHearingApplicationData> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/attend-opposition-hearing`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<AttendOppositionHearingApplicationData>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save Attend Opposition Hearing"
    );
  }
  return (json as ApiSuccessResponse<AttendOppositionHearingApplicationData>).data;
}

// ───────────────────────────────────────────────────────────────────────────
// Opposition Counter — Inform Client after Opposition Hearing (4A(xlv))
// ───────────────────────────────────────────────────────────────────────────

export type InformAfterOppositionHearingScenarioValue =
  | "DIDNT_HAPPEN"
  | "ACCEPTED_OR_MERITS"
  | "REFUSED"
  | "FILE_TM_M_ONLY"
  | "DIRECTION_ONLY"
  | "FILE_TM_M_AND_DIRECTION"
  | "NOT_APPLICABLE";

export interface InformAfterOppositionHearingApplicationItem {
  ackApplicationId: string;
  tmNo: string;
  mark: string;
  dateOfFiling: string;
  hearingStatus: AttendOppositionHearingStatusValue | null;
  hearingOfficerRemark: AttendOppositionHearingOfficerRemarkValue | null;
  officerDirections: AttendOppositionHearingOfficerDirection[] | null;
  scenario: InformAfterOppositionHearingScenarioValue;
}

export interface InformAfterOppositionHearingGroup {
  scenario: InformAfterOppositionHearingScenarioValue;
  label: string;
  applications: InformAfterOppositionHearingApplicationItem[];
  message: string;
  canSend: boolean;
}

export interface InformAfterOppositionHearingStatus {
  acknowledgementNo: string | null;
  applicant: string | null;
  totalApplications: number;
  applications: InformAfterOppositionHearingApplicationItem[];
  groups: InformAfterOppositionHearingGroup[];
  hasMessagesToSend: boolean;
}

export async function getInformAfterOppositionHearingStatus(
  leadId: string
): Promise<InformAfterOppositionHearingStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/inform-after-opposition-hearing/status`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<InformAfterOppositionHearingStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Inform Client after Opposition Hearing"
    );
  }
  return (json as ApiSuccessResponse<InformAfterOppositionHearingStatus>).data;
}

export async function sendAllInformAfterOppositionHearing(
  leadId: string
): Promise<unknown> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/inform-after-opposition-hearing/send-all`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    }
  );
  const json = (await res.json()) as ApiSuccessResponse<unknown> | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to send Inform Client after Opposition Hearing"
    );
  }
  return (json as ApiSuccessResponse<unknown>).data;
}

export async function recordInformAfterOppositionHearing(
  leadId: string
): Promise<void> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/inform-after-opposition-hearing/record`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    }
  );
  if (!res.ok) {
    const json = (await res.json().catch(() => ({}))) as ApiErrorResponse;
    throw new Error(
      json?.message ||
        json?.error ||
        "Failed to record Inform Client after Opposition Hearing"
    );
  }
}

// ───────────────────────────────────────────────────────────────────────────
// Opposition Counter — Invoice for TM-M after Opposition Hearing (4A(xlvi))
// ───────────────────────────────────────────────────────────────────────────

export interface TmMAfterOppositionHearingInvoiceTmRow {
  tmNo: string;
  defaultInvoiceAmount: number;
  invoiceAmount: number;
  invoiceDate: string | null;
  individualInvoiceNo: string | null;
  includeInvoiceNo: boolean;
}

export interface TmMAfterOppositionHearingInvoiceStatus {
  defaultInvoiceAmount: number | null;
  invoiceAmount: number | null;
  invoiceNo: string | null;
  invoiceDate: string | null;
  noOfAppsForTmMAfterOppositionHearing: number;
  includeInvoiceNo: boolean;
  tmMAfterOppositionHearingInvoicePdfKey: string | null;
  tmMAfterOppositionHearingInvoicePdfUrl: string | null;
  isActive: boolean;
  collectedAmount: number | null;
  outstandingAmount: number | null;
  tmInvoiceRows: TmMAfterOppositionHearingInvoiceTmRow[];
}

export interface TmMAfterOppositionHearingInvoicePreviewApplication {
  tmNo: string;
  applicationDate: string;
  mark: string;
  typeOfMark: string;
  classNumber: number;
  dateOfOppositionHearing: string | null;
  selected: boolean;
}

export interface TmMAfterOppositionHearingInvoicePreview {
  dataLabel: string;
  addressee: string;
  addresseeLine1: string | null;
  addresseeLine2: string | null;
  addresseeLine3: string | null;
  formOfAddress: string | null;
  applications: TmMAfterOppositionHearingInvoicePreviewApplication[];
  invoiceAmount: number;
  invoiceDate: string | null;
  invoiceNo: string | null;
  includeInvoiceNo: boolean;
  noOfAppsForTmMAfterOppositionHearing: number;
}

export async function getTmMAfterOppositionHearingInvoiceStatus(
  leadId: string
): Promise<TmMAfterOppositionHearingInvoiceStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/tm-m-after-opposition-hearing-invoice/status`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<TmMAfterOppositionHearingInvoiceStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load TM-M after Opposition Hearing Invoice status"
    );
  }
  return (json as ApiSuccessResponse<TmMAfterOppositionHearingInvoiceStatus>).data;
}

export async function getTmMAfterOppositionHearingInvoicePreview(
  leadId: string
): Promise<TmMAfterOppositionHearingInvoicePreview> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/tm-m-after-opposition-hearing-invoice/preview`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<TmMAfterOppositionHearingInvoicePreview>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load TM-M after Opposition Hearing Invoice preview"
    );
  }
  return (json as ApiSuccessResponse<TmMAfterOppositionHearingInvoicePreview>).data;
}

export interface SaveTmMAfterOppositionHearingInvoiceRow {
  tmNo: string;
  invoiceAmount: number;
  invoiceDate: string;
  includeInvoiceNo: boolean;
}

export async function saveTmMAfterOppositionHearingInvoice(
  leadId: string,
  applications: SaveTmMAfterOppositionHearingInvoiceRow[]
): Promise<TmMAfterOppositionHearingInvoiceStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/tm-m-after-opposition-hearing-invoice`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ applications }),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<TmMAfterOppositionHearingInvoiceStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save TM-M after Opposition Hearing Invoice"
    );
  }
  return (json as ApiSuccessResponse<TmMAfterOppositionHearingInvoiceStatus>).data;
}

export async function generateTmMAfterOppositionHearingInvoicePdf(
  leadId: string
): Promise<TmMAfterOppositionHearingInvoiceStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/tm-m-after-opposition-hearing-invoice/generate-pdf`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<TmMAfterOppositionHearingInvoiceStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to generate TM-M after Opposition Hearing Invoice PDF"
    );
  }
  return (json as ApiSuccessResponse<TmMAfterOppositionHearingInvoiceStatus>).data;
}

export async function sendTmMAfterOppositionHearingInvoiceToClient(
  leadId: string,
  payload: { sendWhatsApp?: boolean; sendEmail?: boolean } = {}
): Promise<{ waLink?: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/tm-m-after-opposition-hearing-invoice/send`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink?: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to send TM-M after Opposition Hearing Invoice to client"
    );
  }
  return (json as ApiSuccessResponse<{ waLink?: string }>).data;
}

export interface TmMAfterOppositionHearingInvoiceEmailPreview {
  to: string | null;
  subject: string;
  body: string;
  filename: string | null;
  downloadUrl: string | null;
}

export interface TmMAfterOppositionHearingInvoiceWhatsAppPreview {
  pdfMessage: string;
  paymentMessage: string;
  emailPreview: TmMAfterOppositionHearingInvoiceEmailPreview;
}

export async function getTmMAfterOppositionHearingInvoiceWhatsAppPreview(
  leadId: string
): Promise<TmMAfterOppositionHearingInvoiceWhatsAppPreview> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/tm-m-after-opposition-hearing-invoice/whatsapp-preview`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<TmMAfterOppositionHearingInvoiceWhatsAppPreview>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load TM-M after Opposition Hearing Invoice message preview"
    );
  }
  return (
    json as ApiSuccessResponse<TmMAfterOppositionHearingInvoiceWhatsAppPreview>
  ).data;
}

// ───────────────────────────────────────────────────────────────────────────
// Opposition Counter — Save Collected Amount for TM-M after OH (4A(xlvii))
// ───────────────────────────────────────────────────────────────────────────

export async function saveTmMAfterOppositionHearingCollectedAmount(
  leadId: string,
  collectedAmount: number
): Promise<TmMAfterOppositionHearingInvoiceStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/tm-m-after-opposition-hearing-invoice/collected-amount`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ collectedAmount }),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<TmMAfterOppositionHearingInvoiceStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save TM-M after Opposition Hearing collected amount"
    );
  }
  return (json as ApiSuccessResponse<TmMAfterOppositionHearingInvoiceStatus>).data;
}

// ───────────────────────────────────────────────────────────────────────────
// Opposition Counter — File TM-M after Opposition Hearing (4A(xlviii))
// + Inform Client after filing (4A(xlix))
// ───────────────────────────────────────────────────────────────────────────

export interface TmMAfterOppositionHearingFiledApplicationRow {
  tmNo: string;
  mark: string;
  typeOfMark: string;
  classNumber: number;
  dateOfOppositionHearing: string | null;
  dateOfTmMAfterOppositionHearingFiled: string | null;
  tmMAfterOppositionHearingFileKey: string | null;
  tmMAfterOppositionHearingFilename: string | null;
  tmMAfterOppositionHearingFileUrl: string | null;
}

export interface TmMAfterOppositionHearingFiledStatus {
  acknowledgementNo: string | null;
  dateOfFiling: string | null;
  applications: TmMAfterOppositionHearingFiledApplicationRow[];
  isActive: boolean;
}

export async function getTmMAfterOppositionHearingFiledStatus(
  leadId: string
): Promise<TmMAfterOppositionHearingFiledStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/tm-m-after-opposition-hearing/filed`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<TmMAfterOppositionHearingFiledStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load File TM-M after Opposition Hearing status"
    );
  }
  return (json as ApiSuccessResponse<TmMAfterOppositionHearingFiledStatus>).data;
}

export async function saveTmMAfterOppositionHearingFiled(
  leadId: string,
  applications: Array<{
    tmNo: string;
    dateOfTmMAfterOppositionHearingFiled?: string | null;
    tmMAfterOppositionHearingFileKey?: string | null;
    tmMAfterOppositionHearingFilename?: string | null;
  }>
): Promise<TmMAfterOppositionHearingFiledStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/tm-m-after-opposition-hearing/filed`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ applications }),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<TmMAfterOppositionHearingFiledStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save File TM-M after Opposition Hearing"
    );
  }
  return (json as ApiSuccessResponse<TmMAfterOppositionHearingFiledStatus>).data;
}

export interface InformClientTmMAfterOppositionHearingPreview {
  message: string;
  applicationCount: number;
}

export async function getInformClientTmMAfterOppositionHearingPreview(
  leadId: string
): Promise<InformClientTmMAfterOppositionHearingPreview> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/tm-m-after-opposition-hearing/inform-client-preview`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<InformClientTmMAfterOppositionHearingPreview>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Inform Client about TM-M after Opposition Hearing preview"
    );
  }
  return (
    json as ApiSuccessResponse<InformClientTmMAfterOppositionHearingPreview>
  ).data;
}

export async function sendInformClientTmMAfterOppositionHearingWhatsApp(
  leadId: string
): Promise<{ waLink?: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/tm-m-after-opposition-hearing/inform-client/send-whatsapp`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink?: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to send Inform Client about TM-M after Opposition Hearing"
    );
  }
  return (json as ApiSuccessResponse<{ waLink?: string }>).data;
}

// ───────────────────────────────────────────────────────────────────────────
// Opposition Counter — Order of Opposition Hearing to Client (4A(li))
// ───────────────────────────────────────────────────────────────────────────

export type OrderOfOppositionHearingOutcomeValue =
  | "ACCEPTED_AFTER_HEARING"
  | "REFUSED_AFTER_HEARING"
  | "REFUSED_AFTER_NON_APPEARANCE"
  | "ACCEPTED_AFTER_NON_APPEARANCE";

export interface OrderOfOppositionHearingApplicationRow {
  tmNo: string;
  mark: string;
  typeOfMark: string;
  classNumber: number;
  dateOfOppositionHearing: string | null;
  orderOfHearingFileKey: string | null;
  orderOfHearingFilename: string | null;
  orderOfHearingFileUrl: string | null;
  outcomeOfHearing: OrderOfOppositionHearingOutcomeValue | null;
}

export interface OrderOfOppositionHearingStatus {
  acknowledgementNo: string | null;
  applicant: string | null;
  applications: OrderOfOppositionHearingApplicationRow[];
}

export async function getOrderOfOppositionHearingStatus(
  leadId: string
): Promise<OrderOfOppositionHearingStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/order-of-opposition-hearing/status`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<OrderOfOppositionHearingStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Order of Opposition Hearing status"
    );
  }
  return (json as ApiSuccessResponse<OrderOfOppositionHearingStatus>).data;
}

export interface SaveOrderOfOppositionHearingRow {
  tmNo: string;
  orderOfHearingFileKey?: string | null;
  orderOfHearingFilename?: string | null;
  outcomeOfHearing?: OrderOfOppositionHearingOutcomeValue | null;
}

export async function saveOrderOfOppositionHearingDetails(
  leadId: string,
  applications: SaveOrderOfOppositionHearingRow[]
): Promise<OrderOfOppositionHearingStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/order-of-opposition-hearing/details`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ applications }),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<OrderOfOppositionHearingStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save Order of Opposition Hearing details"
    );
  }
  return (json as ApiSuccessResponse<OrderOfOppositionHearingStatus>).data;
}

export async function getOrderOfOppositionHearingMessagePreview(
  leadId: string,
  scenario: OrderOfOppositionHearingOutcomeValue,
  tmNos?: string[]
): Promise<{ message: string; applicationCount: number }> {
  const params = new URLSearchParams();
  params.set("scenario", scenario);
  for (const tm of tmNos ?? []) params.append("tmNos", tm);
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/order-of-opposition-hearing/message-preview?${params.toString()}`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ message: string; applicationCount: number }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Order of Opposition Hearing message preview"
    );
  }
  return (json as ApiSuccessResponse<{ message: string; applicationCount: number }>).data;
}

export async function sendAllOrderOfOppositionHearingMessages(
  leadId: string,
  tmNos?: string[]
): Promise<unknown> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/order-of-opposition-hearing/send-all-messages`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  const json = (await res.json()) as ApiSuccessResponse<unknown> | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to send Order of Opposition Hearing messages"
    );
  }
  return (json as ApiSuccessResponse<unknown>).data;
}

// ───────────────────────────────────────────────────────────────────────────
// Final Order — 5A: Registration Details (S3_05a)
// ───────────────────────────────────────────────────────────────────────────

export interface RegistrationDetailsApplication {
  ackApplicationId: string;
  tmNo: string;
  mark: string;
  classNumber: number;
  typeOfMark: string;
  dateOfFiling: string;
  orderOfRegistrationFileKey: string | null;
  orderOfRegistrationFilename: string | null;
  orderOfRegistrationFileUrl: string | null;
  dateOfIssue: string | null;
  certificateNo: string | null;
  validityDate: string | null;
}

export interface RegistrationDetailsStatusResponse {
  acknowledgementNo: string | null;
  dateOfFiling: string | null;
  applicant: string | null;
  totalApplications: number;
  applications: RegistrationDetailsApplication[];
  canForwardPdfs: boolean;
  allDetailsComplete: boolean;
}

export interface SetRegistrationDetailsApplicationInput {
  tmNo: string;
  orderOfRegistrationFileKey?: string | null;
  orderOfRegistrationFilename?: string | null;
  dateOfIssue?: string | null;
  certificateNo?: string | null;
}

export interface SetRegistrationDetailsPayload {
  applications: SetRegistrationDetailsApplicationInput[];
}

export async function getRegistrationDetailsStatus(
  leadId: string
): Promise<RegistrationDetailsStatusResponse> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/registration/details/status`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<RegistrationDetailsStatusResponse>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Registration Details status"
    );
  }
  return (json as ApiSuccessResponse<RegistrationDetailsStatusResponse>).data;
}

export async function setRegistrationDetails(
  leadId: string,
  payload: SetRegistrationDetailsPayload
): Promise<RegistrationDetailsStatusResponse> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/registration/details`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<RegistrationDetailsStatusResponse>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save Registration Details"
    );
  }
  return (json as ApiSuccessResponse<RegistrationDetailsStatusResponse>).data;
}

export async function forwardRegistrationOrderPdfs(
  leadId: string,
  tmNos?: string[]
): Promise<{ waLink: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/registration/details/forward-pdfs`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to forward Registration Order PDFs"
    );
  }
  return (json as ApiSuccessResponse<{ waLink: string }>).data;
}

export interface RegistrationMessagePreviewResponse {
  message: string;
  applicationCount: number;
}

export async function getRegistrationMessagePreview(
  leadId: string,
  tmNos?: string[]
): Promise<RegistrationMessagePreviewResponse> {
  const query = tmNos?.length
    ? `?${tmNos.map((t) => `tmNos=${encodeURIComponent(t)}`).join("&")}`
    : "";
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/registration/details/message-preview${query}`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<RegistrationMessagePreviewResponse>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Registration message preview"
    );
  }
  return (json as ApiSuccessResponse<RegistrationMessagePreviewResponse>).data;
}

export async function sendRegistrationMessage(
  leadId: string,
  tmNos?: string[]
): Promise<{ waLink: string; applicationCount: number }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/registration/details/send-message`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink: string; applicationCount: number }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to send Registration message"
    );
  }
  return (json as ApiSuccessResponse<{ waLink: string; applicationCount: number }>).data;
}

// ───────────────────────────────────────────────────────────────────────────
// Final Order — 5B: Refusal or Abandonment Details (S3_05b)
// ───────────────────────────────────────────────────────────────────────────

export type RefusalOrAbandonmentOption = "Refused" | "Abandoned";

export type RefusalAbandonmentReasonValue =
  | "FC_FAIL_NON_FILING_OF_REPLY"
  | "FC_FAIL_NON_APPEARANCE_IN_HEARING"
  | "FC_FAIL_DISALLOWANCE_AFTER_HEARING"
  | "OBJECTION_NON_FILING_OF_REPLY"
  | "OBJECTION_NON_APPEARANCE_IN_HEARING"
  | "OBJECTION_DISALLOWANCE_AFTER_HEARING"
  | "OPPOSITION_NON_FILING_OF_COUNTER"
  | "OPPOSITION_COUNTER_NON_APPEARANCE_IN_HEARING"
  | "OPPOSITION_COUNTER_DISALLOWANCE_AFTER_HEARING"
  | "OPPOSITION_NON_FILING_OF_EVIDENCE"
  | "OPPOSITION_EVIDENCE_NON_APPEARANCE_IN_HEARING"
  | "OPPOSITION_EVIDENCE_DISALLOWANCE_AFTER_HEARING"
  | "OPPOSITION_HEARING_NON_APPEARANCE"
  | "OPPOSITION_HEARING_DISALLOWANCE";

export interface RefusalAbandonmentDetailsApplication {
  ackApplicationId: string;
  tmNo: string;
  mark: string;
  classNumber: number;
  typeOfMark: string;
  dateOfFiling: string;
  applicationStatus: string;
  orderFileKey: string | null;
  orderFilename: string | null;
  orderFileUrl: string | null;
  refusalOrAbandonment: RefusalOrAbandonmentOption | string | null;
  dateOfOrder: string | null;
  refusalReason: RefusalAbandonmentReasonValue | null;
}

export interface RefusalAbandonmentDetailsStatusResponse {
  acknowledgementNo: string | null;
  dateOfFiling: string | null;
  applicant: string | null;
  totalApplications: number;
  applications: RefusalAbandonmentDetailsApplication[];
  canForwardPdfs: boolean;
  allDetailsComplete: boolean;
  canSendMessage: boolean;
}

export interface SetRefusalAbandonmentDetailsApplicationInput {
  tmNo: string;
  orderFileKey?: string | null;
  orderFilename?: string | null;
  refusalOrAbandonment?: RefusalOrAbandonmentOption | null;
  dateOfOrder?: string | null;
  refusalReason?: RefusalAbandonmentReasonValue | null;
}

export interface SetRefusalAbandonmentDetailsPayload {
  applications: SetRefusalAbandonmentDetailsApplicationInput[];
}

export async function getRefusalAbandonmentDetailsStatus(
  leadId: string
): Promise<RefusalAbandonmentDetailsStatusResponse> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/refusal-abandonment/details/status`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<RefusalAbandonmentDetailsStatusResponse>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Refusal/Abandonment Details status"
    );
  }
  return (json as ApiSuccessResponse<RefusalAbandonmentDetailsStatusResponse>).data;
}

export async function setRefusalAbandonmentDetails(
  leadId: string,
  payload: SetRefusalAbandonmentDetailsPayload
): Promise<RefusalAbandonmentDetailsStatusResponse> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/refusal-abandonment/details`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<RefusalAbandonmentDetailsStatusResponse>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save Refusal/Abandonment Details"
    );
  }
  return (json as ApiSuccessResponse<RefusalAbandonmentDetailsStatusResponse>).data;
}

export async function forwardRefusalAbandonmentOrderPdfs(
  leadId: string,
  tmNos?: string[]
): Promise<{ waLink: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/refusal-abandonment/details/forward-pdfs`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to forward Refusal/Abandonment Order PDFs"
    );
  }
  return (json as ApiSuccessResponse<{ waLink: string }>).data;
}

/**
 * 5B(iii) — one outgoing message and the applications it covers.
 *
 * Review item 89: applications on one acknowledgement can differ in both 5f) Refusal
 * Reason and 5d) Refused or Abandoned, and the source document (pg 162) wants each
 * combination informed separately. The backend groups by (reason, outcome) and returns
 * one entry per group.
 */
export interface RefusalAbandonmentMessageGroup {
  message: string;
  refusalReason: string | null;
  refusalOrAbandonment: string | null;
  tmNos: string[];
  applicationCount: number;
}

export interface RefusalAbandonmentMessagePreviewResponse
  extends RegistrationMessagePreviewResponse {
  /** One entry per (reason, outcome) group. `message` is the first group only. */
  messages: RefusalAbandonmentMessageGroup[];
  refusalReason: string | null;
}

export async function getRefusalAbandonmentMessagePreview(
  leadId: string,
  tmNos?: string[]
): Promise<RefusalAbandonmentMessagePreviewResponse> {
  const query = tmNos?.length
    ? `?${tmNos.map((t) => `tmNos=${encodeURIComponent(t)}`).join("&")}`
    : "";
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/refusal-abandonment/details/message-preview${query}`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<RefusalAbandonmentMessagePreviewResponse>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Refusal/Abandonment message preview"
    );
  }
  return (json as ApiSuccessResponse<RefusalAbandonmentMessagePreviewResponse>).data;
}

export async function sendRefusalAbandonmentMessage(
  leadId: string,
  tmNos?: string[]
): Promise<{
  waLink: string;
  applicationCount: number;
  /** Number of messages sent — one per (reason, outcome) group. See item 89. */
  messageCount: number;
}> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/refusal-abandonment/details/send-message`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(tmNos?.length ? { tmNos } : {}),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{
        waLink: string;
        applicationCount: number;
        messageCount: number;
      }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to send Refusal/Abandonment message"
    );
  }
  return (
    json as ApiSuccessResponse<{
      waLink: string;
      applicationCount: number;
      messageCount: number;
    }>
  ).data;
}

// ===========================================================================
// Status Timeline — chronological status journey for one application (TM No.)
// with the stage data that was entered while the application was in each
// status. Surfaces past data even after status has moved on.
// ===========================================================================

export type StatusTimelineGroup =
  | "tm-no-allotted"
  | "formalities-check-pass"
  | "formalities-check-fail"
  | "marked-for-exam"
  | "examination-report-accepted"
  | "examination-report-objected"
  | "examination-report-accepted-after-objection"
  | "examination-report-hearing"
  | "advertised"
  | "advertised-opposed"
  | "advertised-not-opposed"
  | "opposition-hearing"
  | "registered"
  | "refused"
  | "abandoned"
  | "other";

export interface StatusTimelineInvoiceBlock {
  invoiceNo: string | null;
  invoiceDate: string | null;
  invoiceAmount: number | null;
  fullInvoiceAmount?: number | null;
  computedInvoiceTotal?: number | null;
  discount: number | null;
  collectedAmount: number | null;
  outstandingAmount: number | null;
  invoicePdfKey: string | null;
  invoicePdfUrl: string | null;
}

export interface StatusTimelineFcConfirmationBlock {
  clientGoingAhead: boolean | null;
  invoiceAmount: number | null;
  collectedAmount: number | null;
  outstandingAmount: number | null;
}

export interface StatusTimelineObjectionConfirmationBlock {
  clientGoingAhead: boolean | null;
}

export interface StatusTimelineFcFailData {
  dateOfFcNotice: string | null;
  reasonForFcFailure: string | null;
  lastDateForFcReply: string | null;
  noticeOfFcFailFileKey: string | null;
  noticeOfFcFailFilename: string | null;
  noticeOfFcFailFileUrl: string | null;
  invoice: StatusTimelineInvoiceBlock | null;
  confirmation: StatusTimelineFcConfirmationBlock | null;
  replyFiled: {
    dateOfFcReplyFiled: string | null;
    fcReplyFileKey: string | null;
    fcReplyFilename: string | null;
    fcReplyFileUrl: string | null;
  } | null;
}

export interface StatusTimelineObjectionData {
  dateOfExaminationReport: string | null;
  reasonForObjection: {
    similarity?: boolean;
    nonDistinctive?: boolean;
    supportingDocuments?: boolean;
    others?: boolean;
    othersSpecify?: string | null;
    tmMYes?: boolean;
  };
  similarMarks: Array<{
    mark: string | null;
    tmNo: string | null;
    class: string | null;
    goodsServices: string | null;
    userDate: string | null;
    place: string | null;
  }>;
  lastDateForReplyToObjection: string | null;
  examinationReportFileKey: string | null;
  examinationReportFilename: string | null;
  examinationReportFileUrl: string | null;
  invoice: StatusTimelineInvoiceBlock | null;
  confirmation: StatusTimelineObjectionConfirmationBlock | null;
  replyFiled: {
    dateOfReplyToObjectionFiled: string | null;
    replyToObjectionFileKey: string | null;
    replyToObjectionFilename: string | null;
    replyToObjectionFileUrl: string | null;
  } | null;
}

export interface StatusTimelineAcceptedData {
  examinationReportAcceptanceFileKey: string | null;
  examinationReportAcceptanceFilename: string | null;
  examinationReportAcceptanceFileUrl: string | null;
}

export interface StatusTimelineHearingData {
  dateOfHearing: string | null;
  hearingNoticeFileKey: string | null;
  hearingNoticeFilename: string | null;
  hearingNoticeFileUrl: string | null;
  invoice: StatusTimelineInvoiceBlock | null;
  tmMBeforeHearingInvoice: StatusTimelineInvoiceBlock | null;
  tmMBeforeHearingFiled: {
    dateOfTmMBeforeHearingFiled: string | null;
    tmMBeforeHearingFileKey: string | null;
    tmMBeforeHearingFilename: string | null;
    tmMBeforeHearingFileUrl: string | null;
  } | null;
  attendHearing: {
    hearingStatus: string | null;
    hearingOfficerRemark: string | null;
    officerDirections: Array<{ index: number; direction: string }>;
    remarks: string | null;
    postponementNoticeFileKey: string | null;
    postponementNoticeFilename: string | null;
    postponementNoticeFileUrl: string | null;
  } | null;
  orderOfHearing: {
    outcomeOfHearing: string | null;
    orderOfHearingFileKey: string | null;
    orderOfHearingFilename: string | null;
    orderOfHearingFileUrl: string | null;
  } | null;
  noteOnHearing: {
    noteNonDistinctive1: string | null;
    noteNonDistinctive2: string | null;
    supportingDocsUploaded: boolean | null;
    noteOtherObjections1: string | null;
    noteOtherObjections2: string | null;
    notePdfKey: string | null;
    notePdfUrl: string | null;
  } | null;
}

export interface StatusTimelineAdvertisedData {
  journalDate: string | null;
  lastDateForOpposition: string | null;
  orderFromRegistryFileKey: string | null;
  orderFromRegistryFilename: string | null;
  orderFromRegistryFileUrl: string | null;
}

export interface StatusTimelineRegisteredData {
  orderOfRegistrationFileKey: string | null;
  orderOfRegistrationFilename: string | null;
  orderOfRegistrationFileUrl: string | null;
  dateOfIssue: string | null;
  certificateNo: string | null;
  validityDate: string | null;
}

export interface StatusTimelineRefusedAbandonedData {
  refusalOrAbandonment: string | null;
  dateOfOrder: string | null;
  refusalReason: string | null;
  orderFileKey: string | null;
  orderFilename: string | null;
  orderFileUrl: string | null;
}

export type StatusTimelineSegmentData =
  | StatusTimelineFcFailData
  | StatusTimelineObjectionData
  | StatusTimelineAcceptedData
  | StatusTimelineHearingData
  | StatusTimelineAdvertisedData
  | StatusTimelineRegisteredData
  | StatusTimelineRefusedAbandonedData
  | null;

export type TimelineActionKind =
  | "notice-to-client"
  | "inform-client"
  | "save-data"
  | "call-client"
  | "confirmation"
  | "invoice"
  | "payment"
  | "documents"
  | "filing"
  | "skipped"
  | "other";

export type TimelineActionStatus =
  | "completed"
  | "skipped"
  | "submitted"
  | "superseded"
  | "reverted";

export interface TimelineAction {
  step: string;
  label: string;
  kind: TimelineActionKind;
  status: TimelineActionStatus;
  performedAt: string;
  performedBy: { name: string; email: string } | null;
}

export interface StatusTimelineSegment {
  status: string;
  group: StatusTimelineGroup;
  enteredAt: string | null;
  exitedAt: string | null;
  isCurrent: boolean;
  changedBy: { name: string; email: string } | null;
  hasData: boolean;
  data: StatusTimelineSegmentData;
  actions: TimelineAction[];
}

export interface StatusTimelineApplication {
  ackApplicationId: string;
  tmNo: string;
  mark: string;
  classNumber: number;
  typeOfMark: string;
  applicationDate: string;
  currentStatus: string;
}

export interface StatusTimelineResponse {
  leadId: string;
  tmNo: string;
  acknowledgementNo: string | null;
  dateOfFiling: string | null;
  application: StatusTimelineApplication;
  timeline: StatusTimelineSegment[];
}

export async function getStatusTimeline(
  leadId: string,
  tmNo: string,
  opts?: { light?: boolean }
): Promise<StatusTimelineResponse> {
  const params = new URLSearchParams({ tmNo });
  if (opts?.light) params.set("light", "true");
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/data/timeline?${params.toString()}`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<StatusTimelineResponse>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load status timeline"
    );
  }
  return (json as ApiSuccessResponse<StatusTimelineResponse>).data;
}

export interface StatusTimelineSegmentResponse {
  group: StatusTimelineGroup;
  data: StatusTimelineSegmentData;
  hasData: boolean;
}

export async function getStatusTimelineSegment(
  leadId: string,
  tmNo: string,
  group: StatusTimelineGroup
): Promise<StatusTimelineSegmentResponse> {
  const params = new URLSearchParams({ tmNo, group });
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/data/timeline/segment?${params.toString()}`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<StatusTimelineSegmentResponse>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load status segment"
    );
  }
  return (json as ApiSuccessResponse<StatusTimelineSegmentResponse>).data;
}

// ───────────────────────────────────────────────────────────────────────────
// Opposition Counter — Subsequent Opposition Hearings (4A(l))
// ───────────────────────────────────────────────────────────────────────────

export interface SubsequentOppositionHearingApplicationItem {
  ackApplicationId: string;
  isActive: boolean;
  tmNo: string;
  applicationDate: string;
  mark: string;
  typeOfMark: string;
  classNumber: number;
  dateOfHearing: string | null;
  hearingNoticeFileKey: string | null;
  hearingNoticeFilename: string | null;
  hearingNoticeFileUrl: string | null;
  hearingStatus: AttendOppositionHearingStatusValue | null;
  hearingOfficerRemark: AttendOppositionHearingOfficerRemarkValue | null;
  officerDirections: AttendOppositionHearingOfficerDirection[];
  remarks: string | null;
  postponementNoticeFileKey: string | null;
  postponementNoticeFilename: string | null;
  postponementNoticeFileUrl: string | null;
}

export interface SubsequentOppositionHearingStatus {
  isActive: boolean;
  currentHearingNumber: number;
  fieldPrefix: string;
  acknowledgementNo: string | null;
  dateOfFiling: string | null;
  applicant: string | null;
  totalApplications: number;
  applications: SubsequentOppositionHearingApplicationItem[];
}

export async function getSubsequentOppositionHearingStatus(
  leadId: string
): Promise<SubsequentOppositionHearingStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/subsequent-opposition-hearing`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<SubsequentOppositionHearingStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Subsequent Opposition Hearing status"
    );
  }
  return (json as ApiSuccessResponse<SubsequentOppositionHearingStatus>).data;
}

export interface SaveSubsequentOppositionHearingDetailsItem {
  tmNo: string;
  dateOfHearing?: string | null;
  hearingNoticeFileKey?: string | null;
  hearingNoticeFilename?: string | null;
}

export async function saveSubsequentOppositionHearingDetails(
  leadId: string,
  hearingNumber: number,
  applications: SaveSubsequentOppositionHearingDetailsItem[]
): Promise<SubsequentOppositionHearingStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/subsequent-opposition-hearing/details`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ hearingNumber, applications }),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<SubsequentOppositionHearingStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save Subsequent Opposition Hearing details"
    );
  }
  return (json as ApiSuccessResponse<SubsequentOppositionHearingStatus>).data;
}

export interface SaveSubsequentOppositionHearingOutcomeItem {
  tmNo: string;
  hearingStatus: AttendOppositionHearingStatusValue;
  hearingOfficerRemark?: AttendOppositionHearingOfficerRemarkValue | null;
  officerDirections?: AttendOppositionHearingOfficerDirection[];
  remarks?: string | null;
}

export async function saveSubsequentOppositionHearingOutcome(
  leadId: string,
  hearingNumber: number,
  applications: SaveSubsequentOppositionHearingOutcomeItem[]
): Promise<SubsequentOppositionHearingStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/subsequent-opposition-hearing/outcome`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ hearingNumber, applications }),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<SubsequentOppositionHearingStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save Subsequent Opposition Hearing outcome"
    );
  }
  return (json as ApiSuccessResponse<SubsequentOppositionHearingStatus>).data;
}

export interface SaveSubsequentOppositionHearingPostponementItem {
  tmNo: string;
  postponementNoticeFileKey?: string | null;
  postponementNoticeFilename?: string | null;
}

export async function saveSubsequentOppositionHearingPostponement(
  leadId: string,
  hearingNumber: number,
  applications: SaveSubsequentOppositionHearingPostponementItem[]
): Promise<SubsequentOppositionHearingStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/subsequent-opposition-hearing/postponement`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ hearingNumber, applications }),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<SubsequentOppositionHearingStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save Subsequent Opposition Hearing postponement"
    );
  }
  return (json as ApiSuccessResponse<SubsequentOppositionHearingStatus>).data;
}

export interface SubsequentOppositionHearingNoticePreview {
  message: string;
  applicationCount: number;
  hearingNumber: number;
}

export async function getSubsequentOppositionHearingNoticePreview(
  leadId: string,
  tmNos?: string[]
): Promise<SubsequentOppositionHearingNoticePreview> {
  const params = new URLSearchParams();
  (tmNos ?? []).forEach((t) => params.append("tmNos", t));
  const query = params.toString() ? `?${params.toString()}` : "";
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/subsequent-opposition-hearing/inform-client-preview${query}`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<SubsequentOppositionHearingNoticePreview>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Subsequent Opposition Hearing notice preview"
    );
  }
  return (json as ApiSuccessResponse<SubsequentOppositionHearingNoticePreview>)
    .data;
}

export async function sendSubsequentOppositionHearingNoticeToClient(
  leadId: string,
  tmNos?: string[]
): Promise<{ waLink: string }> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/subsequent-opposition-hearing/inform-client/send-whatsapp`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ tmNos: tmNos ?? [] }),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to send Subsequent Opposition Hearing notice"
    );
  }
  return (json as ApiSuccessResponse<{ waLink: string }>).data;
}

// ───────────────────────────────────────────────────────────────────────────
// Subsequent Opposition Hearing — Invoice (4A(xxxiii) applied to subsequent)
// ───────────────────────────────────────────────────────────────────────────

export interface SubsequentOppositionHearingInvoiceAppRow {
  tmNo: string;
  applicationDate: string;
  mark: string;
  typeOfMark: string;
  classNumber: number;
  dateOfHearing: string | null;
  invoiceAmount: number;
  invoiceDate: string | null;
  invoiceNo: string | null;
  includeInvoiceNo: boolean;
  collectedAmount: number | null;
  outstandingAmount: number | null;
  selected: boolean;
}

export interface SubsequentOppositionHearingInvoiceStatus {
  isActive: boolean;
  hearingNumber: number;
  fieldPrefix: string;
  invoiceAmount: number | null;
  invoiceNo: string | null;
  invoiceDate: string | null;
  noOfApps: number;
  includeInvoiceNo: boolean;
  invoicePdfKey: string | null;
  invoicePdfUrl: string | null;
  collectedAmount: number | null;
  outstandingAmount: number | null;
  applications: SubsequentOppositionHearingInvoiceAppRow[];
}

export interface SubsequentOppositionHearingInvoicePreview {
  dataLabel: string;
  hearingNumber: number;
  acknowledgementNo: string | null;
  dateOfFiling: string | null;
  addressee: string;
  addresseeLine1: string | null;
  addresseeLine2: string | null;
  addresseeLine3: string | null;
  formOfAddress: string | null;
  applications: SubsequentOppositionHearingInvoiceAppRow[];
  invoiceAmount: number;
  invoiceDate: string | null;
  invoiceNo: string | null;
  includeInvoiceNo: boolean;
  noOfApps: number;
}

export async function getSubsequentOppositionHearingInvoiceStatus(
  leadId: string,
  hearingNumber: number,
  tmNos?: string[]
): Promise<SubsequentOppositionHearingInvoiceStatus> {
  const params = new URLSearchParams({ hearingNumber: String(hearingNumber) });
  (tmNos ?? []).forEach((t) => params.append("tmNos", t));
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/subsequent-opposition-hearing/invoice?${params.toString()}`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<SubsequentOppositionHearingInvoiceStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Subsequent Opposition Hearing Invoice"
    );
  }
  return (json as ApiSuccessResponse<SubsequentOppositionHearingInvoiceStatus>)
    .data;
}

export async function getSubsequentOppositionHearingInvoicePreview(
  leadId: string,
  hearingNumber: number,
  tmNos?: string[]
): Promise<SubsequentOppositionHearingInvoicePreview | null> {
  const params = new URLSearchParams({ hearingNumber: String(hearingNumber) });
  (tmNos ?? []).forEach((t) => params.append("tmNos", t));
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/subsequent-opposition-hearing/invoice/preview?${params.toString()}`,
    { method: "GET" }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<SubsequentOppositionHearingInvoicePreview | null>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to load Subsequent Opposition Hearing Invoice preview"
    );
  }
  return (
    json as ApiSuccessResponse<SubsequentOppositionHearingInvoicePreview | null>
  ).data;
}

export interface SaveSubsequentOppositionHearingInvoiceItem {
  tmNo: string;
  invoiceAmount: number;
  invoiceDate: string;
  includeInvoiceNo?: boolean;
}

export async function saveSubsequentOppositionHearingInvoice(
  leadId: string,
  hearingNumber: number,
  applications: SaveSubsequentOppositionHearingInvoiceItem[]
): Promise<SubsequentOppositionHearingInvoiceStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/subsequent-opposition-hearing/invoice`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ hearingNumber, applications }),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<SubsequentOppositionHearingInvoiceStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save Subsequent Opposition Hearing Invoice"
    );
  }
  return (json as ApiSuccessResponse<SubsequentOppositionHearingInvoiceStatus>)
    .data;
}

export async function generateSubsequentOppositionHearingInvoicePdf(
  leadId: string,
  hearingNumber: number,
  tmNos?: string[]
): Promise<{ key: string; url: string; filename: string }> {
  const params = new URLSearchParams({ hearingNumber: String(hearingNumber) });
  // Backend's HearingInvoicePdfTargetDto rejects empty `tmNos` arrays
  // (@ArrayNotEmpty), so omit the field unless we have values.
  const body: Record<string, unknown> = {};
  if (tmNos?.length) body.tmNos = tmNos;
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/subsequent-opposition-hearing/invoice/generate-pdf?${params.toString()}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<GeneratedPdfPayload>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to generate Subsequent Opposition Hearing Invoice PDF"
    );
  }
  return toGeneratedPdf((json as ApiSuccessResponse<GeneratedPdfPayload>).data);
}

export async function sendSubsequentOppositionHearingInvoiceToClient(
  leadId: string,
  hearingNumber: number,
  options?: {
    tmNos?: string[];
    sendWhatsApp?: boolean;
    sendEmail?: boolean;
  }
): Promise<{ waLink?: string; email?: string }> {
  // Omit `tmNos` unless it has values so we don't trip array validators on
  // the backend (DTOs that are @IsOptional but @ArrayNotEmpty when present).
  const body: Record<string, unknown> = {
    hearingNumber,
    sendWhatsApp: options?.sendWhatsApp ?? true,
    sendEmail: options?.sendEmail ?? false,
  };
  if (options?.tmNos?.length) body.tmNos = options.tmNos;
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/subsequent-opposition-hearing/invoice/send`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink?: string; email?: string }>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to send Subsequent Opposition Hearing Invoice"
    );
  }
  return (json as ApiSuccessResponse<{ waLink?: string; email?: string }>).data;
}

// ───────────────────────────────────────────────────────────────────────────
// Subsequent Opposition Hearing — Payment (4A(xl) applied to subsequent)
// ───────────────────────────────────────────────────────────────────────────

export interface SaveSubsequentOppositionHearingCollectedAmountItem {
  tmNo: string;
  collectedAmount: number;
}

export async function saveSubsequentOppositionHearingCollectedAmount(
  leadId: string,
  hearingNumber: number,
  applications: SaveSubsequentOppositionHearingCollectedAmountItem[]
): Promise<SubsequentOppositionHearingInvoiceStatus> {
  const res = await apiFetch(
    `${API_BASE}/new-app/status/${leadId}/opposition-counter/subsequent-opposition-hearing/payment`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ hearingNumber, applications }),
    }
  );
  const json = (await res.json()) as
    | ApiSuccessResponse<SubsequentOppositionHearingInvoiceStatus>
    | ApiErrorResponse;
  if (!res.ok) {
    throw new Error(
      (json as ApiErrorResponse)?.message ||
        (json as ApiErrorResponse)?.error ||
        "Failed to save Subsequent Opposition Hearing payment"
    );
  }
  return (json as ApiSuccessResponse<SubsequentOppositionHearingInvoiceStatus>)
    .data;
}
